/**
 * derive.mjs, tout ce qui se CALCULE depuis un brief.
 *
 * Portage des helpers purs de V1 (`launcher/index.html`) : champStat, calculsDe, calcDe,
 * statPhrase, fichePlan, chargementDe, etape, visible, AU, kebab, li. On PORTE, on ne
 * REECRIT pas : le comportement de V1 a ete durci par un test grandeur nature, une
 * « amelioration » spontanee serait un bug.
 *
 * Zero dependance, ESM, fonctions PURES : ce fichier tourne dans `server.mjs` (Node natif)
 * comme dans le navigateur. Aucun acces disque, DOM ou reseau, AUCUNE mutation du brief
 * (V1 mutait au passage dans `vb()` et `au()` ; ici on lit sans jamais creer la case).
 *
 * UN ecart de signature, impose par la purete, RETRO-COMPATIBLE avec INTERFACE.md (le
 * parametre ajoute est le dernier, et il est facultatif) : `brief` en dernier parametre.
 * V1 lisait la variable globale `S` pour resoudre le jeu d'une porte. Sans global, il faut
 * le brief : depuis la version 2 du modele un jeu vit a la RACINE (`brief.jeux[]`) et une
 * porte le DESIGNE par son uid (`porte.dsRef`), donc plus aucune porte ne porte ses champs.
 * Omis, une porte se comporte comme si son jeu avait disparu (champs vides, chargement
 * « - ») : degrade, jamais faux.
 *
 * `estVisible(porte, n, tout)` a ete RETIREE avec l'affichage progressif d'une porte : les
 * sept sous-sections se rendent toutes, tout le temps. `etapeDe` reste, elle : la carte
 * d'une porte s'en sert pour DIRE la prochaine etape a franchir, ce qui n'a jamais rien
 * masque.
 */
import { alimenteSansMoteur, resoudreElementsCadre } from '@taki/data-model/cadre-canvas';
import { champsDEnTeteDuJeu, conditionSchema, couleurSchema, enTetesDeLaPorte, estAutomatique, memeJeuDePorte, natureDuType, vuesDeFicheDeLaPorte } from '@taki/data-model';
import { CALCULS, FAMILLES, JEUX, STD, VUES } from './familles.mjs';
import { NATIFS_PAR_FAMILLE } from './natifs.generated.mjs';
import { kebab } from '../../identifiants.mjs';
export { kebab };
/*
 * Le catalogue des trois niveaux de canvas vit dans `model.mjs` : c'est la FORME
 * d'un brief qui dit quelles clefs portent quel niveau. Ici on ne fait que LIRE
 * un brief a travers lui. Aucun cycle : `model.mjs` ne depend que des
 * identifiants purs du socle.
 */
import {
  ROLES_TRI,
  SENS_TRI,
  clefDeChamp,
  estFamilleADecider,
  familleADeciderDe,
  libelleDeValeur,
  niveauCanvasDe,
  refFiltre,
  refValeur,
  valeursDeChamp,
  clefDuChamp,
  natifDuChamp,
  tonDeValeur,
  parentDeValeur,
  valeurStockee,
  CATEGORIES_DE_FICHIERS,
  TAILLE_MAX_FICHIER_MO,
  estCarteFichiers,
  etatDePonderation,
  fichiersDuJeu,
  niveauDeVueCustom,
  estNatureLiee,
} from './model.mjs';
import { formuleDeclaree, reglagesDeclares } from './reglages-de-champ.mjs';

/*
 * La migration utilise `clefDeChamp`, definie dans les identifiants communs.
 * Les anciens chemins d'import restent disponibles par ces reexports.
 */
export { clefDeChamp, valeursDeChamp };

/* ========== texte ========== */

/**
 * L'ID D'APP QU'UN BRIEF DESIGNE : le dossier `apps/hello/src/apps/<id>/`.
 *
 * L'ID DECLARE FAIT FOI, LE SLUG DU NOM N'EST QUE LE REPLI. C'est la regle du
 * MOTEUR (`creerApp`, `tools/studio-dev/moteur-app.mjs`), et elle vit ici pour
 * que personne ne la retape : ecrite deux fois, elle a deja diverge une fois.
 * Le Studio annoncait le dossier `kebab(nom)` pendant que le moteur ecrivait
 * `brief.id`, donc la section Identite montrait une collision sur un nom quand
 * il n'y en avait aucune, et se taisait sur la vraie.
 *
 * ELLE NE REGARDE PAS `construite` : c'est la question « quel dossier ce brief
 * VISE », pas « quelle app il a POSEE ». La seconde a sa propre fonction, juste
 * dessous, et les deux ne disent pas la meme chose des qu'on renomme une app.
 *
 * @param {any} brief @returns {string} */
export function idDAppVise(brief) {
  const declare = typeof brief?.id === 'string' ? brief.id.trim() : '';
  if (declare !== '') return declare;
  return kebab(typeof brief?.nom === 'string' ? brief.nom.trim() : '');
}

/**
 * L'APP QUE CE PROJET A DEJA POSEE, ou `''` s'il n'en a pose aucune.
 *
 * LE FAIT `construite` TRANCHE, JAMAIS UNE RESSEMBLANCE DE NOMS. Il est pose
 * par le pont au moment ou l'app est ecrite (`construite.appId`), et c'est le
 * seul ecrit qui relie un projet a un dossier du depot. Un projet qui DECLARE
 * l'id d'une app sans l'avoir construite n'en a pas pose : c'est peut-etre
 * l'app de quelqu'un d'autre, et le confondre avec la sienne, c'est refuser de
 * dire la collision qui compte.
 *
 * POURQUOI ELLE EXISTE : sans elle, un projet dont l'app est deja posee lit sa
 * PROPRE app dans l'inventaire du depot et s'annonce en collision avec
 * elle-meme (« Une app "contact" existe deja : la creation sera refusee »),
 * alors qu'il est simplement son brief.
 *
 * @param {any} brief @returns {string} */
export function appPoseeDe(brief) {
  const fait = brief?.construite;
  if (fait === null || typeof fait !== 'object' || Array.isArray(fait)) return '';
  return typeof fait.appId === 'string' ? fait.appId.trim() : '';
}

/**
 * L'ID SOUS LEQUEL CE PROJET VIT DANS LE DEPOT : l'app posee si elle l'est, le
 * dossier vise sinon.
 *
 * C'EST L'ID QUI NE BOUGE PLUS UNE FOIS L'APP POSEE. Renommer une app vivante
 * ne deplace pas son dossier, ne renomme pas son entree de registre et ne
 * change pas sa release : le nom affiche suit, l'id non. Tout ce qui cherche
 * « de quelle app ce projet parle » demande ceci, et rien d'autre.
 *
 * @param {any} brief @returns {string} */
export function idDApp(brief) {
  return appPoseeDe(brief) || idDAppVise(brief);
}

/**
 * CE QUE L'IDENTITE D'UN BRIEF VAUT FACE A L'INVENTAIRE DU DEPOT : trois etats,
 * et jamais deux a la fois.
 *
 *  - `posee` : ce projet a DEJA construit son app. Il n'a plus rien a creer, il
 *    est son brief. Ce n'est pas un probleme, c'est l'etat normal de tout projet
 *    passe la creation.
 *  - `collision` : une AUTRE app du depot porte deja l'id vise. La creation sera
 *    refusee (`creerApp`, « L'id "x" est deja utilise »), et le champ a corriger
 *    est celui qui commande le dossier.
 *  - `libre` : rien ne s'oppose a la creation sous cet id.
 *
 * POURQUOI UNE FONCTION, ET PAS UN `if` DANS LE JSX : c'est la meme regle que
 * `projet.ts` et `champsEdition.ts`. Une decision enfermee dans un composant ne
 * se teste qu'en montant un DOM, et les tests de ce package tournent sans DOM ;
 * celle-ci a deja menti en production (« Une app "contact" existe deja : la
 * creation sera refusee », servie au projet de l'app `contact` elle-meme), donc
 * elle se gele.
 *
 * L'ORDRE COMPTE : une app posee passe DEVANT la collision, parce qu'elle est
 * sa propre collision. Sans ca, le projet de `contact` lit `contact` dans
 * l'inventaire et s'annonce en conflit avec lui-meme.
 *
 * @param {any} brief
 * @param {{ id?: string }[]} apps l'inventaire des apps du depot
 * @returns {{ code: 'posee' | 'collision' | 'libre', app: string }} */
export function verdictDIdentite(brief, apps) {
  const posee = appPoseeDe(brief);
  if (posee !== '') return { code: 'posee', app: posee };
  const vise = idDAppVise(brief);
  const prises = (Array.isArray(apps) ? apps : []).map((a) => String(a?.id ?? '').trim());
  if (vise !== '' && prises.includes(vise)) return { code: 'collision', app: vise };
  return { code: 'libre', app: vise };
}

/** Un champ libre multiligne decoupe en lignes utiles. Le `li` de V1 (defini DEUX fois
 * la-bas, dans emitJeu et dans genPrompt : c'est le doublon que ce module supprime).
 * @param {string} texte @returns {string[]} */
export function lignes(texte) {
  return texte
    ? texte
        .split('\n')
        .map((x) => x.trim())
        .filter(Boolean)
    : [];
}

/* ========== ce que le fondateur delegue a l'agent ========== */

/** @param {any} jeu */
function auto(jeu) {
  return (jeu && jeu.auto) || {};
}

/**
 * « L'agent decide » sur un detail du jeu de donnees (famille, type, scope, decoupe).
 * Le `AU(p, k)` de V1, qui prenait une PORTE : depuis la version 2 du modele il prend
 * le JEU, parce que c'est le jeu qui porte le sac `auto`. Un appelant qui n'a que la
 * porte passe par `jeuDe(porte, brief)`.
 * @param {any} jeu @param {string} quoi @returns {boolean}
 */
export function estDelegue(jeu, quoi) {
  return !!auto(jeu)[quoi];
}

/** Le reglage d'une vue, en LECTURE seule. Le `vb(p, v)` de V1, sans sa mutation.
 * @param {any} porte @param {string} vue @returns {any} */
export function bindingDe(porte, vue) {
  return (porte && porte.vb && porte.vb[vue]) || {};
}

/** « L'agent decide » sur une vue entiere. Le `VA(p, v)` de V1.
 * @param {any} porte @param {string} vue @returns {boolean} */
export function estDelegueVue(porte, vue) {
  return !!bindingDe(porte, vue).auto;
}

/* ========== les champs d'une porte ========== */

/** Un identifiant de champ pose par le socle (`std:createdAt`). Le `estStd` de V1.
 * @param {any} id @returns {boolean} */
export function estChampStd(id) {
  return typeof id === 'string' && id.startsWith('std:');
}

/**
 * LE JEU QUE CETTE PORTE MONTRE, ou `null`.
 *
 * Elle remplace `sourceDe`, qui rendait la PORTE detentrice du jeu : un jeu n'avait pas
 * d'identite, il etait identifie par la porte qui se trouvait le posseder. Depuis la
 * version 2 du modele, une porte designe son jeu par uid et la resolution est une simple
 * recherche dans `brief.jeux`.
 *
 * `null` a DEUX sens, et l'appelant n'a pas a les distinguer : la porte n'a choisi aucun
 * jeu, ou elle en designe un qui n'existe plus.
 *
 * @param {any} porte @param {any} [brief] @returns {any}
 */
export function jeuDe(porte, brief) {
  const ref = String((porte && porte.dsRef) || '');
  if (ref === '') return null;
  const jeux = (brief && brief.jeux) || [];
  return jeux.find((x) => x && x.uid === ref) || null;
}

/**
 * LE NOM DU JEU D'UNE PORTE, tel que le lit le fondateur (« pages »), jamais
 * son uid technique : le recapitulatif des portes du Builder l'affiche.
 * « Sans jeu » quand la porte n'en montre aucun.
 * @param {any} porte @param {any} brief @returns {string}
 */
export function nomDuJeuDePorte(porte, brief) {
  const jeu = jeuDe(porte, brief);
  if (jeu === null) return 'Sans jeu';
  return String(jeu.nom || '').trim() || String(jeu.type || '').trim() || 'Jeu sans nom';
}

/** Les champs declares du JEU que la porte montre. Le `champsDe` de V1.
 * @param {any} porte @param {any} [brief] @returns {any[]} */
export function champsDe(porte, brief) {
  const j = jeuDe(porte, brief);
  return j && Array.isArray(j.champs) ? j.champs : [];
}

/**
 * LA CAPACITE DE FICHIERS DU JEU QUE LA PORTE MONTRE, ou `null` quand elle est
 * coupee (ou que la porte n'a pas de jeu). C'est ce que la section des fichiers
 * joints d'une fiche attend pour se montrer : sans elle, la carte existe dans le
 * fichier et ne rend rien.
 * @param {any} porte @param {any} [brief]
 * @returns {{ types?: string[], tailleMaxMo?: number } | null}
 */
export function fichiersDeLaPorte(porte, brief) {
  return fichiersDuJeu(jeuDe(porte, brief));
}

/**
 * LA CAPACITE DE FICHIERS TELLE QUE `taki.fields.json` L'ECRIT, ou `null`.
 *
 * ELLE NE POSE AUCUN DEFAUT : `{}` reste `{}` (tous les types, le plafond du
 * socle), et un `tailleMaxMo: 25` ecrit a la main reste ecrit. C'est ce qui
 * rend l'aller-retour exact. Elle ne fait qu'ecarter ce que le schema
 * refuserait (`fichiersDeJeuSchema`) : une categorie inconnue, une liste vide,
 * une taille hors de 1 a 25 ou pas entiere.
 * @param {any} jeu @returns {{ types?: string[], tailleMaxMo?: number } | null}
 */
export function fichiersProjetes(jeu) {
  const f = fichiersDuJeu(jeu);
  if (f === null) return null;
  const sortie = {};
  if (Array.isArray(f.types)) {
    const types = [...new Set(f.types.filter((t) => CATEGORIES_DE_FICHIERS.includes(t)))];
    if (types.length > 0) sortie.types = types;
  }
  const taille = f.tailleMaxMo;
  if (Number.isInteger(taille) && taille >= 1 && taille <= TAILLE_MAX_FICHIER_MO) {
    sortie.tailleMaxMo = taille;
  }
  return sortie;
}

/** Le nom lisible d'un champ, du socle ou declare. Le `champNom` de V1.
 * @param {any} porte @param {any} id @param {any} [brief] @returns {string} */
export function nomDeChamp(porte, id, brief) {
  if (typeof id === 'string' && id.startsWith('std:')) {
    const s = STD.find((x) => x.k === id.slice(4));
    return s ? s.nom : '';
  }
  const c = champsDe(porte, brief).find((x) => x.uid === id);
  return c ? c.nom : '';
}

/* ========== les filtres, les favoris et le tri ==========
 *
 * LES QUATRE CLEFS QUI ONT CESSE DE STOCKER DU FRANCAIS. Elles portaient des
 * phrases (« ce qu'il garde », « filtres et tri enregistres », « Nom
 * croissant, puis date ») la ou le socle attend des REFERENCES. Ce bloc les
 * LIT ; la projection, plus bas, les traduit dans le vocabulaire des fichiers.
 *
 * TOUT Y EST DE LA LECTURE PURE : aucune de ces fonctions n'analyse une phrase,
 * et c'est la ligne rouge du lot. */

/** Le catalogue de filtres d'une porte, toujours a la forme `{ champ, autres }`.
 * @param {any} porte @returns {{ champ: string, autres: any[] }} */
export function filtresDe(porte) {
  const f = porte && porte.filtres;
  const sac = f && typeof f === 'object' && !Array.isArray(f) ? f : {};
  return {
    champ: String(sac.champ || ''),
    autres: Array.isArray(sac.autres) ? sac.autres : [],
  };
}

/**
 * LES CHAMPS QU'UN CATALOGUE DE FILTRES PEUT PRENDRE : les champs DECLARES de
 * nature « liste de choix » qui ont au moins une valeur.
 *
 * UN CHAMP NATIF (`socle:status`) EN EST, des qu'il porte ses valeurs : le
 * socle le filtre par valeur quand sa colonne du catalogue de l'ecran porte
 * ses `options` (`filtresDe` dans `@taki/ui`), et la derivation pose cette
 * colonne (`filtresProjetes`).
 *
 * @param {any} porte @param {any} brief @returns {any[]}
 */
export function champsAChoixDe(porte, brief) {
  return champsDe(porte, brief).filter(
    (c) =>
      c &&
      String(c.nom || '').trim() !== '' &&
      c.nature === 'liste de choix' &&
      valeursDeChamp(c).length > 0,
  );
}

/**
 * TOUS LES FILTRES QU'UN FAVORI PEUT COCHER, dans l'ordre ou ils se rendent :
 * une entree par valeur du catalogue `parChamp`, puis les « autres ».
 *
 * CHAQUE ENTREE PORTE SA REFERENCE, et c'est elle que le brief stocke : le
 * `label` ne sert plus qu'a l'affichage. Il a servi de reference pendant une
 * version, et les deux degats etaient silencieux : renommer une option perdait
 * le favori, un filtre nomme homonyme d'une valeur ecrasait cette valeur.
 * `valeur:<valeur stockee>` et `filtre:<uid>` ne peuvent plus se cogner.
 *
 * L'ORDRE COMPTE : le catalogue d'abord. Une valeur et un filtre nomme peuvent
 * porter le meme MOT (« En panne ») ; la case rendue est alors celle de la
 * valeur, et la migration traduit dans le meme ordre.
 *
 * LA VALEUR EST CELLE DU FICHIER DE CHAMPS (`valeurStockee`), jamais la clef
 * derivee du libelle. Les deux coincident tant que `clefsValeurs` ne fige rien ;
 * des qu'il fige « Fournisseur » en `supplier`, la case offrait
 * `valeur:fournisseur`, le validateur l'acceptait, et `filtresProjetes` (qui
 * resout `options[].value`) posait un favori sans filtre, en silence. Vu le
 * 14/09/2026 a la naissance de l'Annuaire.
 *
 * @param {any} porte @param {any} brief
 * @returns {{ ref: string, label: string, source: string }[]}
 */
export function filtresOfferts(porte, brief) {
  const { champ, autres } = filtresDe(porte);
  const sortie = [];
  const declare = champsAChoixDe(porte, brief).find((c) => c.uid === champ);
  if (declare) {
    for (const v of valeursDeChamp(declare)) {
      sortie.push({ ref: refValeur(valeurStockee(declare, v)), label: v, source: 'champ' });
    }
  }
  for (const a of autres) {
    const label = String((a && a.label) || '').trim();
    if (label === '') continue;
    /* MEME MOT, DEUX SOURCES : l'ecran ne rend qu'une case, celle du catalogue.
       Le filtre nomme reste declare (il a son id dans le fichier), il n'est
       simplement pas proposable a un favori sous ce mot-la. */
    if (sortie.some((x) => x.label === label)) continue;
    sortie.push({ ref: refFiltre(String((a && a.uid) || '')), label, source: 'autre' });
  }
  return sortie;
}

/**
 * LE NOM D'APPEL D'UN EFFET OU D'UN PREDICAT, derive de son libelle.
 *
 * IL NE SE TAPE JAMAIS, exactement comme la clef d'un champ ne se tape pas :
 * c'est `clefDeChamp` sur le libelle FINAL. Un libelle qui ne donne aucun
 * identifiant (des chiffres seuls, des symboles) rend `''`, et l'appelant
 * ECARTE plutot que d'inventer un nom.
 *
 * @param {string} label @returns {string}
 */
export function nomDAppel(label) {
  return clefDeChamp(label);
}

/**
 * UN NIVEAU DE TRI, DIT EN FRANCAIS : « Nom, croissant ».
 *
 * C'est le pendant de `phraseDeStat` : le formulaire relit la rangee au lieu de
 * demander au fondateur de la deviner. La phrase se DERIVE, elle ne se stocke
 * nulle part.
 *
 * @param {any} porte @param {any} niveau @param {any} brief @returns {string}
 */
export function phraseDeNiveau(porte, niveau, brief) {
  const role = String((niveau && niveau.role) || '');
  const sens = SENS_TRI.find((x) => x.id === String((niveau && niveau.sens) || 'asc'));
  const mot = sens ? sens.mot : 'croissant';
  if (role !== '') {
    const dit = String((niveau && niveau.mot) || '').trim();
    return dit === '' ? 'Ce rôle n\u2019a pas de mot : il ne sera pas trié.' : dit + ', ' + mot;
  }
  const nom = nomDeChamp(porte, String((niveau && niveau.champ) || ''), brief);
  return nom === '' ? 'Aucun champ choisi.' : nom + ', ' + mot;
}

/* ========== les stats ==========
 * Une stat = un champ, un calcul, un libelle. Les calculs offerts dependent de la NATURE
 * du champ : `familleDe` de V1 regroupe les natures (`NATURES`, familles.mjs) en sept familles de calcul. */

/** @param {string} nature @returns {string} */
function familleDeNature(nature) {
  /* UNE NOTE ET UN CALCUL sont des nombres (`estNumerique` du catalogue) : ils
     se totalisent et se moyennent comme eux. */
  if (['nombre', 'prix', 'pourcentage', 'durée', 'duree', 'note', 'calcul'].includes(nature)) return 'num';
  if (nature === 'liste de choix' || nature === 'liste de choix multiple') return 'choix';
  if (['date', 'date et heure'].includes(nature)) return 'date';
  if (nature === 'oui / non') return 'bool';
  if (estNatureLiee(nature) || TYPE_PAR_NATURE[nature] === 'personne') return 'lien';
  return 'texte';
}

/** Le champ vise par une stat, ou `null` pour « toutes les fiches ». Le `champStat` de V1.
 * @param {any} porte @param {any} stat @param {any} [brief] @returns {any} */
export function champDeStat(porte, stat, brief) {
  if (!stat.champ) return null;
  if (estChampStd(stat.champ)) {
    const x = STD.find((y) => y.k === stat.champ.slice(4));
    return x ? { nom: x.nom, nature: x.nature, valeurs: '' } : null;
  }
  return champsDe(porte, brief).find((x) => x.uid === stat.champ) || null;
}

/** Les calculs offerts pour cette stat, « Autre calcul » toujours en dernier.
 * Le `calculsDe` de V1. @param {any} porte @param {any} stat @param {any} [brief]
 * @returns {any[]} */
export function calculsDe(porte, stat, brief) {
  const c = champDeStat(porte, stat, brief);
  return CALCULS[c ? familleDeNature(c.nature) : 'aucun'].concat([
    { k: 'libre', t: "Autre calcul, je l'écris...", libre: 1 },
  ]);
}

/** Le calcul retenu, ou le premier de la liste si le choix n'a plus de sens (un changement
 * de champ peut retirer un calcul de l'offre). Le `calcDe` de V1.
 * @param {any} porte @param {any} stat @param {any} [brief] @returns {any} */
export function calculDe(porte, stat, brief) {
  const l = calculsDe(porte, stat, brief);
  return l.find((x) => x.k === stat.calcul) || l[0];
}

/** La stat dite en francais, telle qu'elle s'affiche sous la ligne et part dans le prompt.
 * Le `statPhrase` de V1. @param {any} porte @param {any} stat @param {any} [brief]
 * @returns {string} */
export function phraseDeStat(porte, stat, brief) {
  const c = champDeStat(porte, stat, brief);
  const kk = calculDe(porte, stat, brief);
  const k = kk.k;
  const n = c ? c.nom : '';
  if (kk.libre) return (c ? '« ' + n + ' » : ' : '') + (stat.libre || 'règle à préciser');
  if (!c) return 'le nombre de fiches';
  if (k === 'total') return 'la somme des valeurs de « ' + n + ' »';
  if (k === 'moyenne') return 'la moyenne de « ' + n + ' »';
  if (k === 'min')
    return (
      (familleDeNature(c.nature) === 'date'
        ? 'la plus ancienne valeur de « '
        : 'la plus petite valeur de « ') +
      n +
      ' »'
    );
  if (k === 'max')
    return (
      (familleDeNature(c.nature) === 'date'
        ? 'la plus récente valeur de « '
        : 'la plus grande valeur de « ') +
      n +
      ' »'
    );
  if (k === 'nbVaut')
    /* LE LIBELLE, jamais la valeur stockee : la phrase se lit sous la ligne et
       part dans le prompt, et `supplier` n'est pas un mot de l'app. Meme geste
       que le prompt pour un favori (« Camera », pas `valeur:camera`). */
    return 'le nombre de fiches dont « ' + n + ' » vaut « ' + (libelleDeValeur(c, stat.valeur) || '?') + ' »';
  if (k === 'nbDistinct')
    return (
      'le nombre de valeurs DIFFÉRENTES de « ' +
      n +
      ' » (un compte de valeurs distinctes, jamais une addition)'
    );
  if (k === 'nbRempli') return 'le nombre de fiches où « ' + n + ' » est renseigné';
  return 'le nombre de fiches où « ' + n + ' » est vide';
}

/* ========== la fiche ==========
 * Le socle fait COULER les cartes sur deux colonnes dans l'ordre de declaration ; une carte
 * qui n'est pas demi prend la rangee pour elle seule. UNE EXCEPTION depuis le 14/10/2026 :
 * une carte `placement: "droite"` part dans la colonne de DROITE, en haut de la fiche, et le
 * socle l'honore (`planDesCartes`, `packages/ui/src/views/core/RecordView.tsx`). Ce calcul
 * RESTITUE la disposition obtenue, il ne la decide pas.
 *
 * LA FICHE EST UNE TETE PLUS DES SECTIONS, ET RIEN D'AUTRE. Decision du
 * fondateur du 22/09/2026 : un champ s'affiche la ou une section le cite, nulle
 * part ailleurs. Plus aucune carte CALCULEE, ni « Autres champs » qui ramassait
 * les orphelins, ni « Informations » derivee : une carte que le fondateur ne
 * peut pas retirer est une carte qu'il subit, et elle rendait impossible un jeu
 * de 150 champs dont une porte ne montre que trois. Le socle applique la meme
 * regle du meme jour (`buildCards`, `packages/ui/src/views/core/RecordView.tsx`). */

/**
 * UNE CASE DU PLAN. `disposition` est celle de la carte (`demi`, `pleine`, `titre-pleine`) ;
 * `rangee` dit si elle prend la rangee entiere : toute disposition qui n'est pas demi, et
 * toute carte LIEE (`liee`), que le socle rend toujours sur la rangee entiere (`LinkedCard`)
 * et qui n'a pas de disposition a elle. La carte des FICHIERS joints (`fichiers`) aussi,
 * et pour la meme raison ; `lecture` dit qu'elle est en lecture seule. Les deux
 * (`disposition` et `rangee`) parce que le calcul des rangees n'a besoin que du booleen,
 * alors que l'apercu et le prompt doivent NOMMER ce qu'ils montrent.
 *
 * `champs` nomme ce que la carte RENDRA, dans l'ordre ou la fiche le rend : les libelles des
 * champs et des totaux, sans les clefs que la tete tient ni celles qu'une section plus haut
 * a deja prises (le socle ne rend un champ qu'une fois, `buildCards`). L'apercu les range
 * comme la fiche : une colonne, ou deux pour `titre-pleine`, lues de haut en bas.
 * `droite` dit que la carte est dans la colonne de DROITE (`placement: "droite"`).
 * @typedef {{ uid: string, titre: string, n: number, champs: string[], disposition: string, rangee: boolean, liee?: boolean, fichiers?: boolean, lecture?: boolean, droite?: boolean }} CarteFiche
 */

/**
 * LE PLACEMENT D'UNE SECTION DE FICHE, lu par COMPARAISON EXPLICITE : `droite`
 * ou `principal`, et tout le reste (l'absence comprise) vaut `principal`. Une
 * carte liee ou de fichiers n'a pas de placement : elle prend la rangee.
 * @param {any} carte @returns {'principal' | 'droite'}
 */
export function placementDe(carte) {
  return carte !== null && typeof carte === 'object' && carte.placement === 'droite'
    ? 'droite'
    : 'principal';
}

/**
 * LES DEUX ROLES QUE LA TETE DE LA FICHE REND, ET QUI SE MODIFIENT LA.
 *
 * C'est le socle qui le decide, pas ce fichier (`RecordView`) : le TITRE est le
 * `h2` de la bande d'identite, ouvrable au clic ; le STATUT y est epingle,
 * cliquable. Les deux se saisissent donc en tete, et le champ qui les tient n'a
 * rien a faire dans une section.
 *
 * LES AUTRES ROLES NE SONT PAS LA, ET C'EST VOLONTAIRE. `subtitle` et `avatar`
 * sont rendus en tete en LECTURE seule (un `<p>`, une vignette) : sans section
 * pour les ranger, « Intervenant », sous-titre des interventions, ne se
 * modifierait plus NULLE PART. `badges` n'est rendu nulle part sur la fiche, et
 * `dateStart`, `dateEnd`, `groupKey`, `lane` sont des cases ordinaires du pool
 * (`roleFields`). Tous se rangent donc comme n'importe quel champ ; leur case
 * de role du pool, elle, n'est simplement pas reclamee, donc pas rendue.
 */
const ROLES_EN_TETE = ['title', 'status'];

/**
 * LES CHAMPS QUE LA TETE DE LA FICHE TIENT DEJA, par uid : ceux qu'aucune
 * section ne range, parce qu'ils se modifient en tete.
 *
 * NATIF OU DECLARE, PEU IMPORTE. C'est le ROLE qui decide, pas la provenance du
 * champ : un champ declare qui tient le titre s'affiche et se saisit dans le
 * `h2`, le ranger en plus dans une section l'afficherait deux fois. C'est ce qui
 * a change le 22/09/2026 : la regle ne connaissait que les natifs, donc le
 * fondateur voyait le meme champ deux fois sur la moitie des portes.
 *
 * CE QUE CA CHANGE A L'ECRAN. La case a cocher « Dans la section » de ce
 * champ-la etait un controle MORT (regle G16) : la cocher n'ecrivait pas un
 * octet dans `taki.screens.json`. Le formulaire lit cette fonction pour
 * l'eteindre et dire pourquoi a la place (`brief-ui/SectionFiche.tsx`).
 *
 * @param {any} porte @param {any} [brief] @returns {Map<string, string>} uid -> role
 */
export function champsEnTeteDeFiche(porte, brief) {
  const b = bindingDe(porte, 'core.fiche');
  const champs = champsDe(porte, brief);
  const pris = new Map();
  for (const role of ROLES_EN_TETE) {
    const valeur = b[role];
    const brut = valeur && typeof valeur === 'object' ? valeur.champ : valeur;
    /* UN ROLE CALCULE (`fn:`), COMPOSE (`champs:a+b`) OU STANDARD (`std:`) NE
       DESIGNE AUCUN CHAMP DU JEU : il n'a pas d'uid, donc aucune case du
       formulaire ne lui correspond et il n'y a rien a eteindre. */
    if (typeof brut !== 'string' || brut.trim() === '' || brut.includes(':')) continue;
    const champ = champs.find((c) => c !== null && typeof c === 'object' && (c.uid === brut || (natifDuChamp(c) ? 'socle:' + natifDuChamp(c) : clefDuChamp(c)) === brut));
    if (champ === undefined) continue;
    pris.set(champ.uid, role);
  }
  return pris;
}

/** Les rangees de la fiche, les champs qu'aucune section ne cite et les sections declarees.
 * Le `fichePlan` de V1. @param {any} porte @param {any} [brief]
 * @returns {{ rows: CarteFiche[][], absents: any[], cds: any[] }} */
export function planDeFiche(porte, brief) {
  const b = bindingDe(porte, 'core.fiche');
  /* CE QU'UNE SECTION PEUT RANGER, et rien d'autre : le champ qui tient le titre
     ou le statut se saisit en tete, il n'est ni une colonne de la fiche, ni une
     case a cocher du formulaire. */
  const enTete = champsEnTeteDeFiche(porte, brief);
  const ch = champsDe(porte, brief).filter((x) => x.nom.trim() && !enTete.has(x.uid));
  const cds = b.cards || [];
  /* COMPARAISON EXPLICITE, JAMAIS UN `!!` : la clef est facultative, et c'est la LECTURE qui
     decide ce que vaut son absence. Absente vaut demi, comme le socle. */
  const dispositionDe = (c) =>
    placementDe(c) === 'droite'
      ? 'demi'
      : c.disposition === 'pleine' || c.disposition === 'titre-pleine'
        ? c.disposition
        : 'demi';
  const avecRangee = (it) => ({
    ...it,
    rangee: it.liee === true || it.fichiers === true || it.disposition !== 'demi',
  });
  /* UNE CARTE LIEE PREND LA RANGEE, comme le socle la rend (`LinkedCard`, toujours pleine
     largeur) : l'apercu la posait en demi-colonne avec « 0 champ(s) », une fiche que le produit
     ne rendait pas. Elle n'a pas de disposition a elle, le schema refuse la clef. */
  const estLiee = (c) => c.liee !== undefined && c.liee !== null && c.liee !== '';
  /* UNE CLEF EN TETE COCHEE DANS UNE SECTION NE COMPTE PAS : la derivation la
     jette (le titre et le statut ne sont pas des colonnes de la fiche), donc la
     carte rendue en porte une de moins que la case cochee ne le disait. Un brief
     d'avant le 22/09/2026 en porte, et c'est INERTE, pas une faute. Les autres
     clefs restent comptees telles quelles, les TOTAUX compris : un total est
     bien une case de la carte (`rollupField`). */
  const clefsVives = (c) => (c.keys || []).filter((k) => !enTete.has(k));
  /*
   * UNE CARTE QUE LE FICHIER N'ECRIRA PAS N'EST PAS UNE CASE DU PLAN, ET SES
   * CHAMPS NE SONT PAS RECLAMES.
   *
   * La derivation passe son tour sur une carte de champs sans titre ou sans
   * champ (« une carte sans champ ne s'ecrit pas »), et le socle n'en rend
   * aucune (`buildCards` : une carte dont rien n'est reclame n'est pas poussee,
   * et rien n'y est marque pris). L'apercu en faisait quand meme une case, donc
   * une rangee, donc un voisin decale ; et il comptait ses champs pour PRIS,
   * alors qu'ils ne se rendent nulle part. Une carte LIEE, elle, n'a jamais de
   * champ et s'ecrit toujours.
   */
  /*
   * LA CARTE DES FICHIERS JOINTS N'EST UNE CASE QUE SI ELLE SE REND : un titre,
   * et un jeu qui ouvre les fichiers. Capacite coupee, la fiche ne la montre pas
   * (le socle la passe), donc l'apercu non plus : le formulaire dit pourquoi, a
   * cote de la section. Seule la PREMIERE compte, la derivation ecarte les autres.
   */
  const fichiersOuverts = fichiersDeLaPorte(porte, brief) !== null;
  const premiereFichiers = cds.find((c) => estCarteFichiers(c));
  const ecrites = cds.filter((c) =>
    estCarteFichiers(c)
      ? fichiersOuverts && c === premiereFichiers && String(c.title || '').trim() !== ''
      : estLiee(c) || (String(c.title || '').trim() !== '' && clefsVives(c).length > 0),
  );
  const pris = new Set(ecrites.flatMap((c) => c.keys || []));
  /* LES ABSENTS DE LA FICHE : les champs du jeu qu'aucune section ECRITE ne
     cite, hors tete. Ils ne se rendent NULLE PART (plus de carte de ramassage),
     et le formulaire les NOMME pour que ce soit un choix, pas une surprise. */
  const absents = ch.filter((c) => !pris.has(c.uid));
  /* CE QUE CHAQUE SECTION RENDRA, NOMME, dans l'ordre du socle : une clef deja
     prise par une section plus haut ne se rend pas deux fois (`buildCards`), un
     total se nomme par son libelle. Une clef qui ne nomme rien ne se montre pas. */
  const rendus = new Set();
  const nomsDe = (c) => {
    const totaux = Array.isArray(c.totaux) ? c.totaux : [];
    const noms = [];
    for (const k of clefsVives(c)) {
      const total = totaux.find((t) => t !== null && typeof t === 'object' && t.uid === k);
      if (total !== undefined) {
        noms.push(String(total.libelle || '').trim() || 'Total');
        continue;
      }
      if (rendus.has(k)) continue;
      /* UN CHAMP DE TRACE (`std:createdBy`) n'est pas un champ du jeu : il se
         nomme par le socle, sans quoi la case comptait « 2 champ(s) » et n'en
         montrait qu'un. */
      const nom = estChampStd(k) ? nomDeChamp(porte, k, brief) : ch.find((x) => x.uid === k)?.nom.trim();
      if (nom === undefined || nom === '') continue;
      rendus.add(k);
      noms.push(nom);
    }
    return noms;
  };
  const items = ecrites.map((c) =>
    estCarteFichiers(c)
      ? avecRangee({
          uid: c.uid,
          titre: c.title,
          n: 0,
          champs: [],
          disposition: 'pleine',
          fichiers: true,
          ...(c.modifiable === false ? { lecture: true } : {}),
        })
      : avecRangee({
          uid: c.uid,
          titre: c.title || 'Sans titre',
          n: clefsVives(c).length,
          champs: estLiee(c) ? [] : nomsDe(c),
          disposition: dispositionDe(c),
          ...(estLiee(c) ? { liee: true } : {}),
          ...(!estLiee(c) && placementDe(c) === 'droite' ? { droite: true } : {}),
        }),
  );
  /*
   * ET PLUS AUCUNE CASE CALCULEE. « Autres champs » ramassait les absents,
   * « Informations » remplacait une fiche sans section : deux cartes que le
   * fondateur voyait dans l'apercu et ne pouvait ni renommer, ni deplacer, ni
   * retirer. Le socle a cesse de les rendre le 22/09/2026 (`buildCards`), donc
   * l'apercu cesse de les annoncer : une fiche sans section n'est que sa tete,
   * et le formulaire le DIT en clair au lieu d'inventer une carte.
   */
  const rows = [];
  /*
   * UNE CARTE A DROITE CHANGE LE PLAN, comme le socle (`planDesCartes`) : en haut,
   * la pile de gauche (les demi du flot) et celle de droite, cote a cote ;
   * dessous, ce qui prend la rangee. L'apercu les apparie rang par rang, ce qui
   * suffit a montrer qui est a gauche et qui est a droite.
   */
  if (items.some((it) => it.droite === true)) {
    const gauche = items.filter((it) => it.droite !== true && !it.rangee);
    const droite = items.filter((it) => it.droite === true);
    const dessous = items.filter((it) => it.droite !== true && it.rangee);
    for (let i = 0; i < Math.max(gauche.length, droite.length); i += 1) {
      rows.push([gauche[i], droite[i]].filter((it) => it !== undefined));
    }
    for (const it of dessous) rows.push([it]);
    return { rows, absents, cds };
  }
  let cur = [];
  items.forEach((it) => {
    if (it.rangee) {
      if (cur.length) {
        rows.push(cur);
        cur = [];
      }
      rows.push([it]);
    } else {
      cur.push(it);
      if (cur.length === 2) {
        rows.push(cur);
        cur = [];
      }
    }
  });
  if (cur.length) rows.push(cur);
  return { rows, absents, cds };
}

/* ========== le chargement des donnees ========== */

/** @param {string} f */
function repoDe(f) {
  return f ? f.replace(/y$/, 'ie') + 's' : '<famille>';
}

/** L'appel SDK que l'agent devra ecrire, quand il est deja connu. Le `chargementDe` de V1.
 * @param {any} porte @param {any} [brief] @returns {string} */
export function chargementDe(porte, brief) {
  const j = jeuDe(porte, brief);
  if (!j) return '-';
  if (estDelegue(j, 'famille') || !j.famille)
    return "à écrire par l'agent, une fois la famille choisie";
  return (
    'sdk.' + repoDe(j.famille) + '.list({' + (j.type ? " type: '" + j.type + "'" : '') + ' })'
  );
}

/* ========== l'avancement : ce qui debloque la suite ========== */

/** Le `vuesPretes` de V1. @param {any} porte @returns {boolean} */
function vuesPretes(porte) {
  return porte.vues
    .filter((v) => v !== 'core.fiche')
    .every((v) => {
      const b = bindingDe(porte, v);
      if (b.auto) return true;
      if (v === 'core.tableau') return (b.colonnes || []).some((c) => c.champ);
      if (v === 'core.kanban') return !!(b.title && b.groupKey);
      if (v === 'core.calendrier') return !!(b.title && b.dateStart);
      return !!b.title;
    });
}

/**
 * Jusqu'ou la porte est remplie : 1 identite, 2 vues, 3 detail par vue, 9 complete.
 * Le `etape` de V1, MOINS son palier « jeu de donnees ».
 *
 * IL EST PARTI AVEC LE JEU. Un jeu vit a la racine et peut etre partage par trois
 * portes : compter son remplissage dans l'avancement de CHACUNE ferait dire trois fois
 * la meme chose, et « complete le jeu de donnees » enverrait dans une porte le travail
 * qui se fait dans la section « Jeux de donnees ». Ce que la porte doit encore dire de
 * son jeu (l'a-t-elle choisi, quelle tranche en montre-t-elle) reste un VERROU
 * (`brief/validate.mjs`), pas un palier.
 *
 * @param {any} porte @returns {number}
 */
export function etapeDe(porte) {
  /* Le prompt pour l'agent est facultatif : une porte Canvas n'a pas de palier. */
  if (porte.custom) return 9;
  if (!porte.label || !porte.id) return 1;
  if (!porte.vues.length) return 2;
  const t = vuesPretes(porte);
  if (!t) return 3;
  return 9;
}

/* ========================================================================== */
/* « L'AGENT DECIDE » : l'etat d'une SECTION, et ce qu'on lui laisse comme piste */
/* ========================================================================== */

/**
 * LA BASCULE DE SECTION PILOTE LES POINTS FINS, ELLE NE LES REMPLACE PAS.
 *
 * Le formulaire porte neuf points de delegation FINS, et toute la boucle de relecture
 * repose dessus : `revue.mjs` sait quel drapeau baisser quand une proposition est
 * acceptee, `prompt.mjs` sait dire « je te laisse decider » champ par champ. On n'en
 * supprime aucun. Une bascule de section est un GESTE GROUPE par-dessus :
 *
 *   - la lever leve tous les drapeaux de sa section ;
 *   - la baisser les rabaisse tous ;
 *   - decocher ensuite UN point a la main met la section en etat INTERMEDIAIRE
 *     (`'partiel'`), qui dit « une partie seulement ».
 *
 * Trois sections n'ont AUCUN point fin (Parametres, Liens, Automatisations) : elles
 * portent leur propre drapeau de section, sur le modele de `demoAuto`. C'est la seule
 * difference entre les deux familles, et `etatDeSection` la gomme : de l'exterieur, une
 * section gouverne N booleens, qu'ils soient fins ou non.
 *
 * COCHER GRISE, ET LA PISTE A UN ENDROIT A ELLE. Une unite deleguee ne se saisit plus :
 * son interieur est gele, et une zone de texte libre prend le relais. Ce texte vit dans
 * le sac `instructions` (`model.mjs`), lu et ecrit ICI par `instructionDe` et
 * `ecrireInstruction`, sous l'identifiant de l'unite : `SECTIONS[x].id` au grain SECTION,
 * `PointDeSection.cle` au grain SOUS-SECTION. Jamais un titre affiche : un lien deduit
 * d'un libelle casse au premier mot change.
 *
 * CE MODULE NE DECIDE PAS DE L'AFFICHAGE. Il dit ce qu'une section gouverne et comment la
 * basculer ; ou la bascule se dessine, et ce que le prompt en fait, appartient a l'UI et a
 * `prompt.mjs`. Il ne connait donc ni React, ni le store : `basculerSection` rend un PATCH,
 * l'appelant le passe a `patch()` (brief), `patchPorte(uid, ...)` (porte) ou
 * `patchJeu(uid, ...)` (jeu).
 */

/**
 * @typedef {Object} Section
 * @property {string} id      l'identifiant STABLE, la clef de `SECTIONS`. C'est lui qu'on
 *                            partage entre modules, jamais un titre recopie a la main.
 * @property {'brief'|'porte'|'jeu'} niveau ou vivent ses drapeaux : a la racine du brief,
 *                            sur une porte, ou sur un JEU de donnees. Il dit aussi quelle
 *                            `cible` les fonctions attendent.
 * @property {string} titre   le titre affiche, tel que le fondateur le lit.
 * @property {string} ancre   l'ancre du rail (`SectionCadre`). C'est un IDENTIFIANT
 *                            stable, jamais un rang : les quatre sections d'app gardent
 *                            les ids du Launcher (`s4`..`s8`), et le RANG, lui, se lit
 *                            dans `SECTIONS_BRIEF` (`brief/validate.mjs`). Notifications
 *                            porte donc `s8` tout en se rangeant avant `s7`.
 * @property {string} couvre  ce que la section couvre, en clair : la fin de la phrase
 *                            « Je te laisse decider : <couvre>. » du prompt.
 * @property {string|null} drapeau la clef de son drapeau PROPRE, quand elle en a un.
 */

/**
 * Un point de delegation FIN gouverne par une section.
 * @typedef {Object} PointDeSection
 * @property {string} cle      le chemin du drapeau DANS la cible : `auto.famille`,
 *                             `vb["core.liste"].auto`, `ftAuto.tri`. Jamais le chemin
 *                             absolu d'une proposition de relecture, qui prefixe
 *                             `portes[uid=...]` : celui-la se fabrique dans l'UI.
 * @property {string} libelle  ce que ce point delegue, en francais.
 * @property {boolean} delegue sa valeur, lue comme partout ailleurs.
 * @property {string} [vue]    l'id de vue, pour les points du sac `vb`.
 */

/**
 * @typedef {Object} EtatDeSection
 * @property {Section} section     le descripteur
 * @property {PointDeSection[]} points les points FINS, dans l'ordre du formulaire. VIDE pour
 *                                 une section qui n'a qu'un drapeau propre.
 * @property {string|null} drapeau la clef de son drapeau propre, ou `null`
 * @property {number} total        combien de booleens la section gouverne (points + drapeau)
 * @property {number} delegues     combien sont leves
 * @property {'aucun'|'partiel'|'tous'} etat l'etat de la bascule. `'partiel'` est l'etat
 *                                 INTERMEDIAIRE de la spec : ni cochee ni vide.
 * @property {boolean} pilotable   `false` quand la section ne gouverne RIEN aujourd'hui (une
 *                                 porte qui ne monte que la fiche n'a aucun detail par vue) :
 *                                 il n'y a alors pas de bascule a dessiner.
 * @property {string} instruction  le texte libre pose sur CETTE section, deja `trim()`. Vide
 *                                 quand il n'y en a pas : l'UI n'a jamais a relire le sac.
 */

/** Les quatre points fins du jeu de donnees, sac `auto` du JEU. */
const POINTS_DS = [
  { cle: 'famille', libelle: 'la famille du socle' },
  { cle: 'type', libelle: 'le type local' },
  { cle: 'scope', libelle: 'la portée' },
  { cle: 'decoupe', libelle: 'le découpage' },
];

/** Les trois points fins de « Filtres et tri », sac `ftAuto`. */
const POINTS_FT = [
  { cle: 'filtres', libelle: 'les filtres' },
  { cle: 'favoris', libelle: 'les favoris' },
  { cle: 'tri', libelle: 'le tri par défaut' },
];

/**
 * LE CATALOGUE DES SECTIONS DELEGUABLES, et lui seul fait foi.
 *
 * Une section absente d'ici n'a pas de case « l'agent décide » : c'est le cas de la Carte
 * d'identite, du Metier et des Portes (le fondateur seul peut les ecrire, et il l'a tranche
 * pour la premiere : « identite est toujours rempli par le user, jamais ia agent ou
 * autre »), et aujourd'hui des sections Porte, Vues, Actions et Stats d'une porte
 * ouverte, qui n'ont ni point fin ni drapeau propre. Leur en donner un est UNE LIGNE ici plus une clef dans `nouvellePorte()` :
 * c'est volontairement le seul endroit a toucher.
 *
 * Pas de `numero` dans ce catalogue : il n'est pas stable (une porte 100% custom descend
 * « Jeu de donnees » de 2 a 3), et un numero faux dans une table partagee vaut moins que pas
 * de numero du tout. Il reste ou il est deja, pose par le composant qui rend la section.
 *
 * PAS d'annotation `Record<string, Section>` sur cette constante, et ce n'est pas un oubli :
 * sous `noUncheckedIndexedAccess`, un Record rendrait `SECTIONS.jeu` possiblement `undefined`
 * chez tous les appelants TypeScript. On laisse donc la forme s'inferer (l'acces par point
 * marche), et l'acces DYNAMIQUE passe par `sectionDe(id)` ou par les deux listes ordonnees.
 */
export const SECTIONS = Object.freeze({
  /* --- la vue d'ensemble : les drapeaux vivent a la RACINE du brief ---
     Pas de « Parametres » : ils viennent du socle, rien ne s'y confie. */
  liens: Object.freeze({
    id: 'liens',
    niveau: /** @type {'brief'} */ ('brief'),
    titre: 'Liens',
    ancre: 's5',
    couvre: 'les liens avec les autres apps',
    drapeau: 'liensAuto',
  }),
  extra: Object.freeze({
    id: 'extra',
    niveau: /** @type {'brief'} */ ('brief'),
    titre: 'Automatisations',
    ancre: 's6',
    couvre: "les automatisations de l'app",
    drapeau: 'extraAuto',
  }),
  /*
   * LES NOTIFICATIONS SONT UNE SECTION A ELLES, sortie des automatisations : une
   * automatisation AGIT sur les donnees, une notification PREVIENT quelqu'un.
   * Meme forme que les trois du dessus, drapeau compris : elle n'a aucun point
   * fin, donc son drapeau EST sa delegation.
   */
  notifications: Object.freeze({
    id: 'notifications',
    niveau: /** @type {'brief'} */ ('brief'),
    titre: 'Notifications',
    ancre: 's8',
    couvre: "les notifications de l'app : qui est prévenu, et quand",
    drapeau: 'notificationsAuto',
  }),
  /*
   * Le jeu de demo est le NEUVIEME point fin de la boucle de relecture (`demoAuto` gouverne
   * `demoDesequilibre` comme `auto.famille` gouverne `jeu.famille`) ET le drapeau de sa
   * section : c'est le meme booleen, vu de deux endroits. Donc accepter une proposition sur
   * `demoDesequilibre` fait retomber la bascule de la section 7, sans un octet de code en
   * plus. C'est exactement ce qu'on veut : un seul geste, une seule verite.
   */
  demo: Object.freeze({
    id: 'demo',
    niveau: /** @type {'brief'} */ ('brief'),
    titre: 'Jeu de démo',
    ancre: 's7',
    couvre: 'le jeu de démonstration',
    drapeau: 'demoAuto',
  }),

  /*
   * UN JEU DE DONNEES OUVERT : les drapeaux vivent sur le JEU, pas sur la porte.
   *
   * C'EST LE SEUL DE SON NIVEAU, et c'est tout le lot : le jeu etait une
   * sous-section d'une porte, donc un jeu partage par trois portes offrait TROIS
   * cases « l'agent decide » pour un seul reglage, et le denominateur de la ligne
   * « Confie a l'agent » comptait trois unites la ou il n'y en a qu'une.
   */
  jeu: Object.freeze({
    id: 'jeu',
    niveau: /** @type {'jeu'} */ ('jeu'),
    titre: 'Jeu de données',
    ancre: 'jd',
    couvre: 'ce jeu de données',
    drapeau: null,
  }),

  /* --- une porte ouverte : les drapeaux vivent sur la PORTE --- */
  detail: Object.freeze({
    id: 'detail',
    niveau: /** @type {'porte'} */ ('porte'),
    titre: 'Détail par vue',
    ancre: 'vb',
    couvre: 'le détail de chaque vue de cette porte',
    drapeau: null,
  }),
  fiche: Object.freeze({
    id: 'fiche',
    niveau: /** @type {'porte'} */ ('porte'),
    titre: 'Fiche',
    ancre: 'fi',
    couvre: 'la fiche de cette porte',
    drapeau: null,
  }),
  filtres: Object.freeze({
    id: 'filtres',
    niveau: /** @type {'porte'} */ ('porte'),
    titre: 'Filtres et tri',
    ancre: 'ft',
    couvre: 'les filtres, les favoris et le tri de cette porte',
    drapeau: null,
  }),
  /*
   * LES STATS SE DELEGUENT PAR UN DRAPEAU, pas par des points fins, et c'est le
   * fondateur qui l'a demandee : « Pour 8 Stats : active aussi IA completion ».
   *
   * SON DRAPEAU VIT SUR LA PORTE, pas a la racine du brief, contrairement aux
   * quatre autres sections a drapeau (Parametres, Liens, Automatisations, Jeu de
   * demo) : deux portes du meme brief ne comptent pas les memes choses, et l'une
   * peut vouloir ses stats quand l'autre les laisse. C'est exactement ou vit
   * deja `ftAuto`. `basculerSection` n'a rien a apprendre pour ca : il ecrit un
   * drapeau sur la CIBLE qu'on lui passe, et le niveau dit laquelle.
   */
  stats: Object.freeze({
    id: 'stats',
    niveau: /** @type {'porte'} */ ('porte'),
    titre: 'Stats',
    ancre: 'st',
    couvre: 'les compteurs de cette porte',
    drapeau: 'statsAuto',
  }),
});

/**
 * Les sections deleguables de la vue d'ensemble, dans l'ordre du formulaire. Des
 * DESCRIPTEURS, pas des identifiants : `SECTIONS_BRIEF.map((s) => ...)` se type tout seul,
 * la ou `ids.map((id) => SECTIONS[id])` obligerait chaque appelant a indexer une table gelee.
 */
export const SECTIONS_BRIEF = Object.freeze([
  SECTIONS.liens,
  SECTIONS.extra,
  SECTIONS.notifications,
  SECTIONS.demo,
]);

/**
 * LA SECTION DELEGUABLE D'UN JEU DE DONNEES. Une seule aujourd'hui, et la liste existe
 * quand meme : c'est elle que l'ecran parcourt, comme il parcourt les deux autres.
 */
export const SECTIONS_JEU = Object.freeze([SECTIONS.jeu]);

/** Les sections deleguables d'une porte ouverte, dans l'ordre du formulaire. */
export const SECTIONS_PORTE = Object.freeze([
  SECTIONS.detail,
  SECTIONS.fiche,
  SECTIONS.filtres,
  SECTIONS.stats,
]);

/** Le descripteur d'une section, ou `null` si l'identifiant est inconnu.
 * @param {string} id @returns {Section|null} */
export function sectionDe(id) {
  const table = /** @type {Record<string, Section>} */ (/** @type {unknown} */ (SECTIONS));
  return Object.prototype.hasOwnProperty.call(table, id) ? table[id] : null;
}

/**
 * Le descripteur, ou une erreur qui NOMME les identifiants connus. Quatre modules ecrivent
 * contre cette table : une faute de frappe doit se voir tout de suite, pas se comporter en
 * section vide qu'on cherchera une heure.
 * @param {string|Section} id @returns {Section}
 */
function exiger(id) {
  const s = typeof id === 'string' ? sectionDe(id) : id && sectionDe(id.id);
  if (s) return s;
  const connues = Object.keys(SECTIONS).join(', ');
  throw new Error(
    'section inconnue : ' + JSON.stringify(typeof id === 'string' ? id : id && id.id) +
      ' (connues : ' + connues + ')',
  );
}

/**
 * LE SEUL PIEGE DE CETTE API : passer le brief la ou la porte est attendue, ou l'inverse.
 * Rien ne planterait, la section repondrait simplement « rien de delegue » pour toujours, et
 * on chercherait une heure.
 *
 * TROIS NIVEAUX DEPUIS QUE LE JEU EST SORTI DE LA PORTE, et le predicat a du etre refait :
 * il reposait sur « une porte est le seul objet a porter `ds` et `vues` », or `ds` n'existe
 * plus. Chaque niveau se reconnait donc a une clef qui n'appartient qu'a lui : `portes`
 * pour le brief, `vues` pour une porte, `champs` pour un jeu. On ne parle QUE quand la
 * confusion est PROUVEE : une cible qu'aucun des trois ne reconnait passe sans un mot.
 *
 * @param {Section} s @param {any} cible @returns {void}
 */
function verifierCible(s, cible) {
  if (!cible || typeof cible !== 'object') return;
  const vu = {
    brief: Array.isArray(cible.portes),
    porte: Array.isArray(cible.vues),
    jeu: Array.isArray(cible.champs),
  };
  const NOMS = { brief: 'le BRIEF', porte: 'la PORTE', jeu: 'le JEU de données' };
  if (vu[s.niveau]) return;
  for (const autre of ['brief', 'porte', 'jeu']) {
    if (autre === s.niveau || !vu[autre]) continue;
    throw new Error(
      'section "' + s.id + '" : elle attend ' + NOMS[s.niveau] + ', on lui a passé ' + NOMS[autre] + '.',
    );
  }
}

/** Les vues dont le detail se regle dans la section « Detail par vue » : celles que la porte
 * MONTE, la fiche exceptee (elle a sa propre section). @param {any} porte @returns {string[]} */
function vuesDeDetail(porte) {
  const vues = (porte && porte.vues) || [];
  return [...new Set(vues.filter((v) => typeof v === 'string' && v !== 'core.fiche'))];
}

/** Le libelle d'une vue, celui du socle. @param {string} vue @returns {string} */
function libelleDeVue(vue) {
  const v = VUES.find((x) => x.id === vue);
  return 'la vue ' + (v ? v.label : vue);
}

/**
 * Les points fins gouvernes par une section, dans l'ordre du formulaire.
 *
 * VIDE pour une section qui n'a qu'un drapeau propre (Parametres, Liens, Automatisations,
 * Jeu de demo), et vide aussi pour « Detail par vue » quand la porte ne monte que la fiche.
 *
 * `cible` est le BRIEF pour une section de niveau `brief`, la PORTE pour une section de
 * niveau `porte`, le JEU pour la section de niveau `jeu` : le descripteur dit lequel, et
 * il n'y en a jamais deux a passer.
 *
 * @param {string|Section} id @param {any} cible @returns {PointDeSection[]}
 */
export function pointsDeSection(id, cible) {
  const s = exiger(id);
  verifierCible(s, cible);
  const p = cible || {};
  if (s.id === 'jeu') {
    /* LE PREFIXE A PERDU SON `ds.`, comme la cible a perdu sa porte : le sac vit
       maintenant a la racine du JEU (`jeu.auto.famille`). */
    return POINTS_DS.map((x) => ({
      cle: 'auto.' + x.cle,
      libelle: x.libelle,
      delegue: estDelegue(p, x.cle),
    }));
  }
  if (s.id === 'detail') {
    return vuesDeDetail(p).map((v) => ({
      cle: 'vb[' + JSON.stringify(v) + '].auto',
      libelle: libelleDeVue(v),
      delegue: estDelegueVue(p, v),
      vue: v,
    }));
  }
  if (s.id === 'fiche') {
    return [
      {
        cle: 'vb["core.fiche"].auto',
        libelle: 'la fiche',
        delegue: estDelegueVue(p, 'core.fiche'),
        vue: 'core.fiche',
      },
    ];
  }
  if (s.id === 'filtres') {
    return POINTS_FT.map((x) => ({
      cle: 'ftAuto.' + x.cle,
      libelle: x.libelle,
      delegue: !!(p.ftAuto || {})[x.cle],
    }));
  }
  return [];
}

/**
 * L'etat d'une bascule de section : ENTIEREMENT deleguee, PAS DU TOUT, ou EN PARTIE.
 *
 * Le drapeau propre d'une section se lit `=== true`, et ABSENT vaut « pas delegue » : un
 * brief ecrit avant ces clefs se comporte donc exactement comme avant. Les points FINS
 * gardent leur lecture d'origine (`!!`), parce que ce sont eux que `prompt.mjs` et
 * `validate.mjs` lisent deja : deux lectures du meme drapeau finiraient par diverger.
 *
 * @param {string|Section} id @param {any} cible @returns {EtatDeSection}
 */
export function etatDeSection(id, cible) {
  const s = exiger(id);
  verifierCible(s, cible);
  const points = pointsDeSection(s, cible);
  const total = points.length + (s.drapeau ? 1 : 0);
  const delegues =
    points.filter((x) => x.delegue).length +
    (s.drapeau && (cible || {})[s.drapeau] === true ? 1 : 0);
  /*
   * Une section qui ne gouverne rien n'a pas d'etat : `every` sur une liste vide rendrait
   * « tous » et `some` rendrait « aucun », les deux au meme instant. On tranche pour
   * « aucun » et on le DIT avec `pilotable: false`, plutot que de rendre un « tous » qui
   * ferait dessiner une bascule cochee sur une section sans contenu.
   */
  const etat = total === 0 || delegues === 0 ? 'aucun' : delegues === total ? 'tous' : 'partiel';
  return {
    section: s,
    points,
    drapeau: s.drapeau,
    total,
    delegues,
    etat: /** @type {'aucun'|'partiel'|'tous'} */ (etat),
    pilotable: total > 0,
    /* BRUTE, PARCE QUE CETTE VALEUR ALIMENTE UN `<textarea>` CONTROLE.
       `etatDeSection` sert l'ECRAN ; la nettoyer ici faisait disparaitre chaque
       espace des qu'on le tapait, et interdisait la seconde ligne. Le prompt,
       lui, appelle `instructionPropre` a ses propres points de lecture. */
    instruction: instructionDe(cible, s.id),
  };
}

/* ========================================================================== */
/* LA ZONE D'INSTRUCTIONS : ce qu'on laisse a l'agent quand on lui confie      */
/* ========================================================================== */

/**
 * LE TEXTE LIBRE POSE SUR UNE UNITE DELEGUEE, deja `trim()`, vide quand il n'y en a pas.
 *
 * L'UNITE se nomme par son identifiant STABLE, jamais par un titre affiche : `SECTIONS[x].id`
 * au grain SECTION (`liens`, `jeu`, `filtres`...), `PointDeSection.cle` au grain
 * SOUS-SECTION (`vb["core.liste"].auto`, `ftAuto.tri`). Ce sont deja les identifiants que
 * `pointsDeSection` expose et que `revue.mjs` sait viser : le point dans `vb["core.liste"]`
 * passe par l'accesseur entre crochets, qui existe pour ca.
 *
 * `cible` est le BRIEF pour une unite de niveau `brief`, la PORTE pour une unite de niveau
 * `porte` : le sac vit au meme endroit que les drapeaux qu'il accompagne.
 *
 * @param {any} cible @param {string} idUnite @returns {string}
 */
export function instructionDe(cible, idUnite) {
  const sac = (cible && cible.instructions) || {};
  const brut = Object.prototype.hasOwnProperty.call(sac, idUnite) ? sac[idUnite] : '';
  return String(brut ?? '');
}

/**
 * Le texte d'une unite, PROPRE : sans blanc de bord, prêt pour le prompt.
 *
 * POURQUOI LES DEUX EXISTENT, et c'est un bug paye au premier essai. La zone
 * d'instructions est un `<textarea>` CONTROLE : ce que la lecture rend est
 * repose dans le champ a chaque rendu. Quand cette lecture etait `trim()`ee,
 * taper un ESPACE le voyait disparaitre aussitot (le store gardait « le chef »,
 * le champ affichait « le chef », le curseur remontait) et ENTREE ne creait
 * jamais de seconde ligne. La zone etait inutilisable pour ecrire une phrase.
 *
 * LA LECTURE EST DONC BRUTE, et le nettoyage se fait au SEUL endroit qui en a
 * besoin : la generation du prompt, qui ne veut pas d'un texte a rallonge de
 * blancs. Une saisie ne se nettoie pas pendant qu'on tape.
 *
 * @param {any} cible @param {string} idUnite @returns {string}
 */
export function instructionPropre(cible, idUnite) {
  return instructionDe(cible, idUnite).trim();
}

/**
 * LE PATCH qui pose (ou retire) le texte d'une unite. Rendu pret a poser, comme
 * `basculerSection` : `patch(...)` pour le brief, `patchPorte(uid, ...)` pour une porte,
 * `patchJeu(uid, ...)` pour un jeu.
 *
 * UN TEXTE VIDE SUPPRIME LA CLEF plutot que d'ecrire une chaine vide. Meme discipline que
 * « baisser ne touche QUE ce qui est leve » : absent et vide se lisent pareil, donc ecrire
 * le vide ne ferait que gonfler le brief. Et un patch VIDE (`{}`) quand rien ne change :
 * le poser est un non-evenement.
 *
 * @param {string} idUnite @param {string} texte @param {any} cible
 * @returns {Record<string, any>} le patch, eventuellement vide
 */
export function ecrireInstruction(idUnite, texte, cible) {
  const sac = (cible && cible.instructions) || {};
  const avant = Object.prototype.hasOwnProperty.call(sac, idUnite) ? String(sac[idUnite] ?? '') : '';
  const apres = String(texte ?? '');
  if (avant === apres) return {};
  /** @type {Record<string, any>} */
  const suite = {};
  for (const cle of Object.keys(sac)) {
    if (cle !== idUnite) suite[cle] = sac[cle];
  }
  if (apres.trim() !== '') suite[idUnite] = apres;
  return { instructions: suite };
}

/**
 * Le PATCH qui bascule une section entiere, dans un sens ou dans l'autre.
 *
 * Rendu pret a poser : `patch(basculerSection('liens', true, brief))` pour une section de
 * niveau `brief`, `patchPorte(porte.uid, basculerSection('jeu', true, porte))` pour une
 * section de niveau `porte`. Le patch est de SURFACE, comme le store l'attend : les sacs
 * imbriques (`auto`, `vb`, `ftAuto`) sont recopies entiers, jamais mutes.
 *
 * DEUX REGLES D'ECRITURE, dissymetriques, et c'est voulu :
 *
 *   - LEVER pose `true` partout ou il manque, en creant le sac au besoin : on ne peut pas
 *     dire « delegue » sans ecrire la clef.
 *   - BAISSER ne touche QUE ce qui est leve. Une clef absente vaut deja « pas delegue » :
 *     l'ecrire a `false` gonflerait le brief sans rien changer, et c'est la discipline que
 *     `revue.mjs` tient deja (« absent, deja baisse : rien a faire, et surtout rien a creer »).
 *
 * Donc un patch VIDE (`{}`) quand il n'y a rien a ecrire : le poser est un non-evenement.
 *
 * @param {string|Section} id @param {boolean} valeur @param {any} cible
 * @returns {Record<string, any>} le patch, eventuellement vide
 */
export function basculerSection(id, valeur, cible) {
  const s = exiger(id);
  verifierCible(s, cible);
  const v = valeur === true;
  const p = cible || {};

  if (s.drapeau) {
    const leve = p[s.drapeau] === true;
    return leve === v ? {} : { [s.drapeau]: v };
  }

  if (s.id === 'jeu') {
    /* Le patch se pose sur un JEU (`patchJeu(uid, ...)`), plus sur une porte : le
       sac `auto` est une clef de racine du jeu. */
    const sac = p.auto || {};
    /** @type {Record<string, any>} */
    const suite = {};
    for (const x of POINTS_DS) {
      if (!!sac[x.cle] !== v) suite[x.cle] = v;
    }
    if (!Object.keys(suite).length) return {};
    return { auto: { ...sac, ...suite } };
  }

  if (s.id === 'detail' || s.id === 'fiche') {
    const vues = s.id === 'fiche' ? ['core.fiche'] : vuesDeDetail(p);
    const vb = p.vb || {};
    /** @type {Record<string, any>} */
    const suite = {};
    for (const vue of vues) {
      if (estDelegueVue(p, vue) !== v) suite[vue] = { ...(vb[vue] || {}), auto: v };
    }
    if (!Object.keys(suite).length) return {};
    return { vb: { ...vb, ...suite } };
  }

  // filtres et tri
  const ftAuto = p.ftAuto || {};
  /** @type {Record<string, any>} */
  const suite = {};
  for (const x of POINTS_FT) {
    if (!!ftAuto[x.cle] !== v) suite[x.cle] = v;
  }
  if (!Object.keys(suite).length) return {};
  return { ftAuto: { ...ftAuto, ...suite } };
}

/**
 * BAISSER (OU LEVER) UN SEUL POINT D'UNE SECTION, et pas la section entiere.
 *
 * POURQUOI ELLE EXISTE. `basculerSection` prend tout : cocher, c'est cocher les
 * quatre vues d'un coup. C'est juste pour un GESTE du fondateur (il coche la
 * section), c'est faux pour la RELECTURE : quand l'agent a relu le seul
 * `core.liste`, le `core.kanban` n'a pas ete relu et sa demande tient encore.
 * Sans ce grain, `honorerLaRelecture` decochait le kanban parce que l'agent
 * avait parle de la liste, et le fondateur perdait une delegation qu'il n'avait
 * pas retiree.
 *
 * `cle` EST CELLE QUE `pointsDeSection` EXPOSE (`vb["core.liste"].auto`,
 * `ftAuto.tri`, `auto.famille`) : une seule ecriture de ces clefs dans tout le
 * Studio, celle-la. Une clef inconnue de la section rend un patch VIDE, jamais
 * une ecriture au hasard.
 *
 * @param {string|Section} id @param {string} cle @param {boolean} valeur @param {any} cible
 * @returns {Record<string, any>} le patch, eventuellement vide
 */
export function basculerPoint(id, cle, valeur, cible) {
  const s = exiger(id);
  verifierCible(s, cible);
  const v = valeur === true;
  const p = cible || {};
  const point = pointsDeSection(s.id, p).find((x) => x.cle === cle);
  if (point === undefined) return {};
  if (point.delegue === v) return {};

  if (s.id === 'jeu') {
    const sac = p.auto || {};
    return { auto: { ...sac, [cle.slice('auto.'.length)]: v } };
  }
  if (s.id === 'detail' || s.id === 'fiche') {
    /* `vb["core.liste"].auto` : la vue est entre les guillemets, et on la relit
       par le meme JSON qui l'a ecrite plutot que par une decoupe a la main. */
    const deb = cle.indexOf('[');
    const fin = cle.lastIndexOf(']');
    if (deb < 0 || fin < deb) return {};
    let vue;
    try {
      vue = JSON.parse(cle.slice(deb + 1, fin));
    } catch {
      return {};
    }
    if (typeof vue !== 'string') return {};
    const vb = p.vb || {};
    return { vb: { ...vb, [vue]: { ...(vb[vue] || {}), auto: v } } };
  }
  // filtres et tri
  const ftAuto = p.ftAuto || {};
  return { ftAuto: { ...ftAuto, [cle.slice('ftAuto.'.length)]: v } };
}

/* ========================================================================== */
/* LES TROIS NIVEAUX DE CANVAS : ce qu'un brief DECLARE de hors gabarit        */
/* ========================================================================== */

/**
 * NATIVE = ce qui suit le gabarit. CANVAS = ce qui en sort, en TROIS niveaux
 * nommes : App Canvas, Space Canvas, View Canvas. Le catalogue des niveaux, les
 * zones que chacun a le droit de garder et la date d'accord vivent dans
 * `model.mjs` (`NIVEAUX_CANVAS`, `ZONES_CANVAS`) ; ce bloc ne fait que LIRE un
 * brief a travers eux. Doctrine : `docs/GABARIT-ECRAN.md` section 9, regle G23.
 *
 * TOUT LE MONDE LIT ICI, ET NULLE PART AILLEURS. Trois modules et le formulaire
 * ont besoin de savoir « cette app sort-elle du gabarit, et comment » : trois
 * lectures separees du meme booleen finiraient par diverger. Les predicats
 * ci-dessous sont la seule reponse.
 *
 * Ces fonctions DECRIVENT, elles ne JUGENT pas : une declaration incoherente
 * (App Canvas qui garde ses portes, date manquante, zone `3c` cochee) ressort
 * telle quelle. C'est `validate.mjs` qui la refuse, en une phrase qui dit
 * pourquoi.
 */

/** @param {any} brief @returns {boolean} l'app entiere sort du gabarit : App Canvas. */
export function estAppCanvas(brief) {
  return !!brief && brief.appCustom === true;
}

/** @param {any} porte @returns {boolean} cette porte sort du gabarit : Space Canvas. */
export function estSpaceCanvas(porte) {
  return !!porte && porte.custom === true;
}

/**
 * Cette porte monte-t-elle au moins une vue a elle : View Canvas.
 *
 * Une porte en Space Canvas rend `false` meme si elle traine un `vuesCustom` :
 * son composant pilote ses lentilles via poserVues, independamment des vues
 * declarees d'une collection. Une vue sans nom ne compte pas non plus : elle
 * ne peut pas produire d'id namespace pour la fiche.
 *
 * @param {any} porte @returns {boolean}
 */
export function aDesViewCanvas(porte) {
  if (!porte || estSpaceCanvas(porte)) return false;
  if (porte.customVues !== true) return false;
  return (porte.vuesCustom || []).some((/** @type {any} */ v) => ((v && v.nom) || '').trim());
}

/**
 * Les zones que ce canvas GARDE du gabarit, filtrees par la table du niveau et
 * remises dans l'ordre du plan des zones.
 *
 * Une clef ABSENTE rend `[]`, exactement comme un tableau vide : un canvas qui
 * ne garde rien, c'est-a-dire le comportement d'avant ces clefs. Ce qui a ete
 * coche a tort (`3c` sur une porte, `3a` sur une app, une zone inventee) est
 * simplement ECARTE ici ; `validate.mjs` le NOMME, phrase par phrase.
 *
 * @param {'app'|'space'|'view'} niveau
 * @param {any} cible le BRIEF pour `app`, la PORTE pour `space`
 * @returns {string[]}
 */
export function zonesGardees(niveau, cible) {
  const n = niveauCanvasDe(niveau);
  if (!n || !n.clefZones || !cible) return [];
  const brut = cible[n.clefZones];
  if (!Array.isArray(brut)) return [];
  return n.zones.filter((z) => brut.includes(z));
}

/** Le cadre canonique, avec lecture des anciens raccourcis. */
export function elementsGardes(niveau, cible) {
  const clef = niveau === 'app' ? 'appCanvasElements' : 'canvasElements';
  return resoudreElementsCadre({ level: niveau, ...(Array.isArray(cible?.[clef])
    ? { elements: cible[clef] } : { keeps: zonesGardees(niveau, cible) }) }).filter((id) => niveau !== 'app' || Array.isArray(cible?.[clef]) || id !== 'vues');
}

/** La declaration App Canvas, identique a la naissance et a la synchronisation. */
export function cadreAppDeBrief(brief) {
  const elements = elementsGardes('app', brief);
  /* Une App Canvas n'a pas de moteur : tout element garde est alimente par la
     surface, donc declare dans `alimente`. Ce que la reprise y a lu reste
     (capacite dormante d'un element retire, `useCadreSurface`). */
  const alimente = alimenteSansMoteur(elements, brief.appCanvasAlimente);
  return { elements, ...(alimente.length ? { alimente } : {}) };
}

/**
 * Une porte Canvas qui ne montre AUCUN jeu n'a pas de moteur : comme l'App
 * Canvas, tout element garde est alimente par la surface (meme logique,
 * `alimenteSansMoteur`). Avec un jeu, le moteur alimente, et seul ce que la
 * reprise a lu dans `alimente` reste.
 * @param {any} porte @returns {boolean}
 */
export function porteCanvasSansMoteur(porte) {
  return estSpaceCanvas(porte) && (porte.sansJeu === true || !porte.dsRef);
}

/** La declaration `canvas` d'une porte Canvas, identique a la naissance et a la synchronisation. */
export function cadrePorteDeBrief(porte) {
  const elements = elementsGardes('space', porte);
  const lu = Array.isArray(porte?.canvasAlimente) ? porte.canvasAlimente : [];
  const alimente = porteCanvasSansMoteur(porte) ? alimenteSansMoteur(elements, lu, 'space') : lu;
  return { elements, ...(alimente.length ? { alimente } : {}) };
}

/**
 * Ce qui a ete coche et que le niveau REFUSE : le complement de `zonesGardees`,
 * dans l'ordre ou le fondateur l'a coche, sans doublon.
 * @param {'app'|'space'|'view'} niveau @param {any} cible @returns {string[]}
 */
export function zonesRefusees(niveau, cible) {
  const n = niveauCanvasDe(niveau);
  if (!n || !n.clefZones || !cible) return [];
  const brut = cible[n.clefZones];
  if (!Array.isArray(brut)) return [];
  /** @type {string[]} */
  const out = [];
  for (const z of brut) {
    const id = String(z);
    if (n.zones.includes(id) || out.includes(id)) continue;
    out.push(id);
  }
  return out;
}

/**
 * L'id NAMESPACE d'une vue custom, tel que la fiche l'exige : `<app>.<vue>`
 * (`canvas.views`, et `pnpm canvas:check` le recoupe avec le code). Rend `''`
 * tant que l'app ou la vue n'a pas de nom : on ne fabrique pas un id a moitie.
 * @param {any} brief @param {any} vueCustom @returns {string}
 */
export function idDeVueCustom(brief, vueCustom) {
  /* UN ID DEJA ECRIT GARDE SON NOM, comme le nom d'appel d'un filtre : la
     reprise le pose (`vuesCustom[].id` du fichier) quand le libelle ne le
     redonne pas, sinon renommer la vue renommerait son composant et l'accord
     G23 qui la couvre. */
  const ecrit = String((vueCustom && vueCustom.id) || '').trim();
  if (/^[a-z][a-z0-9-]*\.[a-z][a-z0-9-]*$/.test(ecrit)) return ecrit;
  const app = kebab((brief && brief.id) || '');
  const nom = kebab(((vueCustom && vueCustom.nom) || '').trim());
  return app && nom ? app + '.' + nom : '';
}

/**
 * LES VUES CANVAS DU BRIEF, nommees et pretes a ecrire (`ecran.vuesCustom`),
 * dans l'ordre des portes : `{ id, label, niveau, porte, jeu }`. Une vue de
 * niveau `record` est une VUE DE FICHE du jeu de sa porte (`jeu`), une vue de
 * niveau `collection` se monte dans les vues de sa porte. Une vue sans nom, ou
 * dont l'id sort deja, n'est pas comptee : on ne fabrique pas un id a moitie,
 * et deux portes du meme jeu qui nommeraient la meme vue de fiche n'en font
 * qu'une, celle de la premiere.
 *
 * @param {any} brief
 * @returns {{ id: string, label: string, niveau: 'collection'|'record', porte: any, jeu: { family: string, type?: string } | null }[]}
 */
export function vuesCanvasDuBrief(brief) {
  const sortie = [];
  const vus = new Set();
  for (const porte of Array.isArray(brief && brief.portes) ? brief.portes : []) {
    if (!aDesViewCanvas(porte)) continue;
    const jeu = jeuDePorte(porte, brief);
    for (const v of porte.vuesCustom || []) {
      const label = ((v && v.nom) || '').trim();
      const id = idDeVueCustom(brief, v);
      if (label === '' || id === '' || vus.has(id)) continue;
      const niveau = niveauDeVueCustom(v);
      /* UNE VUE DE FICHE SANS JEU N'A RIEN A MONTRER : elle attend le jeu. */
      if (niveau === 'record' && (jeu === null || estFamilleADecider(jeu.family))) continue;
      vus.add(id);
      sortie.push({
        id,
        label,
        niveau,
        porte,
        jeu: jeu === null ? null : jeu.type === undefined ? { family: jeu.family } : { family: jeu.family, type: jeu.type },
      });
    }
  }
  return sortie;
}

/**
 * LES VUES DE FICHE QU'UNE PORTE PEUT MONTRER, hors « Fiche » : les vues Canvas
 * de niveau `record` de son JEU (quelle que soit la porte du brief qui les
 * declare : une vue de fiche se declare par jeu) et les vues HTML publiees pour
 * ce jeu (`brief.vuesHtml`). `{ id, label, sorte }`, dans cet ordre.
 *
 * @param {any} porte @param {any} brief
 * @returns {{ id: string, label: string, sorte: 'canvas'|'html' }[]}
 */
export function vuesDeFicheDePorte(porte, brief) {
  const lu = ecranDesVuesDeFiche(porte, brief);
  if (lu === null) return [];
  const { ecran, canvas, html } = lu;
  return vuesDeFicheDeLaPorte(ecran, 'porte', { sansRestriction: true }).map((id) => {
    const vueCanvas = canvas.find((v) => v.id === id);
    if (vueCanvas !== undefined) return { id, label: vueCanvas.label, sorte: /** @type {const} */ ('canvas') };
    const vueHtml = html.find((v) => v.id === id);
    return { id, label: String(vueHtml?.label || id), sorte: /** @type {const} */ ('html') };
  });
}

/**
 * L'ECRAN QUE LA REGLE DU SOCLE LIT (`vuesDeFicheDeLaPorte`, `@taki/data-model`,
 * sa seule implementation) : le brief lui presente l'ecran tel que la
 * derivation l'ecrira (ses vues Canvas de fiche nommees, ses vues HTML) et la
 * porte avec son jeu et ses `vuesFiche` ; il ne garde ici que les libelles et
 * la sorte. `null` pour une porte sans jeu.
 *
 * @param {any} porte @param {any} brief
 */
function ecranDesVuesDeFiche(porte, brief) {
  const jeu = jeuDePorte(porte, brief);
  if (jeu === null) return null;
  const canvas = vuesCanvasDuBrief(brief).filter((v) => v.niveau === 'record' && v.jeu !== null);
  const html = (Array.isArray(brief && brief.vuesHtml) ? brief.vuesHtml : []).filter(
    (v) => v !== null && typeof v === 'object' && typeof v.id === 'string' && v.jeu !== null && typeof v.jeu === 'object' && typeof v.jeu.family === 'string',
  );
  const ecran = {
    portes: [{
      id: 'porte',
      jeu: jeu.type === undefined ? { family: jeu.family } : { family: jeu.family, type: jeu.type },
      ...(Array.isArray(porte?.vuesFiche) ? { vuesFiche: porte.vuesFiche.map(String) } : {}),
    }],
    vuesCustom: canvas.map((v) => ({ id: v.id, niveau: 'record', jeu: v.jeu })),
    vuesHtml: html.map((v) => ({ id: v.id, jeu: v.jeu })),
  };
  return { ecran, canvas, html };
}

/**
 * LES CHAMPS QUE « EN TÊTE DE FICHE » PEUT MONTRER sur cette porte : les champs
 * a choix UNIQUE de son jeu, natifs a catalogue compris, `{ uid, nom, label }`
 * (`uid` celui du brief, `nom` celui du fichier). Une etape est UNE valeur :
 * un choix multiple n'a pas de place dans la rangee 3.
 *
 * @param {any} porte @param {any} brief
 * @returns {{ uid: string, nom: string, label: string }[]}
 */
export function champsDEtapes(porte, brief) {
  const jeu = jeuDe(porte, brief);
  if (jeu === null) return [];
  /* LA REGLE DU SOCLE (`champsDEnTeteDuJeu`), une seule lecture avec les
     Parametres et le Studio d'une app lue : elle juge le jeu tel que la
     derivation l'ecrira (ses champs declares, ses masques) et les natifs tels
     que leur colonne de catalogue les porte. Ici, on la rend aux uids. */
  const traduits = traduireChamps(jeu, brief);
  const offerts = new Set(
    champsDEnTeteDuJeu(
      { champs: traduits.flatMap((t) => (t.declare === null ? [] : [t.declare])), masques: Array.isArray(jeu.masques) ? jeu.masques : [] },
      traduits.flatMap((t) => (t.natif ? [t.natif] : [])),
    ).map((c) => c.value),
  );
  const sortie = [];
  for (const traduit of traduits) {
    const cible = traduit.declare !== null ? traduit.declare : traduit.natif;
    if (!cible || !offerts.has(traduit.nom)) continue;
    const champ = (Array.isArray(jeu.champs) ? jeu.champs : []).find((c) => c && c.uid === traduit.uid);
    sortie.push({
      uid: traduit.uid,
      nom: traduit.nom,
      label: String((cible.label || (champ && champ.nom) || traduit.nom)),
    });
  }
  return sortie;
}

/**
 * @typedef {Object} SpaceCanvas
 * @property {string} uid   l'uid de la porte, pour designer une ligne du formulaire
 * @property {string} id    son id technique, celui qui part dans `canvas.spaces[].id`
 * @property {string} label son nom lisible, pour une phrase adressee au fondateur
 * @property {string[]} keeps ce qu'elle garde de la rangee 3
 */

/**
 * @typedef {Object} ViewCanvas
 * @property {string} uid   l'uid de la vue custom
 * @property {string} porte l'uid de la porte qui la monte
 * @property {string} nom   son nom, tel que le fondateur l'a ecrit
 * @property {string} id    son id namespace, `<app>.<vue>`, ou `''`
 */

/**
 * @typedef {Object} EtatCanvas
 * @property {boolean} app        l'App Canvas est declaree
 * @property {string[]} appKeeps  ce que son en-tete reduit reprend
 * @property {SpaceCanvas[]} spaces les portes en Space Canvas, dans l'ordre du menu
 * @property {ViewCanvas[]} views   les vues custom nommees, dans l'ordre de saisie
 * @property {string[]} niveaux   les ids de niveau DECLARES, dans l'ordre de la doctrine
 * @property {boolean} aucun      rien ne sort du gabarit : l'app est NATIVE
 * @property {string} grantedOn   la date d'accord, telle quelle (jamais reparee ici)
 * @property {SpaceCanvas[]} contradiction les portes declarees malgre une App Canvas
 *   a surface unique. Le rail ne determine pas le niveau : une App Canvas peut le garder.
 *   Vide dans tous les autres cas.
 */

/**
 * Ce que ce brief declare de hors gabarit, en un seul objet.
 * @param {any} brief @returns {EtatCanvas}
 */
export function niveauxCanvasDe(brief) {
  const b = brief || {};
  const app = estAppCanvas(b);
  const portes = Array.isArray(b.portes) ? b.portes : [];

  /** @type {SpaceCanvas[]} */
  const spaces = [];
  /** @type {ViewCanvas[]} */
  const views = [];
  for (const p of portes) {
    if (!p || typeof p !== 'object') continue;
    if (estSpaceCanvas(p)) {
      spaces.push({
        uid: p.uid || '',
        id: p.id || '',
        label: p.label || '',
        keeps: zonesGardees('space', p),
      });
      continue;
    }
    if (!aDesViewCanvas(p)) continue;
    for (const v of p.vuesCustom || []) {
      const nom = ((v && v.nom) || '').trim();
      if (!nom) continue;
      views.push({ uid: (v && v.uid) || '', porte: p.uid || '', nom, id: idDeVueCustom(b, v) });
    }
  }

  /** @type {string[]} */
  const niveaux = [];
  if (app) niveaux.push('app');
  if (spaces.length) niveaux.push('space');
  if (views.length) niveaux.push('view');

  return {
    app,
    appKeeps: zonesGardees('app', b),
    spaces,
    views,
    niveaux,
    aucun: niveaux.length === 0,
    grantedOn: typeof b.canvasGrantedOn === 'string' ? b.canvasGrantedOn : '',
    contradiction: app ? spaces : [],
  };
}

/**
 * LA CLE `canvas` DE `taki.app.json`, prete a ecrire, ou `null` si l'app est
 * entierement NATIVE (auquel cas la fiche ne porte pas la clef du tout).
 *
 * Forme exacte attendue par `scripts/check-app-entities.mjs` : `grantedOn`
 * obligatoire, puis `app` (qui EXCLUT `spaces` et `views`), ou `spaces` et
 * `views` ensemble. La liste elements est toujours explicite, vide comprise.
 * Le cadre des portes ne se duplique pas dans la fiche.
 *
 * Cette fonction DECRIT, elle ne repare pas. Sur la contradiction « App Canvas
 * qui garde des portes » elle rend le niveau DECLARE (`app` seul), parce que
 * c'est ce que le fondateur a coche ; c'est `validerTout` qui refuse de generer
 * tant que la contradiction tient.
 *
 * @param {any} brief
 * @returns {{ grantedOn: string, app?: { elements: string[], alimente?: string[] }, spaces?: { id: string }[], views?: string[] } | null}
 */
export function ficheCanvasDe(brief) {
  const e = niveauxCanvasDe(brief);
  if (e.aucun) return null;

  /** @type {any} */
  const canvas = { grantedOn: e.grantedOn };
  if (e.app) {
    canvas.app = cadreAppDeBrief(brief);
    return canvas;
  }
  if (e.spaces.length)
    canvas.spaces = e.spaces.map((s) => ({ id: s.id }));
  if (e.views.length) canvas.views = e.views.map((v) => v.id);
  return canvas;
}

/* ========== le fichier de champs d'une app ==========
 *
 * LA MUE DU BRIEF. Un brief decrit une app EN CONSTRUCTION : ses champs, ses
 * portes, ses vues, plus tout un narratif (pour qui, quel probleme, quel geste)
 * qui a fini son travail des que l'app existe.
 *
 * Ce qui SURVIT, c'est la structure, et elle survit sous une autre forme : le
 * `taki.fields.json` que le code importe, que le studio edite, et que la CI
 * verifie. Ce qui MEURT, c'est le narratif : il part dans le prompt de l'agent
 * et dans le corps de la PR, pas dans un fichier a cote du code. Une
 * description que personne ne relit est le cadavre qu'a ete `brief.json`.
 *
 * Cette fonction est le point de passage exact entre les deux mondes. */

/**
 * La nature d'un champ de brief (francais, orientee saisie) devient le type du
 * fichier de champs (anglais, orientee donnee).
 *
 * DEUX VOCABULAIRES, ET UNE SEULE TRADUCTION, ici. `prix` et `pourcentage`
 * disent un FORMAT, pas un type : ils deviennent `number`, et la mise en forme
 * reste une decision d'app. Y mettre `money` ferait entrer la presentation dans
 * un fichier de donnees, et c'est la ligne rouge du chantier.
 *
 * Une nature inconnue n'a pas d'equivalent : elle rend `undefined` et le champ
 * est ECARTE du fichier plutot que traduit de travers. `champ lié` est un
 * `lien` du socle, a condition que sa cible nomme une famille
 * (`cibleDeChampLie`).
 */
/**
 * Le prefixe d'un champ NATIF de la famille dans les fichiers de declaration.
 * Le vocabulaire fait foi dans `packages/data-model/src/app-screens.ts`
 * (`PREFIXE_CHAMP_SOCLE`) ; ce module est sans dependance par contrat
 * (`brief/INTERFACE.md`), il porte donc la chaine et non l'import.
 */
const PREFIXE_SOCLE = 'socle:';

/** La nature du formulaire qui produit un champ `personne`, celle que `NATURES` propose. */
export const NATURE_PERSONNE = "personne de l'organisation";

/* EXPORTEE pour la REPRISE, qui a besoin de la lire a l'envers (un `number` du
   fichier redevient `nombre`, `prix` ou `pourcentage`). Deux tables auraient
   diverge des le premier ajout de nature. */
export const TYPE_PAR_NATURE = {
  texte: 'text',
  'texte long': 'textarea',
  /* TROIS TEXTES QUE LE FICHIER DE CHAMPS DISTINGUE (`custom-field.ts`) : la
     donnee dit ce qu'elle est, meme si le socle les saisit en texte aujourd'hui
     (`EDITEUR_PAR_TYPE`, `packages/ui/src/views/fields.ts`). Sans nature pour
     les dire, une app qui les portait les voyait retomber en `text`. */
  'e-mail': 'email',
  'téléphone': 'phone',
  'lien web': 'url',
  nombre: 'number',
  prix: 'number',
  pourcentage: 'number',
  /* UNE DUREE EST UN NOMBRE DE MINUTES (le socle stocke `durationMinutes`,
     jamais des heures decimales) : `number` sur le disque, et l'habillage
     `duree` du socle pour l'ecrire « 1 h 30 » (`habillageDeNature`). */
  'durée': 'number',
  duree: 'number',
  'oui / non': 'boolean',
  date: 'date',
  'date et heure': 'datetime',
  'liste de choix': 'select',
  /* LE `multiselect` DU SOCLE, QUE LE BRIEF NE SAVAIT PAS DIRE. Le fichier de
     champs l'accepte depuis toujours (`custom-field.ts`) et le socle sait le
     filtrer ; le formulaire, lui, n'avait aucune nature pour lui. Une app qui
     en portait un voyait donc son champ retomber en `text` a la premiere
     synchronisation, c'est-a-dire perdre ses valeurs multiples. */
  'liste de choix multiple': 'multiselect',
  /* UN CHAMP LIE EST UN `lien` DU SOCLE : l'id d'une fiche d'une autre famille,
     que l'ecran lit par l'annuaire et ouvre par le shell, SANS porte a monter
     dans l'app (`reference` dans `custom-field.ts`). La forme ACCENTUEE d'abord :
     c'est elle que `NATURES` propose, donc celle que la reprise doit rendre
     quand elle relit la table a l'envers (`natureDuType`). Sa cible se resout
     dans `cibleDeChampLie` ; sans cible resolue, le champ reste ECARTE. */
  'champ lié': 'lien',
  'champ lie': 'lien',
  /* UNE PERSONNE DE L'ORGANISATION, choisie par son nom dans l'annuaire du
     socle (`personne` dans `custom-field.ts`) : ni texte libre, ni famille a
     viser. La forme longue d'abord, c'est celle que `NATURES` propose. */
  [NATURE_PERSONNE]: 'personne',
  personne: 'personne',
  /* LA LISTE DE TEXTES N'AVAIT PAS DE TYPE, et le champ etait ecarte ; elle
     est devenue les ETIQUETTES LIBRES du socle (une liste de chaines, 27/10/2026). */
  'liste de textes': 'etiquettes',
  /* LES TYPES DU 27/10/2026 SANS EQUIVALENT D'AVANT, APRES ceux d'avant : la
     reprise relit cette table a l'envers et prend la premiere nature qui
     produit un type. Ceux qui doublaient l'existant s'y sont FONDUS (regle du
     fondateur, `docs/socle/champs-types.md`, section 1) : un montant est un
     `prix` qui porte sa devise, une jauge l'affichage d'un `pourcentage`, des
     liens multiples un `champ lié` coche « Plusieurs fiches ». Leurs reglages
     s'ecrivent par `reglagesDeclares` (`reglages-de-champ.mjs`). */
  note: 'note',
  'période': 'periode',
  'liste de contrôle': 'checklist',
  calcul: 'calcul',
  'numéro automatique': 'numero',
};

/**
 * VRAI QUAND LE SOCLE POSE LA VALEUR DE CE CHAMP DU BRIEF (un calcul, un numero
 * automatique : `estAutomatique` du catalogue) : elle ne se saisit jamais. Le
 * Studio ne lui offre donc ni case « Modifiable » ni colonne modifiable, et le
 * prompt le dit en lecture seule. Lecture par `hasOwn` : une nature lue dans un
 * brief peut valoir `constructor`.
 *
 * @param {any} champ un champ du brief @returns {boolean}
 */
export function estChampAutomatique(champ) {
  const nature = champ && typeof champ.nature === 'string' ? champ.nature : '';
  return Object.hasOwn(TYPE_PAR_NATURE, nature) && estAutomatique(TYPE_PAR_NATURE[nature]);
}

/**
 * LA RAISON MONTREE A LA PLACE DE LA CASE « Modifiable » d'un champ automatique,
 * en un mot et une phrase, ou `null` pour un champ qui se saisit.
 *
 * @param {any} champ un champ du brief @returns {{ mot: string, raison: string }|null}
 */
export function raisonDeLectureSeule(champ) {
  if (!estChampAutomatique(champ)) return null;
  return TYPE_PAR_NATURE[champ.nature] === 'calcul'
    ? { mot: 'calculé', raison: 'un calcul se recalcule à partir de sa formule, il ne se saisit pas' }
    : { mot: 'attribué', raison: 'un numéro automatique est attribué à la création, il ne se saisit pas' };
}

/**
 * L'ANNUAIRE DES PERSONNES DE L'ORGANISATION, seule cible d'un champ `personne`
 * (`FAMILLE_PERSONNES`, `packages/data-model/src/personne.ts` ; recopie, ce
 * module est sans dependance).
 */
export const FAMILLE_PERSONNES = 'personnes';

/**
 * LES JETONS DU SOCLE qu'une valeur de depart nomme au lieu d'une valeur fixe
 * (`JETONS_DU_SOCLE`, `app-screens.ts` ; recopie, le schema fait foi) : la
 * personne qui ouvre la fiche, le jour d'alors (date pure, fuseau de l'org),
 * l'instant d'alors. Ils s'ecrivent tels quels dans `creation.defauts`.
 */
export const JETON_PERSONNE_CONNECTEE = 'socle:personneConnectee';
export const JETON_AUJOURDHUI = 'socle:aujourdhui';
export const JETON_MAINTENANT = 'socle:maintenant';

/**
 * LE JETON QU'UN CHAMP PEUT PRENDRE COMME VALEUR DE DEPART, et son libelle, ou
 * `null` quand il n'en a pas : la nature du champ dit lequel.
 *
 * UN CHAMP NATIF DE NATURE DATE PREND `maintenant`, jamais `aujourdhui` : un
 * natif de la famille range un INSTANT (`habillageDeNatif`), et le controle des
 * apps refuse un jour sur un instant. Seul un champ DECLARE de nature `date`
 * est un jour pur.
 * @param {any} champ @returns {{ jeton: string, libelle: string }|null}
 */
export function jetonDeDepart(champ) {
  const nature = String(champ?.nature ?? '');
  if (nature === NATURE_PERSONNE) return { jeton: JETON_PERSONNE_CONNECTEE, libelle: 'La personne connectée' };
  const natif = natifDuChamp(champ) !== '';
  if (nature === 'date et heure' || (natif && nature === 'date')) return { jeton: JETON_MAINTENANT, libelle: 'Maintenant' };
  if (nature === 'date') return { jeton: JETON_AUJOURDHUI, libelle: "Aujourd'hui" };
  return null;
}

/**
 * CE QUI NOMME UNE PERSONNE A LA PLACE DE SON ID : le nom ou l'e-mail, les deux
 * seuls champs lisibles de l'annuaire. Vide = la convention du socle (le nom,
 * sinon l'e-mail).
 */
export const TITRES_DE_PERSONNE = ['socle:displayName', 'socle:email'];

/**
 * LA REFERENCE D'UN CHAMP `personne`, ou `null` : elle n'existe que pour dire
 * ce qui le nomme (`nomPersonne` du brief), la famille etant fixe. Sans choix,
 * rien ne s'ecrit et le socle suit sa convention.
 * @param {any} champ @returns {{ family: string, titre: string }|null}
 */
export function referenceDePersonne(champ) {
  const titre = String(champ?.nomPersonne ?? '').trim();
  return TITRES_DE_PERSONNE.includes(titre) ? { family: FAMILLE_PERSONNES, titre } : null;
}

/** La forme stockee d'une cible qui nomme une famille : `famille:<id>[/<type>]`. */
export const PREFIXE_CIBLE_FAMILLE = 'famille:';

/**
 * LA FAMILLE QU'UN CHAMP LIE DESIGNE, ou `null` quand le brief ne le dit pas.
 *
 * Le formulaire stocke la cible sous QUATRE formes, et c'est ici, une seule
 * fois, qu'elles se lisent :
 *  - `porte:<uid>` : une porte de CETTE app ; sa famille et son type sont ceux
 *    de son jeu, tels que `jeuDePorte` les ecrit. Un jeu dont la famille reste a
 *    decider ne designe rien : la reference attendra la decision.
 *  - `famille:<id>[/<type>]` : une famille du socle nommee, avec ou sans type
 *    local. C'est la forme que le selecteur ecrit depuis ce lot, parce qu'elle
 *    survit au changement de la liste des apps installees.
 *  - `jeu:<index>` : l'ancienne forme, un index dans la table `JEUX` figee.
 *    Elle se relit contre CETTE table, jamais contre la liste vivante : la meme
 *    position n'y designe pas le meme jeu. Le type ne s'ecrit que si le jeu n'en
 *    a qu'un : une reference vers `contacts` a deux types n'a pas a choisir.
 *  - `libre` : le texte du fondateur, lu comme `famille[/type]` quand il nomme
 *    une famille du socle ; sinon rien, et le BRIEF.md continue de le porter
 *    en toutes lettres pour l'agent.
 *
 * EXPORTEE parce que le prompt, la reprise et l'onglet « Liens » citent la meme
 * cible : trois lectures seraient trois faits qui divergeront.
 *
 * @param {any} champ @param {any} brief @param {any[]} [jeuxFiges]
 * @returns {{ family: string, type?: string } | null}
 */
export function cibleDeChampLie(champ, brief, jeuxFiges = JEUX) {
  const reference = cibleDeChampLieBrute(champ, brief, jeuxFiges);
  if (reference === null) return null;
  const titre = String(champ.titreReference ?? '').trim();
  return titre === '' ? reference : { ...reference, titre };
}

export function champsDeSourceBrief(source) {
  if (!source || typeof source !== 'object') return [];
  return [...(source.champ ? [source.champ] : []),
    ...(source.champs ?? []).flatMap(s => typeof s === 'string' ? [s] : champsDeSourceBrief(s)),
    ...(source.replis ?? []).flatMap(champsDeSourceBrief)];
}

function cibleDeChampLieBrute(champ, brief, jeuxFiges = JEUX) {
  const cible = String(champ?.cible ?? '').trim();
  if (cible === '') return null;
  if (cible.startsWith('porte:')) {
    const portes = Array.isArray(brief?.portes) ? brief.portes : [];
    const porte = portes.find((p) => String(p?.uid ?? '') === cible.slice('porte:'.length));
    if (porte === undefined) return null;
    const jeu = jeuDePorte(porte, brief);
    if (jeu === null || estFamilleADecider(jeu.family)) return null;
    return jeu.type === undefined ? { family: jeu.family } : { family: jeu.family, type: jeu.type };
  }
  if (cible.startsWith(PREFIXE_CIBLE_FAMILLE)) {
    return referenceLue(cible.slice(PREFIXE_CIBLE_FAMILLE.length));
  }
  if (cible.startsWith('jeu:')) {
    const jeu = jeuxFiges[Number(cible.slice('jeu:'.length))];
    if (!jeu || !familleConnue(jeu.famille)) return null;
    const types = Array.isArray(jeu.types) ? jeu.types : [];
    return types.length === 1 ? { family: jeu.famille, type: String(types[0]) } : { family: jeu.famille };
  }
  if (cible === 'libre') return referenceLue(String(champ?.cibleTxt ?? ''));
  return null;
}

/** `famille[/type]`, ou `null` si la famille n'est pas une famille du socle. */
function referenceLue(texte) {
  const [famille = '', type = ''] = texte.trim().split(/\s*\/\s*/, 2);
  if (!familleConnue(famille)) return null;
  const typeLocal = type.trim();
  return typeLocal === '' ? { family: famille } : { family: famille, type: typeLocal };
}

function familleConnue(famille) {
  return FAMILLES.some((f) => f.id === famille);
}

/**
 * LA CIBLE A ECRIRE DANS LE BRIEF pour une reference lue d'un fichier : c'est la
 * lecture INVERSE de `cibleDeChampLie`, et la reprise en a besoin pour qu'une
 * app finie a la main revienne dans le formulaire avec sa cible en place.
 *
 * Une porte de l'app qui montre exactement cette famille et ce type se
 * designe comme porte (`porte:<uid>`), c'est la forme la plus parlante pour le
 * fondateur ; sinon la famille se nomme (`famille:<id>[/<type>]`).
 *
 * @param {{ family: string, type?: string }} reference @param {any} brief @returns {string}
 */
export function cibleDeReference(reference, brief) {
  const family = String(reference?.family ?? '').trim();
  const type = String(reference?.type ?? '').trim();
  for (const porte of Array.isArray(brief?.portes) ? brief.portes : []) {
    const jeu = jeuDePorte(porte, brief);
    if (jeu !== null && jeu.family === family && (jeu.type ?? '') === type) return 'porte:' + String(porte.uid ?? '');
  }
  return PREFIXE_CIBLE_FAMILLE + family + (type === '' ? '' : '/' + type);
}

/**
 * LE BRIEF SANS CETTE PORTE, et tout ce qui la DESIGNAIT remis d'aplomb.
 *
 * POURQUOI UN FILTRE NE SUFFIT PAS. Retirer une porte du tableau laissait trois
 * choses la designer encore par son uid, et chacune cassait a sa facon :
 *  - un CHAMP LIE d'un autre jeu qui la visait (`porte:<uid>`) ne se resolvait
 *    plus, donc le fichier de champs ne l'ecrivait plus : la reference
 *    disparaissait en silence alors qu'elle vise une FAMILLE, pas une porte.
 *    Elle est REECRITE en `famille:<id>[/<type>]`, ce que la porte montrait ;
 *    si la porte n'avait pas de famille decidee, la cible se vide et le verrou
 *    « on ne dit pas vers quoi » le dit au fondateur ;
 *  - un LIEN d'une porte restante vers la porte retiree : `ficheEcransDe` le
 *    tait, mais une CARTE LIEE ou un TOTAL qui le citait s'ecrivait quand meme
 *    avec l'id d'un lien absent, et le schema refusait la fiche. Le lien, les
 *    cartes et les totaux qui le citent partent avec la porte.
 *  - le JEU de donnees, lui, RESTE : plusieurs portes peuvent le montrer, et
 *    la section « Jeux de donnees » dit « Lu sans porte » avec son propre
 *    bouton de suppression, et `ficheChampsDe` continue de l'ecrire dans le
 *    fichier de champs (`docs/socle/studio-jeu-sans-porte.md`). C'est le choix
 *    de la version 2 du modele.
 *  - ses VUES CANVAS DE FICHE restent aussi (audit I2) : une vue de fiche se
 *    declare PAR JEU, la porte qui la portait dans le brief n'en etait que le
 *    rangement. Elles passent a la porte CLASSIQUE suivante du meme jeu (la
 *    premiere apres elle, sinon la premiere avant), et sans porte classique de
 *    ce jeu, au niveau de l'ecran (`vuesFicheSansPorte`), ecrites telles que
 *    la derivation les ecrivait : la vue, son code et son accord G23 survivent.
 *    Ses vues de COLLECTION, elles, partent avec la porte qui les montait.
 *
 * Le catalogue de tri de l'ecran, lui, se derive des portes restantes : c'est
 * au moteur de synchronisation de ne pas garder les options orphelines du
 * disque (`catalogueEnPlace`, tools/studio-dev/moteur-app.mjs).
 *
 * @param {any} brief @param {string} uidPorte @returns {any} un NOUVEAU brief, rien n'est mute
 */
export function briefSansPorte(brief, uidPorte) {
  const portes = Array.isArray(brief?.portes) ? brief.portes : [];
  if (!portes.some((p) => p !== null && typeof p === 'object' && p.uid === uidPorte)) return brief;
  const cibleRetiree = 'porte:' + uidPorte;

  const jeux = (Array.isArray(brief.jeux) ? brief.jeux : []).map((jeu) => {
    const champs = Array.isArray(jeu?.champs) ? jeu.champs : [];
    if (!champs.some((c) => c && c.cible === cibleRetiree)) return jeu;
    return {
      ...jeu,
      champs: champs.map((c) => {
        if (!c || c.cible !== cibleRetiree) return c;
        const reference = cibleDeChampLie(c, brief);
        return reference === null
          ? { ...c, cible: '', cibleTxt: '' }
          : { ...c, cible: PREFIXE_CIBLE_FAMILLE + reference.family + (reference.type === undefined ? '' : '/' + reference.type), cibleTxt: '' };
      }),
    };
  });

  const restantes = portes.filter((p) => !(p !== null && typeof p === 'object' && p.uid === uidPorte)).map((porte) => {
    const liens = Array.isArray(porte?.liens) ? porte.liens : [];
    const perdus = new Set(liens.filter((l) => l && l.porte === uidPorte).map((l) => l.uid));
    /* LE CLIC QUI OUVRAIT LA PORTE RETIREE (`{ porte }` de cette app) revient a
       la fiche, comme celui d'un lien perdu. */
    if (perdus.size === 0) {
      return porte?.auClic && porte.auClic.porte === uidPorte && !porte.auClic.app ? { ...porte, auClic: null } : porte;
    }
    const sortie = { ...porte, liens: liens.filter((l) => !(l && perdus.has(l.uid))) };
    /* LE CLIC QUI ENTRAIT PAR UN LIEN PERDU revient a la fiche. */
    if (porte.auClic && (perdus.has(porte.auClic.lien) || (porte.auClic.porte === uidPorte && !porte.auClic.app))) sortie.auClic = null;
    const fiche = porte.vb && porte.vb['core.fiche'];
    if (fiche && Array.isArray(fiche.cards)) {
      const cards = fiche.cards
        .filter((carte) => !(carte && perdus.has(carte.liee)))
        .map((carte) => {
          const totaux = Array.isArray(carte?.totaux) ? carte.totaux : [];
          const partis = new Set(totaux.filter((t) => t && perdus.has(t.liee)).map((t) => t.uid));
          if (partis.size === 0) return carte;
          return {
            ...carte,
            totaux: totaux.filter((t) => !(t && partis.has(t.uid))),
            keys: Array.isArray(carte.keys) ? carte.keys.filter((k) => !partis.has(k)) : carte.keys,
          };
        });
      sortie.vb = { ...porte.vb, 'core.fiche': { ...fiche, cards } };
    }
    return sortie;
  });

  /* LES VUES CANVAS DE FICHE DE LA PORTE RETIREE, rattachees (voir plus haut). */
  const rang = portes.findIndex((p) => p !== null && typeof p === 'object' && p.uid === uidPorte);
  const retiree = portes[rang];
  const vuesDeFiche = vuesCanvasDuBrief({ ...brief, portes: [retiree] }).filter((v) => v.niveau === 'record' && v.jeu !== null);
  let vuesFicheSansPorte = Array.isArray(brief.vuesFicheSansPorte) ? brief.vuesFicheSansPorte : [];
  if (vuesDeFiche.length > 0) {
    const ids = new Set(vuesDeFiche.map((v) => v.id));
    const classique = (p) => p !== null && typeof p === 'object' && p.custom !== true && p.dsRef === retiree.dsRef;
    const apres = portes.slice(rang + 1).find(classique);
    const receveuse = apres ?? portes.slice(0, rang).find(classique);
    const gardees = (Array.isArray(retiree.vuesCustom) ? retiree.vuesCustom : []).filter(
      (v) => v !== null && typeof v === 'object' && niveauDeVueCustom(v) === 'record' && ids.has(idDeVueCustom(brief, v)),
    );
    if (receveuse !== undefined) {
      const i = restantes.findIndex((p) => p !== null && typeof p === 'object' && p.uid === receveuse.uid);
      const recue = restantes[i];
      /* TELLE QUELLE, uid et id compris : l'id d'une vue se lit sur son nom
         dans l'app (`idDeVueCustom`), pas sur sa porte, donc il ne bouge pas. */
      const deplacees = gardees.map((v) => structuredClone(v));
      restantes[i] = {
        ...recue,
        customVues: true,
        vuesCustom: [...(Array.isArray(recue.vuesCustom) ? recue.vuesCustom : []), ...deplacees],
      };
    } else {
      const connues = new Set(vuesFicheSansPorte.map((v) => (v !== null && typeof v === 'object' ? v.id : '')));
      vuesFicheSansPorte = [
        ...vuesFicheSansPorte,
        ...vuesDeFiche.filter((v) => !connues.has(v.id)).map((v) => ({ id: v.id, label: v.label, niveau: 'record', jeu: v.jeu })),
      ];
    }
  }

  return { ...brief, jeux, portes: restantes, ...(vuesFicheSansPorte === brief.vuesFicheSansPorte || vuesDeFiche.length === 0 ? {} : { vuesFicheSansPorte }) };
}

/**
 * LA PORTE PAR DEFAUT D'UNE APP QUI N'EN DECLARE AUCUNE.
 *
 * Ce n'est pas une invention de ce lot : le scaffold l'ecrivait deja en
 * litteral dans l'ecran (« sans porte declaree, une seule par defaut : un ecran
 * sans porte n'a pas de menu »). Elle DEMENAGE ici, dans la declaration, parce
 * que c'est desormais la declaration que l'ecran monte. Ecrite aux deux
 * endroits, elle aurait fini par ne plus dire la meme chose.
 */
const PORTE_PAR_DEFAUT = {
  id: 'tous',
  label: 'Tous',
  vues: ['core.liste', 'core.fiche'],
  arrivee: 'core.liste',
  /* AUCUN JEU : elle n'en designe pas, et c'est ce qui la rend honnete. Le
     fichier de champs naitra donc sur le trou nomme `@a-decider/tous`, ce qui
     est exactement ce qu'elle produisait deja avec son sac `ds` vide. */
  dsRef: '',
};

/**
 * LES PORTES AVEC LESQUELLES UNE APP NAIT.
 *
 * Une porte n'entre dans les deux fichiers que si elle a un id ET un libelle :
 * ce sont les deux seules choses qu'un brief dise d'une porte et qu'un ecran
 * sache utiliser sans decision supplementaire. Le moteur appliquait DEJA ce
 * filtre pour le scaffold ; le partager est ce qui garantit que le fichier
 * d'ecrans, le fichier de champs et l'ecran parlent des MEMES portes.
 *
 * Aucune porte declarable (y compris : aucun brief du tout, cas du Launcher en
 * ligne de commande) rend la porte par defaut, jamais un tableau vide : les
 * deux fichiers de declaration s'ecrivent TOUJOURS, et le schema refuse un
 * ecran sans porte.
 *
 * @param {any} brief @returns {any[]}
 */
export function portesDeNaissance(brief) {
  const declarables = (Array.isArray(brief?.portes) ? brief.portes : []).filter(
    (p) => String(p?.id ?? '').trim() !== '' && String(p?.label ?? '').trim() !== '',
  );
  return declarables.length > 0 ? declarables : [PORTE_PAR_DEFAUT];
}

/**
 * LE JEU D'UNE PORTE, tel que les DEUX fichiers de declaration le designent.
 *
 * Rend `{ clef, family, type? }` ou `null` quand la porte n'a pas de jeu du
 * tout. `clef` est ce sur quoi les jeux se dedupliquent : deux portes sur le
 * MEME jeu partagent ses champs, et les ecrire deux fois serait deux faits qui
 * divergeront.
 *
 * QUAND LA FAMILLE EST DELEGUEE, c'est un TROU NOMME, pas une absence : le
 * fichier s'ecrit quand meme, et le Studio propose de le remplir. Le NOM DU JEU
 * entre dans le trou parce que c'est le seul discriminant qui n'invente rien :
 * sans lui, deux jeux delegues fusionneraient en un seul et melangeraient leurs
 * champs.
 *
 * DELEGUEE VEUT DIRE `auto.famille` LEVE SUR LE JEU, PAS SEULEMENT LE CHAMP VIDE, et
 * c'etait la derniere lecture divergente du meme fait. `chargementDe` (plus
 * haut) rend deja « a ecrire par l'agent » des que le point est confie, et
 * `prompt.mjs` ne transmet plus la saisie d'un champ confie : ici seul le vide
 * comptait, donc une famille tapee PUIS confiee entrait quand meme dans
 * `taki.fields.json` comme une decision prise. Le script construisait alors sur
 * une valeur que le fondateur venait justement de laisser a l'agent, en
 * silence. Le trou nomme est la seule reponse honnete : il DIT qu'il reste a
 * decider, et `scripts/check-app-entities.mjs` le compte.
 *
 * EXPORTEE parce que `validate.mjs` doit dire ce que le SCRIPT ecrira sans le
 * recalculer a cote : une famille confiee a l'agent passe la validation de porte
 * et sort pourtant en `@a-decider` dans le fichier. La recopier la-bas aurait
 * fait deux lectures du meme fait.
 *
 * @param {any} porte @param {any} brief @returns {{clef: string, family: string, type?: string}|null}
 */
export function jeuDePorte(porte, brief) {
  return identiteDeJeu(jeuDe(porte, brief));
}

/**
 * L'IDENTITE D'UN JEU DU BRIEF, `{ clef, family, type? }`, ou `null` quand il n'y
 * a pas de jeu. C'est le corps de `jeuDePorte`, sorti pour que le jeu qu'AUCUNE
 * porte ne montre se lise EXACTEMENT comme celui d'une porte : famille confiee
 * a l'agent -> trou nomme, type confie -> pas de type, meme clef de
 * deduplication. Deux lectures du meme fait auraient fini par diverger.
 *
 * @param {any} jeu @returns {{clef: string, family: string, type?: string}|null}
 */
export function identiteDeJeu(jeu) {
  if (jeu === null || jeu === undefined) return null;
  const famille = estDelegue(jeu, 'famille') ? '' : String(jeu.famille || '').trim();
  if (famille === '') {
    /* LE TROU PORTE L'IDENTITE DU JEU, plus celle d'une porte : c'est tout le
       lot. Il portait l'id de la porte detentrice, avec la gymnastique qu'il
       fallait pour que deux portes sur le meme jeu ne se retrouvent pas sur
       deux trous distincts ; un jeu a maintenant un nom a lui, donc le trou se
       nomme tout seul et se partage sans rien a rattraper. L'uid ne sert que
       pour un jeu qui n'a pas encore de nom : c'est le seul discriminant qui
       n'invente rien. */
    const proprietaire = kebab(String(jeu.nom || '').trim()) || String(jeu.uid || '').trim();
    const family = familleADeciderDe(proprietaire);
    return { clef: family, family };
  }
  /* Le type local suit la MEME regle que la famille : confie, il ne s'ecrit pas.
     Il n'a pas de trou nomme a lui parce qu'il est facultatif dans le fichier,
     donc son absence dit deja « pas encore decide ». */
  const type = estDelegue(jeu, 'type') ? '' : String(jeu.type || '').trim();
  return type === ''
    ? { clef: `${famille}::`, family: famille }
    : { clef: `${famille}::${type}`, family: famille, type };
}

/**
 * LES CHAMPS D'UN JEU, traduits du brief vers la forme du fichier.
 *
 * Un champ dont la NATURE n'a pas d'equivalent (une nature inconnue), ou un champ
 * lie dont la cible ne nomme aucune famille du socle, est ECARTE, et ce n'est
 * pas un trou : c'est une traduction impossible, que le BRIEF.md continue de
 * porter en toutes lettres.
 *
 * EXPORTEE pour la meme raison que `jeuDePorte` : c'est ce COMPTE-LA, celui des
 * champs traduits, qui dit si le fichier naitra sur un jeu vide. Le compte des
 * champs du brief serait un autre nombre.
 *
 * LE BRIEF EST LA parce qu'un champ lie peut viser UNE PORTE DE L'APP
 * (`porte:<uid>`), et que seul le brief sait quel jeu cette porte montre. Sans
 * lui, un tel champ ne se traduit pas : l'appelant qui l'oublie verrait un
 * champ de moins que le fichier, en silence. Tous les appelants le passent.
 *
 * @param {any} jeu un JEU de `brief.jeux` (ou tout objet a `champs`)
 * @param {any} [brief] le brief qui porte ce jeu
 * @returns {any[]}
 */
export function champsDeclaresDeJeu(jeu, brief) {
  return traduireChamps(jeu, brief)
    .filter((x) => x.declare !== null)
    .map((x) => x.declare);
}

/**
 * LE NATIF NUMERIQUE QU'UNE FORMULE PEUT CITER, ou rien : la colonne native du
 * jeu (`socle:basePrice`) dont la nature est un nombre, sous le libelle que le
 * brief lui donne (« Prix HT »), le meme que montre sa colonne. Le fichier de
 * champs ne le porte pas (il vit sur la famille) : la formule le cite par sa
 * clef `{socle:basePrice}`, et le schema juge qu'il existe et qu'il est un
 * nombre (`champsAvecNatifs`, `data-model/src/formule.ts`).
 *
 * @param {{ nom: string, natif: any }} x une entree de `traduireChamps`
 * @returns {{ key: string, label: string, type: string }[]}
 */
function natifCitable(x) {
  return x.natif?.type === 'number' ? [{ key: x.nom, label: String(x.natif.label ?? ''), type: 'number' }] : [];
}

/**
 * LES CHAMPS QU'UNE FORMULE PEUT CITER DANS UN JEU : ses champs declares
 * (`champsDeclaresDeJeu`) puis ses natifs numeriques (`natifCitable`). C'est la
 * liste de la case de formule, de sa verification et de sa lecture dans le
 * prompt ; `champsDeclaresDeJeu` reste celle du fichier de champs.
 *
 * @param {any} jeu @param {any} [brief]
 * @returns {{ key: string, label: string, type: string, formule?: string }[]}
 */
export function champsDeFormuleDeJeu(jeu, brief) {
  return traduireChamps(jeu, brief).flatMap((x) => (x.declare === null ? natifCitable(x) : [x.declare]));
}

/**
 * L'HABILLAGE DE CHAQUE CHAMP DECLARE D'UN JEU, par son uid du brief : sa clef
 * dans le fichier, son type, et l'habillage que sa colonne porterait
 * (`habillageDeChamp`), celui-la meme que la derivation des ecrans ecrit quand
 * une vue le cite. Le Studio d'une org le pose sur le catalogue de colonnes
 * pour un champ qu'aucune vue ne cite encore (`atelier-adapter.ts`).
 *
 * @param {any} jeu @param {any} [brief]
 * @returns {{ uid: string, key: string, type: string, habillage: string }[]}
 */
export function habillagesDeclaresDeJeu(jeu, brief) {
  return traduireChamps(jeu, brief)
    .filter((x) => x.declare !== null)
    .map((x) => ({ uid: x.uid, key: x.declare.key, type: x.declare.type, habillage: x.habillage }));
}

/**
 * LES CHAMPS D'UN JEU QUE LE SCRIPT SAIT EXPLOITER, natifs COMPRIS.
 *
 * IL NE FAUT PAS LA CONFONDRE AVEC `champsDeclaresDeJeu`, ET C'EST TOUT LE
 * DEFAUT QU'ELLE CORRIGE. L'autre rend ce qui part dans `taki.fields.json`,
 * c'est-a-dire les champs PERSONNALISES : un jeu dont TOUS les champs vivent
 * deja sur la famille du socle (`contacts` + `name`, `email`, `phone`) n'a rien
 * a y ecrire, et son compte est legitimement NUL. `trousDeDeclaration` lisait ce
 * zero comme « ce jeu n'a aucun champ » et refusait de construire un formulaire
 * pourtant complet, sans que rien ne l'affiche.
 *
 * CE COMPTE-CI EST CELUI DE LA QUESTION « le script a-t-il de quoi ecrire un
 * ecran ». `traduireChamps` a deja ECARTE tout ce qui ne se projette pas (nature
 * sans equivalent, catalogue de choix vide, clef en double) : ce qui reste est,
 * par construction, nomme, type, et citable par un binding. Un jeu REELLEMENT
 * vide rend donc toujours zero, et bloque toujours.
 *
 * @param {any} jeu @param {any} [brief]
 * @returns {{ uid: string, nom: string, natif: any, declare: any }[]}
 */
export function champsExploitablesDeJeu(jeu, brief) {
  return traduireChamps(jeu, brief).map((x) => ({
    uid: x.uid,
    nom: x.nom,
    natif: x.natif,
    declare: x.declare,
  }));
}

/** Un natif est une propriete du schema de CETTE famille, pas un nom libre. */
export function natifConnu(jeu, clef) {
  return (NATIFS_PAR_FAMILLE[String(jeu?.famille ?? '')] ?? []).includes(clef);
}

/**
 * LA MEME TRADUCTION, avec l'UID DU BRIEF a cote de chaque champ traduit.
 *
 * Le brief designe un champ par son `uid` (un jeton opaque, stable au
 * glisser-deposer) ; les deux fichiers de declaration le designent par sa
 * `key`. Le detail par vue, les colonnes, les cartes de fiche et les stats ont
 * tous besoin de passer de l'un a l'autre, et ils doivent le faire SUR LA MEME
 * LISTE que celle qui part dans `taki.fields.json` : un champ ECARTE de la
 * traduction (nature sans equivalent, catalogue vide, clef en double) ne doit
 * pas se retrouver cite par un role qui, lui, aurait survecu.
 *
 * @param {any} jeu @param {any} [brief] @returns {{ uid: string, source: any, declare: any }[]}
 */
function traduireChamps(jeu, brief) {
  const champs = [];
  const clefsVues = new Set();
  for (const champ of Array.isArray(jeu?.champs) ? jeu.champs : []) {
    const typeChamp = TYPE_PAR_NATURE[champ?.nature];
    if (!typeChamp) continue;
    /* UN CHAMP LIE SANS FAMILLE RESOLUE EST ECARTE, comme une liste de choix
       sans valeurs : un `lien` sans `reference`, le schema le refuse, et un
       lien vers on ne sait quoi n'afficherait qu'un id. */
    /* Un lien « Plusieurs fiches » vise sa cible exactement comme un lien
       simple : meme choix au formulaire, meme reference exigee par le schema. */
    const vise = typeChamp === 'lien';
    const reference = vise ? cibleDeChampLie(champ, brief)
      : typeChamp === 'personne' ? referenceDePersonne(champ)
      : null;
    if (vise && reference === null) continue;
    /*
     * UN CHAMP NATIF NE S'ECRIT PAS DANS `taki.fields.json` : il vit deja sur la
     * famille du socle (`name`, `reference`, `basePrice`, `startAt`). Il a
     * pourtant un NOM que les vues citent (`socle:<clef>`), et c'est tout ce
     * qu'on lui demande : sans lui, un titre pose sur le nom de la famille
     * n'avait aucun mot dans le brief, et la reprise d'une app finie a la main
     * laissait la case vide (« aucun champ ne fait le titre » sur une app qui
     * marche).
     */
    const natif = natifDuChamp(champ);
    if (natif !== '') {
      if (!natifConnu(jeu, natif)) continue;
      const nom = PREFIXE_SOCLE + natif;
      if (clefsVues.has(nom)) continue;
      clefsVues.add(nom);
      /* L'EXEMPLE D'UN CHAMP NATIF VIT SUR SA COLONNE DU CATALOGUE, et nulle
         part ailleurs : `taki.fields.json` ne connait pas les champs de la
         famille, donc c'est l'entree de catalogue de l'ecran qui porte son
         placeholder (`colonneDeCatalogue`). */
      const natifDeclare = { champ: nom, label: String(champ.nom ?? '').trim(), type: typeChamp };
      /* LE CARACTERE REQUIS D'UN NATIF VIT SUR SA COLONNE (`requis`), jamais
         dans `taki.fields.json` qui ne le connait pas : c'est ce que l'ecran
         generique lit pour refuser qu'on vide un titre, et pour reclamer la
         case sur la fiche de creation. */
      if (champ.requis === true) natifDeclare.requis = true;
      /* LA FAMILLE QU'UN NATIF `lien` DESIGNE (`socle:contactId` -> contacts)
         vit sur sa colonne du catalogue, seule a pouvoir la porter. */
      if (reference !== null) natifDeclare.reference = reference;
      /* SES DECIMALES (`socle:basePrice` en euros, « 12,00 € ») : un natif n'a
         aucune ligne dans `taki.fields.json`, sa colonne les porte, et elle
         seule, pour un nombre. */
      if (typeChamp === 'number') {
        const { decimales } = reglagesDeclares(champ, typeChamp, habillageDeNatif(champ));
        if (decimales !== undefined) natifDeclare.decimales = decimales;
      }
      const exempleNatif = String(champ.exemple ?? '').trim();
      if (exempleNatif !== '') natifDeclare.placeholder = exempleNatif;
      /* ET SON CATALOGUE DE VALEURS, POUR LA MEME RAISON QUE SON LIBELLE : un
         `socle:status` a des valeurs, et rien d'autre que cette colonne ne peut
         les porter. Sans elles, sa pastille restait eteinte et une stat
         « combien valent X » ne se projetait pas du tout. */
      if (typeChamp === 'select' || typeChamp === 'multiselect') {
        const valeursNatif = valeursDeChamp(champ);
        if (valeursNatif.length === 0) continue;
        natifDeclare.options = valeursNatif.map((label) => {
          const option = { value: valeurStockee(champ, label), label };
        if (champ.options?.find(o => o.value === option.value)?.archived) option.archived = true;
          const ton = tonDeValeur(champ, label);
          if (ton !== '') option.tone = ton;
          return option;
        });
      }
      champs.push({
        uid: String(champ?.uid ?? ''),
        source: champ,
        nom,
        /*
         * L'HABILLAGE D'UN NATIF NE SUIT PAS LA MEME TABLE QU'UN CHAMP DECLARE.
         * `datePure` lit une chaine `YYYY-MM-DD` : c'est la forme d'un champ
         * DECLARE de nature date (doctrine des dates). Un natif de la famille,
         * lui, range ce qu'il veut, et `startAt`, `performedAt` ou `nextDueAt`
         * sont des INSTANTS : l'habiller en date pure rendrait le vide. Seule
         * une nature qui ne laisse aucun doute en impose un (`habillageDeNatif`).
         */
        habillage: habillageDeNatif(champ),
        natif: natifDeclare,
        declare: null,
      });
      continue;
    }
    /* LA CLEF FIGEE d'abord (voir `clefDuChamp`) : renommer ne change pas le stockage. */
    const key = clefDuChamp(champ);
    if (key === '' || clefsVues.has(key)) continue;
    clefsVues.add(key);

    const declare = { key, label: String(champ.nom).trim(), type: typeChamp };
    /* UN CALCUL OU UN NUMERO N'EST JAMAIS OBLIGATOIRE (le schema le refuse) :
       un brief d'avant qui le cochait n'ecrit pas la case. */
    if (champ.requis === true && !estAutomatique(typeChamp)) declare.required = true;
    if (reference !== null) declare.reference = reference;
    /* LES REGLAGES DU TYPE (unite, devise, decimales, affichage, liens
       multiples, note maximale, sequence), seulement ceux que le catalogue lui
       reconnait, et seulement quand ils different du defaut du socle, sauf la
       devise d'un prix, qui s'ecrit toujours (EUR compris, `reglagesDeclares`).
       La formule d'un calcul attend la fin de la boucle : elle se lit contre
       TOUS les champs du jeu. */
    Object.assign(declare, reglagesDeclares(champ, typeChamp, habillageDeChamp(champ)));
    /* L'EXEMPLE EST LE PLACEHOLDER DE LA CASE VIDE, et il compte depuis que la
       CREATION est une fiche vide : sans lui, une fiche neuve n'est qu'une
       colonne de « Non renseigne » et rien ne dit ce qu'on attend. Vide, la
       clef ne s'ecrit pas : le socle a deja un defaut par nature. */
    const exemple = String(champ.exemple ?? '').trim();
    if (exemple !== '') declare.placeholder = exemple;
    if (typeChamp === 'select' || typeChamp === 'multiselect') {
      /* Les valeurs sont saisies separees par des virgules dans le formulaire :
         c'est la forme que le brief a toujours eue, et `valeursDeChamp` est le
         seul endroit qui la decoupe. Un catalogue vide rendrait le champ
         invalide, donc le champ est ecarte. */
      const valeurs = valeursDeChamp(champ);
      if (valeurs.length === 0) continue;
      /* LES CHOIX DEPENDANTS : le parent est un autre « liste de choix » declare
         du meme jeu, nomme par son uid dans le brief et par sa clef dans le
         fichier. Un parent disparu ou change de nature fait tomber la
         dependance ; une valeur dont le parent n'existe plus vaut pour tous. */
      const parent = champ.dependDe
        ? (Array.isArray(jeu?.champs) ? jeu.champs : []).find((c) => c?.uid === champ.dependDe && c !== champ
          && TYPE_PAR_NATURE[c?.nature] === 'select' && natifDuChamp(c) === '' && !c.dependDe)
        : undefined;
      const valeursDuParent = parent ? new Set(valeursDeChamp(parent).map((l) => valeurStockee(parent, l))) : null;
      if (parent) declare.dependDe = clefDuChamp(parent);
      declare.options = valeurs.map((label) => {
        const option = { value: valeurStockee(champ, label), label };
        if (champ.options?.find(o => o.value === option.value)?.archived) option.archived = true;
        /* LE TON, quand le fondateur en a pose un : « En retard » en rouge. Le
           silence vaut neutre, donc on ne l'ecrit pas. */
        const ton = tonDeValeur(champ, label);
        if (ton !== '') option.tone = ton;
        const duParent = valeursDuParent ? parentDeValeur(champ, label) : '';
        if (duParent !== '' && valeursDuParent.has(duParent)) option.parent = duParent;
        return option;
      });
    }
    champs.push({
      uid: String(champ?.uid ?? ''),
      source: champ,
      nom: key,
      habillage: habillageDeChamp(champ),
      natif: null,
      declare,
    });
  }
  /*
   * LA FORMULE D'UN CALCUL, LUE CONTRE LE JEU ENTIER : un calcul peut citer un
   * champ declare apres lui. Elle part en forme STOCKEE (`{prix} * 0.25`), ou
   * telle que tapee quand elle ne se lit pas : c'est alors le schema qui refuse
   * le fichier, avec la phrase de `verifierFormule`, au lieu d'un calcul qui
   * disparaitrait en silence (`formuleDeclaree`).
   */
  const declares = champs.flatMap((c) => (c.declare === null ? natifCitable(c) : [c.declare]));
  for (const c of champs) {
    if (c.declare?.type !== 'calcul') continue;
    const formule = formuleDeclaree(c.source?.formule, declares);
    if (formule !== undefined) c.declare.formule = formule;
  }
  return champs;
}

/**
 * LE FICHIER DE CHAMPS D'UNE APP, projete de son brief.
 *
 * Un jeu par SOURCE DE DONNEES, jamais par porte : deux portes sur le meme jeu
 * partagent ses champs, et les ecrire deux fois serait deux faits qui
 * divergeront.
 *
 * IL S'ECRIT TOUJOURS, ET C'EST LA CORRECTION DE CE LOT. Il sautait un jeu des
 * que la famille etait vide, puis des qu'aucun champ n'etait traduisible, et
 * rendait `null` s'il ne restait rien. Consequence mesuree sur l'app `peche` du
 * fondateur : dix champs saisis, une famille deleguee a l'agent, et AUCUN
 * fichier ecrit, donc une app que le Studio ne pouvait pas modifier. Le studio
 * ne sait editer qu'une app NEE DECLAREE : une app sans ce fichier est verrouillee
 * a vie.
 *
 * IL NE REND JAMAIS `null` : une app sans porte declarable naît sur la porte
 * par defaut, et son jeu est un trou nomme.
 *
 * @param {any} brief @returns {any}
 */
export function ficheChampsDe(brief) {
  const jeux = [];
  const vus = new Set();

  const portes = portesDeNaissance(brief);
  for (const porte of portes) {
    const jeu = jeuDePorte(porte, brief);
    if (jeu === null || vus.has(jeu.clef)) continue;
    vus.add(jeu.clef);
    jeux.push(jeuProjete(jeu, jeuDe(porte, brief), brief));
  }

  /* LES JEUX QU'AUCUNE PORTE NE MONTRE, APRES CEUX DES PORTES, dans l'ordre du
     brief. Une app peut LIRE les champs d'une famille dont une autre app est
     proprietaire sans la montrer (la vue Canvas de Consultations lit le sac
     d'`assets/animal`, que Patients tient) ; `pnpm apps:check --strict` exige
     alors que ces clefs soient declarees chez elle, sans porte. Le jeu est
     projete avec les MEMES regles que celui d'une porte (`jeuProjete`) et se
     deduplique sur la meme clef. Deux cas restent comme avant, ignores : la
     famille a decider (rien n'est encore tranche, un trou nomme n'a de sens
     que pour une porte qui l'attend) et le jeu sans aucun champ ni reglage (il
     n'a rien a redire). */
  for (const source of Array.isArray(brief?.jeux) ? brief.jeux : []) {
    const jeu = identiteDeJeu(source);
    if (jeu === null || vus.has(jeu.clef) || estFamilleADecider(jeu.family)) continue;
    const declare = jeuProjete(jeu, source, brief);
    const reglages = Object.keys(declare).filter((k) => !['family', 'type', 'champs'].includes(k));
    if (declare.champs.length === 0 && reglages.length === 0) continue;
    vus.add(jeu.clef);
    jeux.push(declare);
  }

  /* AUCUN JEU : aucune porte n'en designe un, ou toutes designent un jeu
     disparu (`dsRef` sur un uid qui n'existe plus). L'app naît quand meme
     declaree, sur un trou nomme : le schema refuse un fichier a zero jeu, et un
     fichier absent verrouillerait le Studio pour toujours. */
  if (jeux.length === 0) jeux.push({ family: familleADeciderDe(portes[0].id), champs: [] });

  return { version: 1, jeux };
}

/**
 * UN JEU DU FICHIER DE CHAMPS, projete de son jeu du brief : la regle UNIQUE,
 * pour le jeu qu'une porte montre comme pour celui qu'aucune ne montre.
 *
 * @param {{ clef: string, family: string, type?: string }} jeu son identite (`identiteDeJeu`)
 * @param {any} source le jeu du brief @param {any} brief
 * @returns {any}
 */
function jeuProjete(jeu, source, brief) {
  const champs = champsDeclaresDeJeu(source, brief);
  /* L'ordre des clefs est celui qu'un humain lit : d'ou vient le jeu, puis ce
     qu'il porte. `JSON.stringify` respecte l'ordre d'insertion. */
  const declare =
    jeu.type === undefined
      ? { family: jeu.family, champs }
      : { family: jeu.family, type: jeu.type, champs };
  /* LES FICHIERS JOINTS, QUAND LE JEU LES OUVRE, et seulement la : absente,
     la clef dit deja « coupee », c'est le defaut du socle. */
  const fichiers = fichiersProjetes(source);
  if (fichiers !== null) declare.fichiers = fichiers;
  if (!estFamilleADecider(jeu.family) && !estDelegue(source, 'scope') && ['org-wide', 'owner-scoped'].includes(source?.scope)) declare.scope = source.scope;
  /* LE CHATTER SUR LES FICHES (vague du soir, R9) : ecrit seulement quand le
     jeu l'ETEINT ; absent, il est allume, c'est le defaut. */
  if (source?.chatter === false) declare.chatter = false;
  /* LA COULEUR ET LA PHOTO DE REFERENCE DES FICHES (docs/socle/couleur-photo-fiche.md) :
     ecrites seulement quand le jeu les ALLUME ; absentes, elles sont eteintes. */
  if (source?.couleur === true) declare.couleur = true;
  if (source?.photo === true) declare.photo = true;
  /* LES CHAMPS MASQUES (docs/socle/jeu-listes.md) : seulement ceux que le jeu
     porte encore, jamais un champ obligatoire (le schema le refuserait). */
  const masques = (Array.isArray(source?.masques) ? source.masques : [])
    .filter((clef) => champs.some((champ) => champ.key === clef && champ.required !== true));
  if (masques.length > 0) declare.masques = [...new Set(masques)];
  return declare;
}

/* ========================================================================== */
/* le detail par vue, projete                                                 */
/* ========================================================================== */

/*
 * D'UN COTE DES UID, DE L'AUTRE DES CLEFS, ET UNE SEULE TRADUCTION.
 *
 * Le brief designe un champ par son `uid` (un jeton opaque, stable quand le
 * fondateur reordonne ses champs au glisser-deposer) ou par `std:<clef>` pour
 * les champs que le SDK pose sur tout document. Les deux fichiers de
 * declaration, eux, le designent par sa `key` ou par `socle:<champ natif>`.
 * TOUT ce que ce bloc fait de plus que recopier de la donnee, c'est cette
 * traduction : il n'invente aucun champ, aucun mot, aucune condition.
 *
 * UN UID QUI NE RESOUT PLUS EST ECARTE, jamais devine. Trois facons de ne plus
 * resoudre, et elles arrivent toutes : le champ a ete supprime du formulaire ;
 * sa nature n'a pas d'equivalent dans le fichier de champs (une nature inconnue,
 * ou un `champ lié` sans famille) ; deux champs se reduisent a la meme clef et le second est
 * ecarte de `taki.fields.json`. Dans les trois cas, ecrire le role quand meme
 * ferait pointer le fichier vers une clef que le socle ne trouvera pas, et une
 * colonne introuvable JETTE au montage.
 */

/** Les neuf roles du contrat des vues, dans l'ordre ou ils se lisent. */
export const ROLES_DU_CONTRAT = [
  'title',
  'subtitle',
  'avatar',
  'status',
  'badges',
  'groupKey',
  'lane',
  'dateStart',
  'dateEnd',
];

/**
 * LES JEUX TELS QUE `ficheChampsDe` LES ECRIT, indexes par leur clef.
 *
 * Deux portes qui declarent la MEME famille et le MEME type local partagent un
 * seul jeu dans le fichier, et c'est la PREMIERE qui le remplit. La seconde
 * peut porter d'autres champs : ils ne seront jamais ecrits. Ce recensement est
 * donc ce qui autorise un role a citer un champ, et il suit exactement l'ordre
 * d'iteration de `ficheChampsDe` : le juger autrement laisserait passer une
 * colonne que le socle refuserait au montage.
 *
 * @param {any[]} portes @param {any} brief
 * @returns {Map<string, { clefs: Set<string>, declares: Map<string, any> }>}
 */
function jeuxEcrits(portes, brief) {
  const parClef = new Map();
  for (const porte of portes) {
    const jeu = jeuDePorte(porte, brief);
    if (jeu === null || parClef.has(jeu.clef)) continue;
    const declares = new Map();
    const natifs = new Map();
    const habillages = new Map();
    const noms = [];
    for (const traduit of traduireChamps(jeuDe(porte, brief), brief)) {
      if (traduit.declare !== null) declares.set(traduit.declare.key, traduit.declare);
      else natifs.set(traduit.nom, traduit.natif);
      if (traduit.habillage !== '') habillages.set(traduit.nom, traduit.habillage);
      noms.push(traduit.nom);
    }
    /* `clefs` DIT CE QU'UNE VUE PEUT CITER (les champs declares ET les champs
       natifs du jeu) ; `declares` seul dit ce que `taki.fields.json` porte, et
       c'est lui que la fiche enumere. Confondre les deux mettrait un champ
       natif dans la liste de colonnes de la fiche, ou l'y refuserait. */
    parClef.set(jeu.clef, {
      clefs: new Set(noms),
      noms,
      declares,
      natifs,
      habillages,
    });
  }
  return parClef;
}

/**
 * DE QUOI TRADUIRE LES CHAMPS D'UNE PORTE : ses uid vers des clefs, et le jeu
 * REELLEMENT ECRIT auquel ces clefs doivent appartenir.
 *
 * @param {any} porte @param {any} brief @param {Map<string, any>} ecrits
 * @returns {{ uidVersClef: Map<string, string>, jeu: { clefs: Set<string>, declares: Map<string, any> } }}
 */
function traductionDePorte(porte, brief, ecrits) {
  const uidVersClef = new Map();
  for (const x of traduireChamps(jeuDe(porte, brief), brief)) {
    if (x.uid !== '') uidVersClef.set(x.uid, x.nom);
  }
  const jeu = jeuDePorte(porte, brief);
  const ecrit =
    (jeu !== null && ecrits.get(jeu.clef)) ||
    { clefs: new Set(), noms: [], declares: new Map(), natifs: new Map(), habillages: new Map() };
  return { uidVersClef, jeu: ecrit };
}

/**
 * LE NOM D'UN CHAMP DU BRIEF, dans le vocabulaire des fichiers, ou `null`.
 *
 * `null` veut dire ECARTE, et l'appelant laisse alors tomber le role, la
 * colonne ou la stat entiere. Il ne retombe jamais sur un autre champ : servir
 * un titre pris au hasard serait pire qu'un titre absent, que le socle sait
 * signaler en grisant la vue avec sa raison.
 *
 * @param {any} id @param {{ uidVersClef: Map<string, string>, jeu: any }} t
 * @returns {string|null}
 */
function nomDeChampProjete(id, t) {
  if (typeof id !== 'string' || id.trim() === '') return null;
  if (estChampStd(id)) {
    /* `pick` a 0 = plomberie interne que le formulaire ne propose meme pas
       (`deletedAt`, `schemaVersion`). Un brief qui la porterait vient d'une
       edition a la main. */
    const std = STD.find((x) => x.k === id.slice(4) && x.pick);
    return std ? PREFIXE_SOCLE + std.k : null;
  }
  const clef = t.uidVersClef.get(id);
  return clef !== undefined && t.jeu.clefs.has(clef) ? clef : null;
}

/**
 * L'HABILLAGE QU'UNE NATURE IMPOSE, ou `''`.
 *
 * IL NE SE DEMANDE PAS, ET C'EST TOUT LE POINT. Un prix s'affiche en euros, une
 * date PURE au format du pays, un pourcentage avec son signe : ce ne sont pas
 * des decisions, ce sont les natures elles-memes. Les demander au formulaire
 * aurait fait une case de plus dont la seule bonne reponse etait deja connue,
 * et une app nee du Studio montrait « 18500 » la ou l'app de reference montre
 * « 18 500 € ».
 *
 * `date et heure` N'EN A PAS, et la distinction n'est pas un detail : une date
 * pure est une CHAINE `YYYY-MM-DD` (doctrine des dates, AGENTS.md section 7),
 * un instant est un Timestamp. `datePure` sur un instant rend le vide.
 *
 * Les noms sont ceux de `HABILLAGES_SOCLE` (`packages/ui/src/views/habillages.ts`),
 * que `scripts/check-app-entities.mjs` LIT plutot que de la recopier et
 * confronte a ce que chaque fichier declare : le socle doit savoir rendre ce que
 * la projection ecrit.
 *
 * @param {string} nature @returns {string}
 */
export function habillageDeNature(nature) {
  if (nature === 'prix') return 'euros';
  if (nature === 'pourcentage') return 'pourcentage';
  if (nature === 'date') return 'datePure';
  if (nature === 'durée' || nature === 'duree') return 'duree';
  return '';
}

/**
 * L'HABILLAGE D'UN CHAMP NATIF : celui qu'il NOMME, sinon celui que sa nature
 * impose SANS DOUTE POSSIBLE.
 *
 * DEUX NATURES SEULEMENT, et pas les memes que pour un champ declare. Un natif
 * de nature `date et heure` est un INSTANT (`startAt`, `dueAt`) : `dateHeure`
 * le rend avec son heure, la ou le rendu par defaut la perdait (une saisie
 * d'heures qui affiche « 15/09/2026 » ne dit plus rien). Une `durée` est un
 * nombre de MINUTES (`durationMinutes`), que `duree` ecrit « 1 h 30 ». Une
 * nature `date` sur un natif, elle, reste NUE : le brief ne sait pas si la
 * famille y range une chaine `YYYY-MM-DD` ou un instant, et `datePure` sur un
 * instant rendrait le vide.
 *
 * @param {any} champ @returns {string}
 */
function habillageDeNatif(champ) {
  const sien = String(champ?.habillage ?? '').trim();
  if (sien !== '') return sien;
  const nature = String(champ?.nature ?? '');
  if (nature === 'date et heure') return 'dateHeure';
  if (nature === 'durée' || nature === 'duree') return 'duree';
  return '';
}

/**
 * L'HABILLAGE D'UN CHAMP : celui que le champ NOMME, sinon celui que sa nature
 * impose.
 *
 * L'ORDRE EST LE POINT. Une app finie a la main nomme parfois un habillage a
 * elle (`jour`, `kilometres`), ecrit dans SON registre : ecraser ce nom par le
 * defaut de la nature aurait remplace « 12 mars » par « 12/03/2026 » a la
 * premiere application, donc casse un affichage qui marchait. La reprise relit
 * ce nom, cette fonction le rend, et le defaut ne sert qu'aux champs qui n'en
 * ont pas.
 *
 * @param {any} champ @returns {string}
 */
export function habillageDeChamp(champ) {
  const sien = String(champ?.habillage ?? '').trim();
  return sien !== '' ? sien : habillageDeNature(champ?.nature);
}

/**
 * L'ENTREE DE CATALOGUE d'une colonne. Un champ NATIF porte son libelle et son
 * type (rien d'autre ne les connait) ; un champ DECLARE ne les porte pas, ils
 * vivent deja dans `taki.fields.json` et le schema refuse de les relire ici.
 *
 * @param {string} nom @returns {any}
 */
function colonneDeCatalogue(nom, t) {
  const habillage = t && t.jeu && t.jeu.habillages ? t.jeu.habillages.get(nom) : undefined;
  const habiller = (entree) => {
    if (habillage !== undefined) entree.habillage = habillage;
    return entree;
  };
  if (!nom.startsWith(PREFIXE_SOCLE)) return habiller({ champ: nom });
  /* UN CHAMP NATIF DECLARE PAR LE JEU porte le mot du fondateur et la nature
     qu'il a choisie : c'est la seule source, le fichier de champs ne le
     connait pas. `STD` reste le repli pour les champs que le SDK pose. */
  const natif = t && t.jeu && t.jeu.natifs ? t.jeu.natifs.get(nom) : undefined;
  if (natif !== undefined) {
    const entree = { champ: nom, label: natif.label, type: natif.type };
    /* SON CARACTERE REQUIS : c'est la colonne du catalogue qui le porte pour un
       natif (le fichier de champs ne le connait pas), et l'ecran generique le
       lit pour reclamer la case a la creation. */
    if (natif.requis === true) entree.requis = true;
    if (natif.placeholder !== undefined) entree.placeholder = natif.placeholder;
    /* SES DECIMALES : meme raison, la colonne est leur seul porteur. */
    if (natif.decimales !== undefined) entree.decimales = natif.decimales;
    /* SON CATALOGUE DE VALEURS, meme raison : rien d'autre ne le porte. C'est
       lui qui allume la pastille d'un `socle:status` sans qu'aucune fonction
       soit ecrite a la main dans l'app. */
    if (natif.options !== undefined) entree.options = natif.options;
    /* ET LA FAMILLE QU'UN `lien` NATIF DESIGNE : c'est elle qui fait lire le nom
       de la fiche visee et ouvrir sa fiche, sans porte a monter dans l'app. */
    if (natif.reference !== undefined) entree.reference = natif.reference;
    return habiller(entree);
  }
  const std = STD.find((x) => x.k === nom.slice(PREFIXE_SOCLE.length));
  return habiller({
    champ: nom,
    label: std ? std.nom : nom.slice(PREFIXE_SOCLE.length),
    type: (std && TYPE_PAR_NATURE[std.nature]) || 'text',
  });
}

/**
 * LE DETAIL D'UNE VUE, projete depuis UNE porte : ses roles, et ce qu'elle
 * demande au catalogue de colonnes.
 *
 * Rend `null` quand la porte ne dit RIEN de cette vue. Une vue sans detail
 * n'est pas retiree du fichier pour autant : elle reste MONTEE et le socle la
 * propose grisee avec sa raison (un calendrier sans date, un kanban sans
 * colonnes). Ecrire un detail vide, en revanche, eteindrait le titre de secours
 * que l'ecran pose tant qu'aucun role n'est declare.
 *
 * @param {any} porte @param {string} vue @param {any} t
 * @returns {{ binding: any, colonnes: any[], triables: string[] }|null}
 */
/**
 * LES TROIS AGREGATS QUE LE SOCLE SAIT FAIRE, ecrits une fois de ce cote-ci.
 *
 * DEUX ENDROITS LES CITENT et comptent exactement la meme chose : le pied d'une
 * colonne de tableau (`total` sur une colonne) et le CHAMP DE TOTAL d'une carte
 * de fiche. La table qui fait foi est `agregatSchema`
 * (`packages/data-model/src/app-screens.ts`) ; celle-ci ne fait que la citer.
 */
const AGREGATS_DE_TOTAL = ['somme', 'moyenne', 'compte'];

function detailDeVue(porte, vue, t) {
  const b = bindingDe(porte, vue);
  const binding = {};
  const colonnes = [];
  const triables = [];

  for (const role of ROLES_DU_CONTRAT) {
    /* UN ROLE CALCULE PAR LE CODE (`fn:<nom>`) ou COMPOSE (`champs:<a>+<b>`) :
       c'est ce qu'un agent pose apres la construction (« le vehicule de
       l'entretien »), et ce que la reprise ramene tel quel. Le brief ne le
       tape pas, il le porte, pour que le formulaire ne reclame pas un champ
       la ou l'app en calcule un. */
    // Le formulaire du tableau choisit des colonnes. Son titre reste celui
    // de la fiche, comme en liste, pour satisfaire le contrat de core.tableau.
    const brut = role === 'title' && vue === 'core.tableau'
      ? b.title || bindingDe(porte, 'core.liste').title || bindingDe(porte, 'core.fiche').title
      : b[role];
    /*
     * CE QU'UN ROLE PORTE EN PLUS DE SON CHAMP (`parRole`).
     *
     * Trois clefs, et les deux premieres ne disent PAS la meme chose : `vide`
     * est ce qu'une fiche EXISTANTE affiche quand personne n'a rempli la case
     * (« Lieu a preciser ») ; `placeholder` est ce qu'une fiche PAS ENCORE
     * CREEE montre dans la case a remplir (« Sony FX9 n°3 »). Le formulaire
     * avait un mot par CHAMP et aucun par ROLE, alors qu'un meme champ montre
     * autre chose selon qu'il tient le titre d'une fiche neuve ou une case de
     * plus.
     *
     * `modifiable` est le troisieme : c'est ce qui ouvre le `h2` d'une fiche au
     * clic, et sans lui on ne peut pas nommer la fiche qu'on vient de creer.
     * `groupKey` garde le sien, la case « Cartes deplacables », parce qu'il ne
     * se lit pas au meme endroit du formulaire.
     */
    const sacs = b.parRole !== null && typeof b.parRole === 'object' ? b.parRole : {};
    const brutSac = sacs[role];
    const sac = brutSac !== null && typeof brutSac === 'object' ? brutSac : {};
    const habiller = (source) => {
      const exemple = String(sac.placeholder ?? '').trim();
      const repli = String(sac.vide ?? '').trim();
      if (exemple !== '') source.placeholder = exemple;
      if (repli !== '') source.vide = repli;
      for (const [clef, valeur] of Object.entries(sac)) {
        if (!['vide', 'placeholder'].includes(clef)) source[clef] = valeur;
      }
      return source;
    };
    if (brut && typeof brut === 'object') {
      binding[role] = structuredClone(brut);
      for (const nom of champsDeSourceBrief(brut)) {
        const entree = colonneDeCatalogue(nom, t);
        if (entree && !colonnes.some(c => c.champ === nom)) colonnes.push(entree);
      }
      continue;
    }
    if (typeof brut === 'string' && brut.startsWith('fn:')) {
      binding[role] = habiller({ fn: brut.slice(3) });
      continue;
    }
    if (typeof brut === 'string' && brut.startsWith('champs:')) {
      binding[role] = habiller({ champs: brut.slice(7).split('+').filter((x) => x !== '') });
      continue;
    }
    const nom = nomDeChampProjete(brut, t);
    if (nom === null) continue;
    const source = habiller({ champ: nom });
    /*
     * « Cartes déplaçables » EST le `modifiable` du role qui groupe : le
     * fichier dit QUE la colonne s'ecrit, l'acces au jeu de l'app dit COMMENT.
     * Sans accesseur en ecriture le role reste en lecture, et la carte ne se
     * glisse pas : un fait, jamais une affordance qui refuse au clic.
     */
    if (role === 'groupKey' && b.deplacable === true) source.modifiable = true;
    /*
     * UN ROLE DE TEXTE HERITE L'HABILLAGE DE SON CHAMP. Le socle ne lit un
     * habillage que sur la SOURCE d'un role (`roleDe`, `packages/ui/src/views/
     * screens.ts`), pas sur la colonne du catalogue : un instant pose en
     * sous-titre de liste s'ecrivait « 9/15/2026 », a la locale du navigateur
     * et sans son heure, alors que la meme colonne du tableau disait
     * « 15/09/2026 14:00 ». Seuls `title` et `subtitle` s'ecrivent en texte ;
     * les roles de date, de statut ou de groupe lisent la valeur brute, et un
     * habillage dessus ne dirait rien.
     */
    if ((role === 'title' || role === 'subtitle') && t.jeu && t.jeu.habillages) {
      const habillage = t.jeu.habillages.get(nom);
      if (habillage !== undefined) source.habillage = habillage;
    }
    binding[role] = source;
    /*
     * UN NATIF QUI TIENT UN ROLE ENTRE AU CATALOGUE des qu'il porte AUTRE CHOSE
     * que son nom, meme si aucune vue ne le montre EN COLONNE.
     *
     * Ce qu'il porte n'a pas d'autre porteur : `taki.fields.json` ne connait pas
     * les champs de la famille, et la liste de colonnes de la fiche ne porte que
     * ce qu'une SECTION reclame, d'ou le titre et le statut sont ecartes (ils
     * s'affichent en tete). Sans cette entree, le `socle:status` d'un materiel arrivait
     * sans catalogue : pastille eteinte, kanban grise, et une stat « combien
     * valent disponible » que la projection laissait tomber. Meme sort pour
     * le `socle:name` d'un intervenant, TITRE de sa fiche et REQUIS : c'est la
     * colonne du catalogue que l'ecran generique lit pour reclamer la case a
     * la creation (`accesDuJeu`), et sans elle un nom vide passait.
     */
    if (nom.startsWith(PREFIXE_SOCLE)) {
      const entree = colonneDeCatalogue(nom, t);
      const porteQuelqueChose =
        entree.options !== undefined ||
        entree.requis === true ||
        entree.placeholder !== undefined ||
        entree.habillage !== undefined;
      if (porteQuelqueChose && !colonnes.some((c) => c.champ === nom)) {
        colonnes.push(entree);
      }
    } else if (!colonnes.some((c) => c.champ === nom)) {
      /*
       * UN CHAMP DECLARE QUI TIENT UN ROLE ENTRE AU CATALOGUE, LUI, TOUJOURS.
       *
       * Il y entrait deja, mais par la bande : la fiche listait TOUT le jeu, et
       * le catalogue se remplissait de ses colonnes. Depuis que la fiche ne
       * liste que ce qu'une section cite (22/09/2026), le champ qui tient le
       * statut d'un kanban ou le sous-titre d'une liste pouvait n'etre cite
       * nulle part en colonne et disparaitre du catalogue. Ce n'est pas une
       * perte de rendu (son libelle, son type et ses valeurs vivent dans
       * `taki.fields.json`), mais c'en est une pour la REPRISE : le Studio
       * refuse de reprendre une app quand il constate qu'il lui ferait perdre
       * une ligne, et les trois temoins de `tests/fixtures/apps/` le
       * prouvaient.
       */
      colonnes.push(colonneDeCatalogue(nom, t));
    }
  }

  if (vue === 'core.tableau') {
    const noms = [];
    for (const c of Array.isArray(b.colonnes) ? b.colonnes : []) {
      const nom = nomDeChampProjete(c && c.champ, t);
      if (nom === null || noms.includes(nom)) continue;
      noms.push(nom);
      const entree = colonneDeCatalogue(nom, t);
      /* On n'ecrit QUE le refus de trier : absent, c'est le defaut du socle
         (tout sauf un texte long), et le forcer a vrai trierait sur un
         paragraphe. Le total, lui, porte les memes trois mots des deux cotes. */
      if (c.triable === false) entree.triable = false;
      if (AGREGATS_DE_TOTAL.includes(c.agg)) entree.total = c.agg;
      colonnes.push(entree);
      if (c.triable === true) triables.push(nom);
    }
    if (noms.length > 0) binding.colonnes = noms;
    /*
     * LES MOTS DE LA VUE : le nom que l'en-tete donne a la colonne du TITRE et a
     * celle du STATUT, que le tableau seul rend. Ils ne se derivent d'aucun
     * champ : le titre d'une fiche est un ROLE, et « Nom » n'est pas « Materiel ».
     */
    const motTitre = String(b.motDuTitre ?? '').trim();
    const motStatut = String(b.motDuStatut ?? '').trim();
    if (motTitre !== '') binding.motDuTitre = motTitre;
    if (motStatut !== '') binding.motDuStatut = motStatut;
  }

  if (vue === 'core.fiche') {
    const cartes = [];
    /*
     * LES LIENS DE LA PORTE, resolus une fois : une CARTE LIEE et un CHAMP DE
     * TOTAL ne citent qu'un lien, et le fichier veut son `id`. Le brief le
     * designe par UID (le formulaire refait l'id d'un lien quand on le
     * renomme), mais l'id est accepte aussi : un brief repris d'un fichier
     * ecrit a la main n'a rien d'autre a offrir.
     */
    const liensDeLaPorte = Array.isArray(porte.liens) ? porte.liens : [];
    const idDuLien = (ref) => {
      const cherche = String(ref || '').trim();
      if (cherche === '') return null;
      const lien = liensDeLaPorte.find(
        (l) => l !== null && typeof l === 'object' && (l.uid === cherche || l.id === cherche),
      );
      const id = lien === undefined ? '' : String(lien.id || '').trim();
      return id === '' ? null : id;
    };
    /*
     * CE QUE LA TETE TIENT DEJA (le titre, le statut), calcule AVANT les cartes
     * parce qu'une carte ne peut pas le citer : il s'affiche et se modifie dans
     * la bande d'identite, le ranger en plus l'afficherait deux fois, et le
     * schema refuserait la declaration (« la carte cite X, hors de ses
     * colonnes »). Natif ou declare, la regle est la meme : c'est le role qui
     * decide (voir `ROLES_EN_TETE`).
     *
     * LES AUTRES ROLES NE SONT PAS LA : le sous-titre et la vignette ne se
     * rendent qu'en LECTURE en tete, donc leur champ se range dans une section
     * comme les autres, et c'est la qu'il se modifie.
     */
    const enTete = new Set(
      ROLES_EN_TETE.map((role) => binding[role])
        .map((source) => (source !== null && typeof source === 'object' ? source.champ : null))
        .filter((nom) => typeof nom === 'string'),
    );
    /* CE QUE LA FICHE LISTE : ce que les cartes RECLAMENT, rien de plus. La
       liste se remplit dans la boucle qui suit, a l'ordre de PREMIERE citation,
       et se pose en `binding.colonnes` apres elle. */
    const noms = [];
    const listes = new Set();
    for (const carte of Array.isArray(b.cards) ? b.cards : []) {
      const titre = String((carte && carte.title) || '').trim();
      /*
       * UNE CARTE LIEE N'A PAS DE CHAMPS : elle monte une VUE du socle sur les
       * fiches d'une AUTRE porte. Elle sort donc AVANT la boucle des champs, et
       * avant le garde « une carte sans champ ne s'ecrit pas », qui la jetterait
       * (constate : la reprise n'en faisait qu'une carte a zero clef).
       */
      /*
       * LA CARTE DES FICHIERS JOINTS, a son rang : ni champ, ni lien, ni
       * disposition. Elle s'ecrit MEME QUAND LE JEU N'OUVRE PAS LES FICHIERS :
       * elle est la presentation, la capacite vit sur le jeu, et couper l'une
       * ne doit pas reecrire l'autre (`appScreenFilesCardSchema`). Une seule
       * par vue, la premiere : le schema refuse la seconde.
       */
      if (estCarteFichiers(carte)) {
        if (titre === '' || cartes.some((c) => c.fichiers === true)) continue;
        const id = String(carte.id || '').trim();
        const ecrite = id === '' ? { titre, fichiers: true } : { id, titre, fichiers: true };
        if (carte.modifiable === false) ecrite.modifiable = false;
        cartes.push(ecrite);
        continue;
      }
      if (carte !== null && typeof carte === 'object' && carte.liee !== undefined) {
        const liee = idDuLien(carte.liee);
        const vueLiee = String(carte.vue || '').trim();
        /* Le schema n'accepte que ces deux vues, et elles doivent etre
           MONTEES : une carte rend une vue du catalogue, elle n'en ouvre pas
           une de plus. */
        if (titre === '' || liee === null) continue;
        if (vueLiee !== 'core.liste' && vueLiee !== 'core.tableau') continue;
        const ecrite = { titre, liee, vue: vueLiee };
        if (Number.isInteger(carte.plafond)) ecrite.plafond = carte.plafond;
        cartes.push(ecrite);
        continue;
      }
      const totaux = Array.isArray(carte && carte.totaux) ? carte.totaux : [];
      const champs = [];
      const vus = new Set();
      /* LES NOMS QUE CETTE CARTE RECLAME, retenus a part : ils n'entrent au
         catalogue de la fiche QUE si la carte s'ecrit vraiment (un titre et au
         moins un champ). Une carte a moitie remplie ne fait pas de colonne. */
      const nommes = [];
      /*
       * LA COLONNE « MODIFIABLE » DEVIENT UN VERROU, ET SEULEMENT LUI.
       *
       * Le socle ouvre une case de fiche PAR DEFAUT des que le jeu s'ecrit :
       * une clef cochee n'a donc rien a ecrire, c'est ce que le fichier dit
       * deja en la nommant. Une clef DECOCHEE, elle, est un refus, et un refus
       * ne se devine pas : il s'ecrit `{ champ, modifiable: false }`.
       *
       * UNE CARTE SANS `edit` EST UNE CARTE D'AVANT LE VERROU : le brief ne dit
       * rien de l'edition, donc on n'ecrit aucun refus. Tout y reste modifiable,
       * exactement comme avant ce lot.
       */
      const edit = Array.isArray(carte && carte.edit) ? carte.edit : null;
      for (const clef of Array.isArray(carte && carte.keys) ? carte.keys : []) {
        /*
         * UN CHAMP DE TOTAL PREND LA PLACE D'UN NOM, A SON RANG : la carte
         * garde UN ordre, et c'est pour ca que son uid voyage dans `keys`
         * plutot que dans une seconde liste a recoller. Son `champ` appartient
         * au jeu LIE : il s'ecrit tel quel, aucun uid du brief ne le nomme.
         */
        const total = totaux.find((x) => x !== null && typeof x === 'object' && x.uid === clef);
        if (total !== undefined) {
          const liee = idDuLien(total.liee);
          const calcul = String(total.total || '').trim();
          const libelle = String(total.libelle || '').trim();
          if (liee === null || libelle === '' || !AGREGATS_DE_TOTAL.includes(calcul)) continue;
          const champ = String(total.champ || '').trim();
          /* UNE PONDERATION A MOITIE REMPLIE FAIT TOMBER LE TOTAL, elle ne se
             retire pas en silence : la somme des seules quantites s'afficherait
             sous un mot de prix (`etatDePonderation`, qui dit aussi pourquoi). */
          const ponderation = etatDePonderation(total);
          if (ponderation.etat === 'incomplete') continue;
          /* Compter des fiches ne porte aucun champ, sommer ou moyenner en
             exige un : le schema le refuse, on ne l'ecrit pas a moitie. */
          if (calcul === 'compte') champs.push({ libelle, liee, total: calcul });
          else if (champ !== '') {
            champs.push({
              libelle,
              liee,
              total: calcul,
              champ,
              ...(ponderation.etat === 'complete' ? { fois: ponderation.fois } : {}),
            });
          }
          continue;
        }
        const nom = nomDeChampProjete(clef, t);
        /* UNE CLEF QUE LA TETE TIENT DEJA (le titre, le statut) ne se range
           dans aucune carte : elle s'affiche et se saisit en tete, et le schema
           refuserait la carte. Un brief d'avant le 22/09/2026 en porte une : la
           derivation passe son tour, elle ne refuse rien. */
        if (nom === null || vus.has(nom) || enTete.has(nom)) continue;
        vus.add(nom);
        nommes.push(nom);
        champs.push(
          edit === null || edit.includes(clef) ? nom : { champ: nom, modifiable: false },
        );
      }
      /* Le schema exige un titre et au moins un champ : une carte a moitie
         remplie ne s'ecrit pas, elle reste a finir dans le Studio. */
      if (titre === '' || champs.length === 0) continue;
      /* LA CARTE S'ECRIT : ses champs entrent au catalogue de la fiche, a
         l'ordre de leur PREMIERE citation, sans doublon. */
      for (const nom of nommes) {
        if (listes.has(nom)) continue;
        listes.add(nom);
        noms.push(nom);
      }
      /* LA DISPOSITION NE S'ECRIT QUE QUAND ELLE DIT QUELQUE CHOSE : `demi` est le defaut du
         socle, et « non » s'ecrit en RETIRANT la clef, jamais en la posant a sa valeur neutre.
         Lecture par comparaison explicite : tout ce qui n'est pas l'une des deux autres est
         demi, y compris un `"demi"` ecrit a la main dans le fichier. */
      /* LE PLACEMENT, MEME REGLE : `principal` est le defaut et ne s'ecrit pas ;
         `droite` s'ecrit, et une carte a droite tient en DEMI (le schema refuse
         `droite` avec une autre disposition), donc sa disposition se tait. */
      const placement = placementDe(carte);
      const disposition =
        placement !== 'droite' &&
        carte &&
        (carte.disposition === 'pleine' || carte.disposition === 'titre-pleine')
          ? carte.disposition
          : 'demi';
      const ecrite = disposition === 'demi' ? { titre, champs } : { titre, champs, disposition };
      if (placement === 'droite') ecrite.placement = 'droite';
      cartes.push(ecrite);
    }
    /*
     * LA FICHE REND SES SECTIONS, ET RIEN D'AUTRE. Decision du fondateur du
     * 22/09/2026 : `colonnes` ne liste QUE ce que les cartes reclament, plus
     * jamais tout le jeu.
     *
     * CE QUE CA FERME. La liste etait « tous les champs du jeu », parce que le
     * socle ramassait les non-reclames dans une carte « Autres champs » et
     * qu'on y voyait une promesse (« un champ declare ne disparait jamais »).
     * Cette carte etait CALCULEE : elle s'affichait sur presque toutes les
     * portes, le fondateur ne pouvait ni la nommer, ni la deplacer, ni la
     * retirer, et un jeu de 150 champs dont une porte ne montre que trois etait
     * impossible a declarer. Le socle ne la rend plus (`buildCards`), donc la
     * derivation ne liste plus ce que personne ne montre.
     *
     * UN CHAMP NATIF EST LISTE COMME UN AUTRE des qu'une carte le cite : il
     * n'est dans aucun fichier de champs, donc sans cette entree la fiche ne le
     * montrerait NULLE PART (le debut, la fin et l'organisateur d'une
     * reservation, apps du 4 septembre). Ce que la tete tient deja (titre,
     * statut) en est ECARTE, a la boucle des cartes ci-dessus.
     *
     * L'ORDRE EST CELUI DE LA PREMIERE CITATION, plus celui du jeu : c'est
     * l'ordre ou les cases se rendent, et il n'a plus a suivre une liste que
     * personne ne lit.
     */
    if (noms.length > 0) {
      binding.colonnes = noms;
      /* PAS DEUX FOIS LA MEME COLONNE : un natif sous un role qui porte
         quelque chose (un catalogue de statut, un habillage) est deja entre au
         catalogue plus haut, et il est desormais rangeable en section. */
      for (const nom of noms) {
        if (colonnes.some((c) => c.champ === nom)) continue;
        colonnes.push(colonneDeCatalogue(nom, t));
      }
    }
    /*
     * LES CARTES S'ECRIVENT DES QU'IL Y EN A, et non plus seulement quand la
     * vue a des colonnes : une carte LIEE ne rend aucune case de CETTE fiche,
     * ni celle des FICHIERS joints, donc elles n'exigent aucune colonne (le
     * schema le dit ainsi). Une carte de
     * CHAMPS, elle, en exige toujours : sans colonne, on ne l'ecrit pas, sinon
     * l'assemblage jetterait au montage.
     */
    if (cartes.length > 0 && (noms.length > 0 || cartes.every((c) => c.champs === undefined))) {
      binding.cartes = cartes;
    }
  }

  /*
   * LES TOTAUX LIES PAR LIGNE (tableau, kanban) : la forme d'un champ de total
   * de fiche, `{ libelle, liee, total, champ }`, le lien designe par son UID
   * dans le brief (ou son id, pour un brief repris d'un fichier ecrit a la
   * main). Un total incomplet ne s'ecrit pas : le schema le refuserait.
   */
  if ((vue === 'core.tableau' || vue === 'core.kanban') && Array.isArray(b.totauxLies)) {
    const liens = Array.isArray(porte.liens) ? porte.liens : [];
    const totaux = [];
    for (const total of b.totauxLies) {
      if (total === null || typeof total !== 'object') continue;
      const cherche = String(total.liee || '').trim();
      const lien = liens.find((l) => l !== null && typeof l === 'object' && cherche !== '' && (l.uid === cherche || l.id === cherche));
      const liee = lien === undefined ? '' : String(lien.id || '').trim();
      const calcul = String(total.total || '').trim();
      const libelle = String(total.libelle || '').trim();
      const champ = String(total.champ || '').trim();
      if (liee === '' || libelle === '' || !AGREGATS_DE_TOTAL.includes(calcul)) continue;
      if (calcul === 'compte') totaux.push({ libelle, liee, total: calcul });
      else if (champ !== '') totaux.push({ libelle, liee, total: calcul, champ });
    }
    if (totaux.length > 0) binding.totauxLies = totaux.slice(0, 3);
  }

  /*
   * LA SURBRILLANCE DES CARTES (kanban seulement, `docs/socle/cartes-kanban.md`,
   * section 2) : des regles lues dans l'ordre. Le brief designe le champ comme
   * les roles (uid, ou `std:<champ>`), ou dit `nouveau` ; la derivation ecrit
   * la condition du socle. Une regle dont le champ a disparu, ou a moitie
   * remplie, ne s'ecrit pas : meme sort que le total lie dont le lien est parti.
   *
   * UNE LISTE VIDEE S'ECRIT PAR LA REGLE PAR DEFAUT, et non par une absence : le
   * Studio du depot garde ce que le derive ne dit pas, donc la retirer
   * laisserait les anciennes regles sur le disque. Ecrire « Nouveau » dit
   * exactement ce que le kanban fait sans regle.
   */
  if (vue === 'core.kanban' && Array.isArray(b.surbrillances)) {
    const regles = b.surbrillances
      .map((regle) => surbrillanceProjetee(regle, t))
      .filter((regle) => regle !== null);
    binding.surbrillances = regles.length > 0 ? regles.slice(0, 8) : [{ ...SURBRILLANCE_PAR_DEFAUT }];
  }

  return Object.keys(binding).length === 0 ? null : { binding, colonnes, triables };
}

/**
 * LA REGLE QU'UN KANBAN APPLIQUE QUAND IL N'EN DECLARE AUCUNE, celle du socle
 * (`SURBRILLANCE_PAR_DEFAUT`, `@taki/ui`) : la fiche jamais ouverte, en accent.
 * Le Studio la montre en aide et repart d'elle au premier ajout.
 */
export const SURBRILLANCE_PAR_DEFAUT = Object.freeze({ si: 'nouveau', couleur: 'accent', etiquette: 'Nouveau' });

/** Le mot du brief qui designe la fiche jamais ouverte, a la place d'un champ. */
export const CHAMP_NOUVEAU = 'nouveau';

/**
 * UNE REGLE DE SURBRILLANCE DU BRIEF, ECRITE POUR L'ECRAN, ou `null`.
 *
 * Quatre formes, et la derniere ne se saisit pas : `champ: 'nouveau'` ;
 * `{ champ, valeur }`, la valeur stockee d'une option, un booleen ou un texte
 * (`parmi` a une valeur) ; `{ champ, moi: true }` sur une personne ; et `si`,
 * une condition du socle que la reprise n'a pas su ramener a ces formes, gardee
 * telle quelle (« Condition avancée »).
 *
 * @param {any} regle @param {any} t @returns {{ si: any, couleur: string, etiquette?: string }|null}
 */
function surbrillanceProjetee(regle, t) {
  if (regle === null || typeof regle !== 'object') return null;
  if (!couleurSchema.safeParse(regle.couleur).success) return null;
  let si = null;
  if (regle.si !== undefined) {
    const lue = regle.si === CHAMP_NOUVEAU ? { success: true, data: CHAMP_NOUVEAU } : conditionSchema.safeParse(regle.si);
    if (lue.success) si = lue.data;
  } else if (regle.champ === CHAMP_NOUVEAU) {
    si = CHAMP_NOUVEAU;
  } else {
    const champ = nomDeChampDeRegle(regle.champ, t);
    const valeur = regle.valeur;
    const feuille =
      champ === null ? null
        : regle.moi === true ? { champ, op: 'moi' }
          : (typeof valeur === 'string' && valeur.trim() !== '') || typeof valeur === 'number' || typeof valeur === 'boolean'
            ? { champ, op: 'parmi', valeurs: [valeur] }
            : null;
    if (feuille !== null && conditionSchema.safeParse(feuille).success) si = feuille;
  }
  if (si === null) return null;
  const ecrite = { si, couleur: regle.couleur };
  const etiquette = String(regle.etiquette ?? '').trim().slice(0, 24);
  if (etiquette !== '') ecrite.etiquette = etiquette;
  return ecrite;
}

/**
 * LE NOM D'ECRAN DU CHAMP D'UNE REGLE : la reference du brief (uid,
 * `std:<champ>`), ou un nom deja ecrit pour l'ecran que le jeu connait.
 * @param {unknown} ref @param {any} t @returns {string|null}
 */
function nomDeChampDeRegle(ref, t) {
  const nom = nomDeChampProjete(ref, t);
  if (nom !== null) return nom;
  return typeof ref === 'string' && t.jeu && t.jeu.clefs.has(ref) ? ref : null;
}

/**
 * UN ID DE FICHIER, derive d'un libelle et rendu UNIQUE dans son voisinage.
 *
 * MEME GESTE QUE POUR L'ID D'UNE STAT, et pour la meme raison : cet id voyage
 * (une preference de poste, un favori qu'on rallume), donc il vient du LIBELLE,
 * la seule chose que le fondateur ait tapee, jamais de l'uid opaque du brief.
 * Les trois schemas exigent la meme forme (`^[a-z][a-z0-9-]*$`).
 *
 * @param {string} label @param {Set<string>} vus @param {string} repli
 * @returns {string}
 */
function idProjete(label, vus, repli) {
  let id = kebab(label);
  if (!/^[a-z]/.test(id)) id = id === '' ? repli : repli + '-' + id;
  let unique = id;
  let n = 2;
  while (vus.has(unique)) unique = id + '-' + n++;
  vus.add(unique);
  return unique;
}

/**
 * LE CATALOGUE DE FILTRES D'UNE PORTE, projete, ou `null`.
 *
 * `parChamp` NE S'ECRIT QUE SI LE CHAMP PORTE VRAIMENT DES OPTIONS : dans le
 * fichier de champs pour un champ declare, dans le jeu pour un champ natif
 * (`socle:status`). C'est exactement le controle que `statsProjetees` fait
 * deja pour `nbVaut`, et il n'est pas decoratif : le socle JETTE au montage
 * sur un `parChamp` qui ne resout pas vers un champ a choix, avec un message
 * qui accuse la declaration.
 *
 * UN NATIF SE FILTRE PAR VALEUR DES QUE LE CATALOGUE DE COLONNES DE L'ECRAN
 * PORTE SES `options` (`filtresDe`, `packages/ui/src/views/screens.ts`). On le
 * refusait ici, avec un commentaire d'avant cette regle du socle, alors que le
 * formulaire le proposait : le favori qui cochait « A faire » sortait sans
 * filtre. Sa colonne est donc RECLAMEE au catalogue (`colonne`), pour le cas
 * ou aucune vue ne la cite.
 *
 * `autres` porte les TROUS NOMMES : le libelle du fondateur donne l'id et le
 * nom d'appel, le predicat reste une fonction TypeScript que l'agent ecrit.
 * Il n'y aura jamais d'operateur ici. Un filtre dont le predicat est DEJA ecrit
 * (la reprise a remonte son `fn`) garde le nom que le code lui donne.
 *
 * ELLE REND AUSSI SA TABLE DE REFERENCES (`parRef`), parce qu'un favori coche
 * DES FILTRES DE CETTE PORTE : la reference du brief (`valeur:...`,
 * `filtre:<uid>`) y trouve l'id REELLEMENT ecrit dans le fichier, et rien
 * d'autre. Une reference absente de cette table designe un filtre qui n'existe
 * pas dans la declaration : le favori ne peut pas la poser.
 *
 * @param {any} porte @param {any} t
 * @returns {{ catalogue: any, parRef: Map<string, string>, colonne: any }}
 */
function filtresProjetes(porte, t) {
  const { champ, autres } = filtresDe(porte);
  const sortie = {};
  const parRef = new Map();
  let colonne = null;

  const nom = nomDeChampProjete(champ, t);
  if (nom !== null) {
    const declare = t.jeu.declares.get(nom) ?? t.jeu.natifs?.get(nom);
    if (declare && (declare.options || []).length > 0) {
      sortie.parChamp = nom;
      /* LE SOCLE TIRE UN FILTRE PAR VALEUR : l'id pose par un favori EST la
         valeur stockee, celle que le fichier de champs (ou le jeu) ecrit. */
      for (const option of declare.options) parRef.set(refValeur(option.value), option.value);
      if (nom.startsWith(PREFIXE_SOCLE)) colonne = colonneDeCatalogue(nom, t);
    }
  }

  const nommes = [];
  const ids = new Set();
  const fns = new Set();
  for (const autre of autres) {
    const label = String((autre && autre.label) || '').trim();
    if (label === '') continue;
    /*
     * DEUX FILTRES NE SE MARCHENT PLUS DESSUS.
     *
     * On ECARTAIT le second quand deux libelles donnaient le meme nom d'appel
     * (« En panne » et « en panne ! » rendent tous deux `enPanne`) : le filtre
     * disparaissait de l'app, et son entree dans `parRef` n'etait jamais posee,
     * donc tout favori qui le cochait perdait sa case, en silence. C'est la
     * collision que la mise en references etait censee fermer : elle ne l'avait
     * pas fermee, elle l'avait DEPLACEE du libelle vers le nom d'appel.
     *
     * On rend donc le nom d'appel UNIQUE, exactement comme `idProjete` rend
     * l'id unique. Deux filtres homonymes donnent `enPanne` et `enPanne2` :
     * l'app en garde deux, et c'est au fondateur de les renommer s'il veut,
     * pas au code de choisir lequel jeter.
     */
    const id = String(autre.id || '').trim() || idProjete(label, ids, 'filtre');
    ids.add(id);
    if (autre.condition !== undefined) {
      nommes.push({ id, label, condition: structuredClone(autre.condition) });
      parRef.set(refFiltre(String(autre.uid || '')), id);
      continue;
    }
    if (!String(autre.fn || '').trim()) throw new Error(`Le filtre « ${label} » exige une condition déclarative.`);
    /* UN PREDICAT DEJA ECRIT GARDE SON NOM. Le nom se DERIVE du libelle tant
       que personne ne l'a ecrit ; des que le code de l'app le porte (la reprise
       l'a remonte dans `fn`), c'est LUI qui fait foi, sans quoi la derivation
       renommerait une fonction qui existe et l'app ne monterait plus. */
    const base = String((autre && autre.fn) || '').trim() || nomDAppel(label) || nomDAppel(id) || 'filtre';
    let fn = base;
    let n = 2;
    while (fns.has(fn)) fn = base + n++;
    fns.add(fn);
    nommes.push({ id, label, fn });
    parRef.set(refFiltre(String((autre && autre.uid) || '')), id);
  }
  if (nommes.length > 0) sortie.autres = nommes;

  return { catalogue: Object.keys(sortie).length === 0 ? null : sortie, parRef, colonne };
}

/**
 * UN NIVEAU DE TRI, projete, ou `null`.
 *
 * Une clef porte un CHAMP ou un ROLE, jamais les deux : le schema le refuse
 * autrement. Un role sans son mot est ECARTE plutot que servi muet, parce que
 * le schema EXIGE ce libelle (rien d'autre ne connait le mot de « Nom »).
 *
 * @param {any} niveau @param {any} t
 * @returns {{ clef: string, option: any, niveau: any }|null}
 */
function niveauProjete(niveau, t) {
  const sens = String((niveau && niveau.sens) || 'asc') === 'desc' ? 'desc' : 'asc';
  const role = String((niveau && niveau.role) || '');
  if (role !== '') {
    if (!ROLES_TRI.some((r) => r.id === role)) return null;
    const mot = String((niveau && niveau.mot) || '').trim();
    if (mot === '') return null;
    /*
     * L'OPTION ET LE NIVEAU NE PORTENT PAS LA MEME CHOSE, et le schema le
     * refuse dans les deux sens : une OPTION du catalogue porte son libelle (le
     * popover l'affiche), un NIVEAU de pile ne porte QU'une clef et un sens (il
     * ne s'affiche pas, il trie). Les confondre fait refuser le fichier entier
     * -- constate a la premiere naissance : « tri.defaut[1] : cle inconnue
     * "label" ».
     */
    return { clef: 'role:' + role, option: { role, label: mot }, niveau: { role, sens } };
  }
  const nom = nomDeChampProjete(niveau && niveau.champ, t);
  if (nom === null) return null;
  return { clef: 'champ:' + nom, option: { champ: nom }, niveau: { champ: nom, sens } };
}

/** La pile de tri d'une porte ou d'un favori, projetee, niveau par niveau.
 * @param {any} pile @param {any} t @returns {{ clef: string, option: any, niveau: any }[]} */
function pileProjetee(pile, t) {
  const sortie = [];
  const vues = new Set();
  for (const brut of Array.isArray(pile) ? pile : []) {
    const n = niveauProjete(brut, t);
    /* UN NIVEAU EN DOUBLE NE TRIE RIEN DE PLUS : le premier a deja departage,
       et le second serait une ligne morte dans le popover. */
    if (n === null || vues.has(n.clef)) continue;
    vues.add(n.clef);
    sortie.push(n);
  }
  return sortie;
}

/**
 * LES FAVORIS PRESETS D'UNE PORTE, projetes.
 *
 * IL N'Y A PLUS RIEN A RE-TRADUIRE, et c'est tout le lot : le brief porte une
 * REFERENCE (`valeur:enPanne`, `filtre:<uid>`), la projection la cherche dans
 * la table du catalogue REELLEMENT ecrit et pose l'id trouve. Elle comparait
 * des LIBELLES : renommer une option perdait le favori en silence, et un filtre
 * nomme homonyme d'une valeur ECRASAIT cette valeur, donc le favori posait le
 * predicat au lieu de l'egalite cochee.
 *
 * UNE REFERENCE QUI NE RESOUT PLUS EST ECARTEE, jamais devinee : elle designe
 * un filtre absent de la declaration (option supprimee du champ, filtre nomme
 * sans libelle), et le socle refuserait un favori qui pose un id inconnu.
 *
 * @param {any} porte @param {any} t @param {Map<string, string>} parRef
 * @returns {{ favoris: any[], niveaux: { clef: string, option: any }[] }}
 */
function favorisProjetes(porte, t, parRef) {
  const favoris = [];
  const niveaux = [];
  const ids = new Set();
  for (const brut of Array.isArray(porte.favoris) ? porte.favoris : []) {
    const label = String((brut && brut.label) || '').trim();
    if (label === '') continue;

    const poses = [];
    for (const coche of Array.isArray(brut.filtres) ? brut.filtres : []) {
      const id = parRef.get(String(coche || '').trim());
      if (id !== undefined && !poses.includes(id)) poses.push(id);
    }
    const pile = pileProjetee(brut.tri, t);

    /* UN FAVORI QUI NE POSE RIEN N'EST PAS UN FAVORI : il rallumerait l'etat
       courant, c'est-a-dire rien du tout. Il reste dans le brief (le nom est
       une intention), il n'entre pas dans le fichier. */
    if (poses.length === 0 && pile.length === 0) continue;

    for (const n of pile) niveaux.push({ clef: n.clef, option: n.option });
    favoris.push({
      id: idProjete(label, ids, 'favori'),
      label,
      filtres: poses,
      tri: pile.map((n) => n.niveau),
    });
  }
  return { favoris, niveaux };
}

/**
 * LES ACTIONS D'UNE PORTE, projetees, ET LE BOUTON AJOUTER EN PREMIER.
 *
 * L'AJOUT EST LA PREMIERE ACTION DE CHAQUE PORTE, toujours, parce que c'est
 * exactement ce que le formulaire dit (« Ajouter », etiquette « toujours là »)
 * et que rien ne le declarait : une app neuve naissait donc SANS bouton
 * Ajouter. Son libelle est ignore a l'ecran (le chassis rend « + Ajouter », en
 * premier, en lime, icone figee, regle G5), et une seule par porte.
 *
 * L'AJOUT N'A PAS D'EFFET, ET C'EST LA DECISION DU 09/09/2026 : la CREATION est
 * une FICHE VIDE en zone 5 (brouillon `~nouveau`), tenue par le SOCLE, et
 * l'EDITION se fait en place. Une action `kind: 'add'` SANS `fn` dit exactement
 * cela au moteur, qui pose lui-meme le geste. Ecrire un `fn` ici rendrait la
 * main a l'app, c'est-a-dire au `Modal` que ce chantier vient de retirer.
 *
 * L'EFFET DES AUTRES ACTIONS RESTE DE LA PROSE, ET IL LE RESTERA : il ferme sur
 * l'etat de l'ecran (lire ce que la vue affiche a cet instant), donc aucun
 * fichier de donnees ne peut le porter sans devenir un langage. Ce qui entre
 * ici est son NOM D'APPEL, derive du libelle : l'ecran le fournit au montage,
 * et un nom que l'ecran ne fournit pas est un refus immediat portant ce nom.
 * Une action dont l'effet est DEJA ecrit (la reprise a remonte son `fn`) garde
 * le nom que le code lui donne.
 *
 * @param {any} porte @returns {any[]}
 */
function actionsProjetees(porte) {
  /*
   * UNE PORTE PEUT REFUSER L'AJOUT, et c'est une clef du brief, pas une
   * exception ecrite quelque part.
   *
   * L'ajout est la premiere action de CHAQUE porte, parce que rien ne le
   * declarait et qu'une app neuve naissait sans bouton Ajouter. Mais une porte
   * de CONSULTATION (le materiel en maintenance, une planche d'etiquettes) n'a
   * rien a creer : le bouton y promettait un geste qui n'a pas de sens, et le
   * seul moyen de s'en passer etait d'ecrire le fichier a la main, donc de
   * sortir du Studio.
   */
  /*
   * UNE PORTE CANVAS SANS JEU N'A RIEN OU CREER : l'ajout tenu par le socle cree
   * dans le jeu de la porte, et `pnpm apps:check` refuse un ajout sans jeu
   * (« l'ajout tenu par le socle exige le jeu de la porte »). Vu le 10/10/2026
   * sur `test-canva`, porte Dashboard. La surface pose ses propres gestes par
   * `poserActions` le jour ou elle en a.
   */
  const sansJeu = porte.custom === true && (porte.sansJeu === true || String(porte.dsRef ?? '') === '');
  const refuse = sansJeu || (porte.ajout !== null && typeof porte.ajout === 'object' && porte.ajout.refuse === true);
  const ids = new Set(refuse ? [] : ['ajouter']);
  const fns = new Set();
  const sortie = refuse ? [] : [{ id: 'ajouter', label: 'Ajouter', kind: 'add' }];

  for (const brute of Array.isArray(porte.actions) ? porte.actions : []) {
    const label = String((brute && brute.label) || '').trim();
    if (label === '') continue;
    /* UN EFFET DEJA ECRIT GARDE SON NOM. Le nom se DERIVE du libelle tant que
       personne ne l'a ecrit ; des que le code de l'app le porte (la reprise l'a
       remonte dans `fn`), c'est LUI qui fait foi, sans quoi la derivation
       rebaptiserait une fonction qui existe et le montage de l'ecran la
       refuserait, l'app ne fournissant pas ce nom-la. */
    /* UN GESTE DE FICHE (`niveau: "fiche"`) QUI NOMME UNE FENETRE garde sa
       fenetre et n'a pas d'`fn` : l'un OU l'autre, le schema refuse les deux. */
    const fenetre = String((brute && brute.fenetre) || '').trim();
    const deFiche = String((brute && brute.niveau) || '') === 'fiche';
    const fn = fenetre !== '' ? '' : String((brute && brute.fn) || '').trim() || nomDAppel(label);
    if (fenetre === '' && (fn === '' || fns.has(fn))) continue;
    if (fn !== '') fns.add(fn);

    const declaree = { id: idProjete(label, ids, 'action'), label };
    const variant = String((brute && brute.variant) || '');
    if (variant === 'primary' || variant === 'secondary') declaree.variant = variant;
    if (deFiche) declaree.niveau = 'fiche';
    if (fenetre !== '') declaree.fenetre = fenetre;
    else declaree.fn = fn;
    /*
     * LA FENETRE DE CONFIRMATION SE DERIVE DU LIBELLE, le seul mot que le
     * fondateur ait tape sur cette action. Le brief dit QU'ON confirme (un
     * booleen), pas COMMENT : inventer une phrase metier serait de la prose
     * ecrite a sa place, alors que reprendre son mot ne dit rien de plus que
     * ce qu'il a deja ecrit sur le bouton.
     */
    if (brute.confirm === true && !deFiche) {
      declaree.confirmation = {
        ton: 'neutral',
        titre: label,
        corps: 'Confirmer « ' + label + ' » ?',
        faire: label,
      };
    }
    sortie.push(declaree);
  }
  return sortie;
}

/**
 * LES STATS D'UNE PORTE, projetees.
 *
 * Le calcul retenu est celui que `calculDe` rend, c'est-a-dire CELUI QUE LE
 * FORMULAIRE AFFICHE : changer de champ retire des calculs de l'offre, et V1
 * retombait deja sur le premier de la nouvelle liste. Lire `stat.calcul` brut
 * ecrirait un agregat que le fondateur ne voit plus a l'ecran.
 *
 * DEUX STATS NE S'ECRIVENT PAS, et c'est la ligne rouge : « Autre calcul, je
 * l'écris... » est une REGLE en francais, pas un nom d'agregat ; et un
 * `nbVaut` sur une case a cocher compare a « oui », alors que la valeur stockee
 * depend de l'acces au jeu que l'agent ecrira. Sur une LISTE DE CHOIX, en
 * revanche, la traduction est exacte et elle compte : le formulaire propose le
 * LIBELLE (« En cours »), le document porte la VALEUR (`enCours`), et recopier
 * le libelle donnerait un compteur a zero pour toujours, sans rien signaler.
 *
 * @param {any} porte @param {any} brief @param {any} t @returns {any[]}
 */
/** Les calculs dont le resultat se lit dans l'UNITE du champ (voir `statsProjetees`). */
const CALCULS_QUI_GARDENT_L_UNITE = ['total', 'moyenne', 'min', 'max'];

function statsProjetees(porte, brief, t) {
  const sortie = [];
  const ids = new Set();
  for (const stat of Array.isArray(porte.stats) ? porte.stats : []) {
    const label = String((stat && stat.label) || '').trim();
    if (label === '') continue;
    const calcul = calculDe(porte, stat, brief);
    if (!calcul || calcul.libre) continue;

    const declaree = { label, calcul: calcul.k };
    if (calcul.k !== 'nb') {
      const nom = nomDeChampProjete(stat.champ, t);
      if (nom === null) continue;
      declaree.champ = nom;
      if (calcul.k === 'nbVaut') {
        const champ = t.jeu.declares.get(nom) ?? t.jeu.natifs?.get(nom);
        /*
         * ON RESOUT PAR VALEUR, jamais par libelle. Le formulaire stocke
         * desormais la valeur ; on accepte encore un LIBELLE pour les briefs
         * d'avant la forme 6, que la migration a deja remontes, et pour un
         * brief bricole a la main.
         */
        const cherche = String(stat.valeur || '').trim();
        const options = (champ && champ.options) || [];
        const option =
          options.find((o) => o.value === cherche) || options.find((o) => o.label === cherche);
        if (option === undefined) continue;
        declaree.valeur = option.value;
      }
    }

    /* L'ID VOYAGE DANS LA PREFERENCE DU POSTE (quelles stats s'affichent) : il
       vient du libelle, la seule chose que le fondateur ait tapee, jamais de
       l'uid opaque du brief, que le schema refuse de toute facon. */
    /*
     * L'HABILLAGE D'UNE STAT SUIT CELUI DE SON CHAMP, et seulement quand le
     * calcul garde l'UNITE : la somme et la moyenne d'un prix sont des prix, le
     * plus ancien et le plus recent d'une date sont des dates. Un COMPTE, lui,
     * est un nombre de fiches : l'habiller en euros afficherait « 12 € » sous
     * le mot « materiels ».
     */
    if (CALCULS_QUI_GARDENT_L_UNITE.includes(calcul.k)) {
      const habillage = t.jeu.habillages ? t.jeu.habillages.get(declaree.champ) : undefined;
      if (habillage !== undefined) declaree.habillage = habillage;
    }

    /* SAUF QUAND LE FICHIER EN PORTAIT DEJA UN : une app ecrite a la main (ou
       retouchee apres le moteur) nomme sa stat comme elle veut, et la reprise
       garde ce nom (`reprise.mjs`). Le reecrire d'apres le libelle changerait
       l'identite de la stat pour le poste qui l'affiche et pour l'instance
       d'une org qui l'a personnalisee (`fusionnerDeclarations`), sans que rien
       ne le dise. Il ne sert que s'il a la forme du schema et n'est pas pris. */
    const sien = String((stat && stat.id) || '').trim();
    if (/^[a-z][a-z0-9-]*$/.test(sien) && !ids.has(sien)) {
      ids.add(sien);
      sortie.push({ id: sien, ...declaree });
      continue;
    }
    let id = kebab(label);
    if (!/^[a-z]/.test(id)) id = id === '' ? 'stat' : 'stat-' + id;
    let unique = id;
    let n = 2;
    while (ids.has(unique)) unique = id + '-' + n++;
    ids.add(unique);
    sortie.push({ id: unique, ...declaree });
  }
  return sortie;
}

/**
 * LE DETAIL DE L'ECRAN : son catalogue de colonnes, son detail par vue, son
 * catalogue de tri, ses trois gestes de fiche.
 *
 * TOUT CELA EST DECLARE PAR L'ECRAN ET NON PAR LA PORTE, parce que le socle le
 * veut ainsi : `sortOptions` est une prop du gabarit passee une fois pour
 * toutes les portes, et `bindingsDe` rend UN binding par vue montee. Le brief,
 * lui, regle ces choses PORTE PAR PORTE. La regle de passage, ecrite une fois :
 *
 *  - une vue prend le detail de la PREMIERE porte qui en dit quelque chose ;
 *  - les portes qui montrent LE MEME JEU que la porte d'arrivee composent le
 *    detail de l'ECRAN ;
 *  - UNE PORTE DU MEME JEU QUI LE REGLE AUTREMENT ecrit le sien (`porte.vb`),
 *    en entier, sur les vues qu'elle montre : un kanban groupe par Categorie
 *    sur une porte quand l'ecran groupe par Etape. Celle qui ne dit rien de
 *    plus que l'ecran n'ecrit rien ;
 *  - UNE PORTE QUI MONTRE UN AUTRE JEU COMPOSE LE SIEN, ecrit sur elle
 *    (`porte.vb`). C'est ce que ce lot ajoute, et il a coute deux apps : une
 *    colonne prise a un autre jeu JETTE au montage avec son nom, et jusqu'ici
 *    le detail des autres jeux etait simplement PERDU, donc la porte 2
 *    racontait les fiches de la porte 1 ;
 *  - les gestes de fiche viennent de la porte d'ARRIVEE, la seule qui puisse
 *    trancher pour l'ecran entier.
 *
 * LE CATALOGUE DE COLONNES RESTE CELUI DE L'ECRAN et porte desormais les
 * colonnes des DEUX jeux : c'est ce que le schema attend (un seul catalogue,
 * cite par nom), et le socle ne batit d'une porte que les colonnes que SON
 * detail cite.
 *
 * @param {any[]} portes @param {any} brief @param {string[]} vues @param {Map<string, any>} ecrits
 * @param {{ clef: string, option: any }[]} citees les clefs qu'une pile de tri reclame
 * @param {Map<string, string[]>} vuesParPorte les vues que chaque porte declaree montre
 * @param {any[]} reclamees les colonnes qu'un filtre reclame au catalogue
 */
function detailDEcran(portes, brief, vues, ecrits, citees, vuesParPorte = new Map(), reclamees = []) {
  const arrivee = portes[0];
  const jeuArrivee = jeuDePorte(arrivee, brief);
  const memeJeuQueLArrivee = (p) => {
    const j = jeuDePorte(p, brief);
    return jeuArrivee !== null && j !== null && j.clef === jeuArrivee.clef;
  };
  const lues = portes.filter(memeJeuQueLArrivee);
  const source = lues.length > 0 ? lues : [arrivee];

  const colonnes = new Map();
  const triables = [];

  /** Ce qu'un detail de vue RETENU reclame au catalogue de colonnes et au tri. */
  const retenir = (detail) => {
    /* Une colonne se declare UNE fois pour l'ecran : le tableau et la fiche
       la reprennent chacun dans son ordre. La premiere pose donne le ton,
       parce que c'est la seule qui porte un total ou un refus de tri. */
    for (const entree of detail.colonnes) {
      if (!colonnes.has(entree.champ)) colonnes.set(entree.champ, entree);
    }
    for (const nom of detail.triables) if (!triables.includes(nom)) triables.push(nom);
  };

  /**
   * LE DETAIL PAR VUE D'UN GROUPE DE PORTES qui montrent LE MEME jeu : pour
   * chaque vue, celui de la premiere porte qui la MONTRE et en parle, sinon
   * celui de la premiere qui en parle. Il ne RETIENT rien au catalogue : c'est
   * `vbDe` qui le fait, pour le detail qu'on ecrit.
   *
   * LA PORTE QUI MONTRE LA VUE PASSE D'ABORD : une porte peut parler d'une vue
   * qu'elle ne monte pas (le titre du tableau retombe sur celui de la liste,
   * `detailDeVue`), et ce detail de rencontre gagnait sur le vrai reglage de
   * la porte qui la monte. Constate sur Timesheet : « Ma semaine » n'a pas de
   * tableau et imposait le sien, reduit a son titre, a la porte « Journal ».
   *
   * Il est extrait parce qu'il tourne pour l'ecran (le jeu d'arrivee) et pour
   * chaque autre jeu. Ecrit deux fois, les deux moities auraient fini par ne
   * plus se ressembler.
   *
   * @param {any[]} groupe @returns {Map<string, any>}
   */
  const montre = (porte, vue) => (vuesParPorte.get(String(porte.id).trim()) ?? vues).includes(vue);
  const detailsDuGroupe = (groupe) => {
    const parVue = new Map();
    for (const vue of vues) {
      const ordre = [...groupe.filter((p) => montre(p, vue)), ...groupe.filter((p) => !montre(p, vue))];
      for (const porte of ordre) {
        const detail = detailDeVue(porte, vue, traductionDePorte(porte, brief, ecrits));
        if (detail === null) continue;
        parVue.set(vue, detail);
        break;
      }
    }
    return parVue;
  };

  /** Le detail par vue que ces details composent, retenus au passage. */
  const vbDe = (parVue) => {
    const vb = {};
    for (const [vue, detail] of parVue) {
      vb[vue] = detail.binding;
      retenir(detail);
    }
    return vb;
  };
  const detailDuGroupe = (groupe) => vbDe(detailsDuGroupe(groupe));

  const detailsDeLEcran = detailsDuGroupe(source);
  const vb = vbDe(detailsDeLEcran);

  /*
   * LE REGLAGE PROPRE D'UNE PORTE DU MEME JEU.
   *
   * LA FUSION CI-DESSUS LE PERDAIT SANS RIEN DIRE : la premiere porte qui
   * parlait d'une vue gagnait, donc une seconde porte du meme jeu dont le
   * kanban groupe par Categorie, quand l'ecran groupe par Etape, sortait
   * groupee par Etape. Et la reprise, qui relit ce `vb` sur le disque, voyait
   * la synchronisation suivante l'effacer : elle refusait de reprendre.
   *
   * ON LE CALCULE SUR LES VUES QUE LA PORTE MONTRE, et seulement la : le socle
   * ne lit pas le detail d'une vue que la porte ne monte pas, et la reprise ne
   * le relirait pas (`porte.vues`), donc l'ecrire casserait l'aller-retour.
   * Une vue dont la porte ne dit rien prend celle de l'ecran, comme avant.
   *
   * IDENTIQUE A CELUI DE L'ECRAN, IL NE S'ECRIT PAS : c'est la regle de
   * resolution du socle (`detailParVue`, le `vb` de la porte sinon celui de
   * l'ecran), et c'est ce qui garde intactes les apps qui n'en ont pas besoin.
   * Different sur UNE vue, il s'ecrit EN ENTIER : le socle ne fusionne pas.
   */
  const vbParPorte = new Map();
  for (const porte of lues) {
    if (porte === arrivee) continue;
    const id = String(porte.id).trim();
    const montrees = vues.filter((vue) => montre(porte, vue));
    const t = traductionDePorte(porte, brief, ecrits);
    const sien = {};
    const propres = [];
    let different = false;
    for (const vue of montrees) {
      const propre = detailDeVue(porte, vue, t);
      const commun = detailsDeLEcran.get(vue) ?? null;
      const retenu = propre ?? commun;
      if (retenu === null) continue;
      sien[vue] = retenu.binding;
      if (propre !== null) propres.push(propre);
      if (JSON.stringify(retenu.binding) !== JSON.stringify(commun === null ? null : commun.binding)) {
        different = true;
      }
    }
    if (!different) continue;
    for (const detail of propres) retenir(detail);
    vbParPorte.set(id, sien);
  }

  /*
   * LE DETAIL DES AUTRES JEUX, UNE FOIS PAR JEU, pose sur CHAQUE porte qui le
   * montre. Une porte SANS jeu n'en recoit aucun : elle garde celui de l'ecran,
   * comme avant, et il n'y a rien d'autre a lui donner.
   */
  const dejaFait = new Map();
  for (const porte of portes) {
    if (memeJeuQueLArrivee(porte)) continue;
    const j = jeuDePorte(porte, brief);
    if (j === null) continue;
    if (!dejaFait.has(j.clef)) {
      const groupe = portes.filter((p) => {
        const k = jeuDePorte(p, brief);
        return k !== null && k.clef === j.clef;
      });
      dejaFait.set(j.clef, detailDuGroupe(groupe));
    }
    /*
     * UNE PORTE D'UN AUTRE JEU RECOIT TOUJOURS SON `vb`, MEME VIDE.
     *
     * ON NE POSAIT LE SIEN QUE S'IL PORTAIT QUELQUE CHOSE, et un jeu dont les
     * champs sont CONFIES A L'AGENT n'en porte aucun : la porte retombait alors
     * sur le `vb` de l'ecran, qui cite les colonnes du jeu D'ARRIVEE, pendant
     * que le code genere lui donne les champs de SON jeu. L'assemblage jetait,
     * et comme l'ecran genere construit TOUTES ses portes au montage, l'app
     * ENTIERE ne demarrait pas. C'est pourtant l'etat le plus normal d'un
     * chantier : on declare un second jeu, on confie ses champs, on construit.
     *
     * UN `vb` VIDE EST UNE REPONSE HONNETE : cette porte n'a pas encore de
     * detail. Heriter de celui d'un AUTRE jeu n'en etait pas une.
     */
    vbParPorte.set(String(porte.id).trim(), dejaFait.get(j.clef));
  }

  /* UNE COLONNE QU'UN FILTRE RECLAME ENTRE EN DERNIER, et seulement si aucune
     vue ne l'a deja posee : c'est elle qui porte les `options` d'un natif
     filtre par valeur, et sans elle le socle jette au montage. */
  for (const entree of reclamees) if (!colonnes.has(entree.champ)) colonnes.set(entree.champ, entree);

  /*
   * LE CATALOGUE DE TRI A DEUX SOURCES, ET LA SECONDE EST NOUVELLE.
   *
   * La premiere reste la case « Tri » des colonnes du tableau. La seconde est
   * TOUT CE QU'UNE PILE RECLAME : la pile d'arrivee, et celle de chaque favori.
   * Sans elle le schema refuse le FICHIER ENTIER (« trie sur ..., hors du
   * catalogue de tri de l'ecran »), et l'ecart etait garanti : le catalogue ne
   * dependait que du TABLEAU, donc une app sans tableau n'avait aucune option
   * -- constate sur `agence` et `peche`, qui n'ont pas de clef `tri` du tout et
   * dont l'outil tri n'est donc pas rendu.
   *
   * UNE OPTION PAR ROLE PORTE SON LIBELLE, une option par champ ne le porte
   * PAS : son libelle vit deja dans `taki.fields.json` ou dans le catalogue de
   * colonnes, et le schema refuse le second mot.
   */
  const options = new Map();
  for (const nom of triables) options.set('champ:' + nom, { champ: nom });
  for (const citee of citees) if (!options.has(citee.clef)) options.set(citee.clef, citee.option);
  const tri = [...options.values()];

  const verbes = {};
  for (const verbe of ['archive', 'unarchive', 'remove']) {
    if (((arrivee && arrivee.verbes) || {})[verbe] === true) verbes[verbe] = true;
  }

  return { colonnes: [...colonnes.values()], vb, vbParPorte, tri, verbes };
}

/**
 * CE QUE CHAQUE PORTE AJOUTE A SA DECLARATION : ses filtres, ses favoris, ses
 * actions et sa pile de tri, plus les clefs de tri que tout cela RECLAME.
 *
 * Elle est separee de `detailDEcran` parce que les deux ne vivent pas au meme
 * etage du fichier : filtres, favoris et actions sont declares PAR PORTE, le
 * catalogue de tri l'est par l'ECRAN. Il faut donc avoir lu toutes les portes
 * avant de pouvoir ecrire le catalogue, et c'est cette fonction qui les lit.
 *
 * `tranchable: false` FERME LES DEUX PREMIERS, et le schema du socle le refuse
 * explicitement : « cette porte ne se tranche pas : ses filtres ne seraient
 * jamais rendus ». Les actions, elles, restent.
 *
 * @param {any} porte @param {any} declaree la porte DEJA projetee (pour `tranchable`)
 * @param {any} t
 * @returns {{ blocs: any, citees: { clef: string, option: any }[], colonnes: any[] }}
 */
function extrasDePorte(porte, declaree, t, tPile = t) {
  const blocs = {};
  const citees = [];
  const colonnes = [];

  if (declaree.tranchable !== false) {
    const { catalogue, parRef, colonne } = filtresProjetes(porte, t);
    if (catalogue !== null) blocs.filtres = catalogue;
    if (colonne !== null) colonnes.push(colonne);
    const { favoris, niveaux } = favorisProjetes(porte, t, parRef);
    if (favoris.length > 0) blocs.favoris = favoris;
    for (const n of niveaux) citees.push(n);
  }

  blocs.actions = actionsProjetees(porte);

  /* CE QU'UNE FICHE NEUVE PORTE DEJA. Ecrit seulement s'il y a quelque chose a
     ecrire : un `creation` vide serait une clef de plus qui ne dit rien. */
  const creation = creationProjetee(porte, t);
  if (creation !== null) blocs.creation = creation;

  const pile = pileProjetee(porte.tri, tPile);
  for (const n of pile) citees.push({ clef: n.clef, option: n.option });
  blocs.pile = pile.map((n) => n.niveau);

  return { blocs, citees, colonnes };
}

/**
 * LE FICHIER D'ECRANS D'UNE APP, projete de son brief. Le jumeau exact de
 * `ficheChampsDe` : l'un dit ce que les champs SONT, l'autre OU ils s'affichent.
 *
 * UN SEUL ECRAN, d'id `principal` : c'est celui que le scaffold passe deja a
 * `useScreen`, et un brief ne decrit jamais deux ecrans.
 *
 * CE QUI SE PROJETTE : les portes (id, libelle, placeholder, se tranche), le
 * JEU que chacune montre (il POINTE le jeu du fichier de champs, trou compris),
 * les vues montees et la vue d'arrivee ; puis le DETAIL PAR VUE, le catalogue
 * de colonnes, le catalogue de tri ET SA PILE D'ARRIVEE, les trois gestes de
 * fiche, les FILTRES, les FAVORIS, les ACTIONS et les stats de chaque porte,
 * qui sont tous de la DONNEE deja saisie dans le formulaire.
 *
 * LES QUATRE DERNIERES CLEFS VIENNENT D'Y ENTRER, et ce n'est pas un ajout de
 * confort : elles portaient du FRANCAIS (« ce que le filtre garde », « l'etat
 * enregistre », « les niveaux de tri »), donc rien ne s'en derivait, donc une
 * app neuve naissait sans filtre, sans favori et SANS BOUTON AJOUTER. Le brief
 * porte desormais des REFERENCES (un uid de champ, une valeur cochee, un sens),
 * et la projection ne fait que les traduire.
 *
 * CE QUI NE SE PROJETTE PAS, ET POURQUOI C'EST DELIBERE :
 *  - l'EFFET d'une action et le PREDICAT d'un filtre hors catalogue : ils
 *    ferment sur l'etat de l'ecran, donc aucun fichier ne peut les porter sans
 *    devenir un langage. Ce qui entre, c'est leur NOM D'APPEL, derive du
 *    libelle ; le scaffold ecrit le dictionnaire d'effets EN REGARD, une entree
 *    par nom, chacune portant la prose du brief en commentaire ;
 *  - une VUE 100% CUSTOM : elle demande un composant que la naissance n'ecrit
 *    pas.
 *
 * L'HABILLAGE d'une stat et d'une colonne, les MOTS d'une vue (`motDuTitre`,
 * `motDuStatut`) et le PLACEHOLDER comme le TEXTE DE REPLI d'un role
 * (`parRole`) ETAIENT sur cette liste, pour la meme raison que les quatre clefs
 * ci-dessus : le brief ne les portait nulle part. Il les porte depuis le
 * chantier `inventaire-nee-du-studio`, donc la projection les ecrit, et c'est ce
 * qui a vide la table `LIMITES` du golden.
 *
 * @param {any} brief @returns {any}
 */
export function ficheEcransDe(brief) {
  const portes = portesDeNaissance(brief);
  const idsDeVue = new Set(VUES.map((v) => v.id));
  /* LE CATALOGUE DE COMPOSANTS prend l'union des vues des portes. Chaque
     porte conserve ensuite sa propre selection. Une vue custom n'y
     entre pas, elle demande un composant que la naissance n'ecrit pas. */
  const vues = [];
  for (const porte of portes) {
    for (const vue of Array.isArray(porte.vues) ? porte.vues : []) {
      if (idsDeVue.has(vue) && !vues.includes(vue)) vues.push(vue);
    }
  }
  if (vues.length === 0) vues.push('core.liste');
  /* L'ORDRE DES VUES DE L'ECRAN EST CELUI DU CATALOGUE, pas celui des portes :
     deux portes qui montrent des vues differentes ne doivent pas faire dependre
     l'ordre du selecteur de vues de leur rang dans le menu. C'est aussi ce qui
     rend la reprise stable (vu le 4 septembre : « calendrier » et « fiche »
     inverses a chaque passage). */
  const rangDeVue = new Map(VUES.map((v, i) => [v.id, i]));
  vues.sort((a, b) => (rangDeVue.get(a) ?? 99) - (rangDeVue.get(b) ?? 99));
  /* La vue d'arrivee est celle de la PREMIERE porte, la seule que le fondateur
     voit en arrivant. Le schema exige qu'elle soit montee. */
  const voulue = String(portes[0].arrivee || '').trim();
  const arrivee = vues.includes(voulue) ? voulue : vues[0];

  /* Ce que `ficheChampsDe` ecrira, recense AVANT de projeter quoi que ce soit :
     un role, une colonne ou une stat ne peut citer qu'un champ qui sera dans le
     fichier de champs, sans quoi le socle jette au montage avec son nom. */
  const ecrits = jeuxEcrits(portes, brief);

  /* CE QUI SE DECLARE PAR PORTE, LU AVANT LE CATALOGUE DE TRI : filtres,
     favoris, actions et pile d'arrivee. Le catalogue de l'ecran a besoin de
     savoir ce que ces piles reclament, donc il se compose ensuite. */
  const extras = portes.map((porte) => ({ porte, t: traductionDePorte(porte, brief, ecrits) }));
  const citees = [];
  /* LES COLONNES NATIVES QU'UN FILTRE PAR VALEUR RECLAME (`filtresProjetes`). */
  const colonnesDeFiltre = [];

  /* LA TRADUCTION DE L'ECRAN : l'union de celles des portes. Elle ne sert qu'a
     la pile d'ARRIVEE, la seule chose de ce fichier qui vaut pour tout l'ecran
     et non pour une porte. */
  const tDeLEcran = {
    uidVersClef: new Map(extras.flatMap(({ t }) => [...t.uidVersClef])),
    jeu: {
      clefs: new Set(extras.flatMap(({ t }) => [...t.jeu.clefs])),
      noms: extras.flatMap(({ t }) => t.jeu.noms),
      declares: new Map(extras.flatMap(({ t }) => [...t.jeu.declares])),
      natifs: new Map(extras.flatMap(({ t }) => [...t.jeu.natifs])),
    },
  };

  const declarees = extras.map(({ porte, t }) => {
    const declaree = { id: String(porte.id).trim(), label: String(porte.label).trim() };
    declaree.vues = vues.filter((vue) => (porte.vues ?? []).includes(vue));
    if (declaree.vues.length === 0) declaree.vues = [arrivee];
    declaree.arrivee = declaree.vues.includes(porte.arrivee) ? porte.arrivee : declaree.vues[0];
    const placeholder = String(porte.placeholder || '').trim();
    if (placeholder !== '') declaree.placeholder = placeholder;
    /* LES DEUX ETATS DE LA PORTE (vide, introuvable), ecrits seulement quand le
       fondateur a pose un TITRE : le schema exige le titre, l'indication est
       facultative, et une indication sans titre n'a rien sous quoi se poser. */
    for (const [clef, titreDe, indicationDe] of [
      ['vide', 'videTitre', 'videIndication'],
      ['introuvable', 'introuvableTitre', 'introuvableIndication'],
    ]) {
      const titre = String(porte[titreDe] || '').trim();
      if (titre === '') continue;
      const indication = String(porte[indicationDe] || '').trim();
      declaree[clef] = indication === '' ? { titre } : { titre, indication };
    }
    /* `tranchable` absent vaut oui : on n'ecrit que le refus explicite, comme
       le socle le lit (`portesDe` dans @taki/ui). Et un decoupage CONFIE ne
       s'ecrit pas du tout, pour la meme raison que la famille : un `false`
       laisse dans le champ avant de cocher n'est pas une decision, et l'ecrire
       trancherait a la place de l'agent. */
    const jeuMontre = jeuDe(porte, brief);
    /* LES MODELES DE FICHES : on n'ecrit que l'etat explicite, l'absence vaut
       non, comme le socle le lit (`portesDe` dans @taki/ui). Et jamais sur une
       porte SANS jeu : le socle range un modele dans la famille de son jeu, et
       le schema refuse la clef sans lui. */
    if (porte.modeles === true && jeuMontre !== null) declaree.modeles = true;
    /* LES PLANS RECURRENTS, meme contrat : un plan recree une fiche du jeu de
       la porte, donc pas de jeu, pas de clef. */
    if (porte.recurrences === true && jeuMontre !== null) declaree.recurrences = true;
    if (porte.tranchable === false ||
        (porte.tranchable !== true && jeuMontre && jeuMontre.decoupe === false && !estDelegue(jeuMontre, 'decoupe')))
      declaree.tranchable = false;
    /* UNE PORTE SANS JEU NE SE DECOUPE PAS : la case « se decoupe » vit sur le
       JEU, donc une porte qui n'en montre aucun n'avait aucun moyen de le dire,
       et le socle la proposait tranchable sur rien. */
    if (porte.sansJeu === true && jeuMontre === null) declaree.tranchable = false;
    /* HORS NAVIGATION : `navigation` absent vaut oui, on n'ecrit que le refus.
       Pose ici pour l'ordre du schema ; la passe apres les liens le retire
       quand il ferait refuser le fichier (`navigationTenable`). */
    if (porte.navigation === false) declaree.navigation = false;
    // Le cadre vit sur la porte. La fiche ne conserve que son autorisation.
    if (estSpaceCanvas(porte)) {
      declaree.canvas = cadrePorteDeBrief(porte);
    }

    const jeu = jeuDePorte(porte, brief);
    if (jeu !== null) {
      /*
       * LE NOM PARLANT DU JEU EST CELUI DU JEU, et c'est le lot qui le rend
       * possible.
       *
       * Il venait de `dsDesc`, la « Description des donnees » : un PARAGRAPHE,
       * donc un nom de trente mots la ou le studio en attend deux. Il est alors
       * passe au LIBELLE DE LA PORTE, court par construction, faute d'autre
       * chose de court a portee. C'etait deja un pis-aller, et il devenait faux
       * des que trois portes montraient le meme jeu : trois portes ne peuvent
       * pas donner trois noms a un seul jeu.
       *
       * Un jeu a maintenant un nom a lui. Le libelle de la porte reste le REPLI
       * d'un jeu pas encore nomme : il vaut mieux que rien, et c'est ce que le
       * fichier portait hier.
       */
      const nom =
        String((jeuMontre && jeuMontre.nom) || '').trim() || String(declaree.label || '').trim();
      declaree.jeu =
        jeu.type === undefined
          ? { nom, family: jeu.family }
          : { nom, family: jeu.family, type: jeu.type };
    }

    /*
     * L'ORDRE DES CLEFS EST CELUI DU SCHEMA, et il se lit comme une porte se
     * decrit : ce qu'elle est, ce qu'elle montre, ce qu'on peut y trancher, ce
     * qu'on peut y faire, ce qu'elle compte.
     */
    /*
     * LA PILE D'ARRIVEE SE LIT SUR TOUT L'ECRAN, ET C'EST LA CORRECTION DE CE
     * LOT. `tri.defaut` est une option d'ECRAN (`defaultSort`), passee une fois
     * pour toutes les portes, et le catalogue de tri porte deja les colonnes des
     * DEUX jeux. La resoudre sur le seul jeu d'arrivee rendait INEXPRIMABLE un
     * niveau sur une colonne de l'autre jeu : constate sur « Mes animaux », dont
     * la pile d'arrivee trie sur la date de peremption des aliments.
     */
    const { blocs, citees: reclamees, colonnes } = extrasDePorte(porte, declaree, t, tDeLEcran);
    for (const n of reclamees) citees.push(n);
    for (const c of colonnes) colonnesDeFiltre.push(c);
    if (blocs.filtres !== undefined) declaree.filtres = blocs.filtres;
    if (blocs.favoris !== undefined) declaree.favoris = blocs.favoris;
    /* LA TRANCHE « Une partie, filtrée » est une condition du socle : elle
       s'ecrit telle quelle. « Autre, je decris » reste une consigne de code. */
    if (porte.tranche === 'filtre' && jeuMontre !== null && conditionSchema.safeParse(porte.trancheCondition).success) {
      declaree.tranche = structuredClone(porte.trancheCondition);
    }
    /* UNE PORTE SANS ACTION NE PORTE PAS LA CLEF : un tableau vide et une clef
       absente se lisent pareil pour le socle, et le schema prefere le silence. */
    if (blocs.actions.length > 0) declaree.actions = blocs.actions;
    /* LES DEFAUTS DE LA CREATION VONT AVEC L'AJOUT : c'est ce que le socle pose
       dans la fiche vide que « + Ajouter » ouvre. */
    if (blocs.creation !== undefined) declaree.creation = blocs.creation;
    /* La pile d'ARRIVEE est une chose de l'ECRAN, pas de la porte : elle voyage
       a cote de la declaration le temps qu'on assemble le catalogue de tri. */
    declaree.__pile = blocs.pile;

    /* LES STATS SONT LE SEUL BLOC QUI RESTE PORTE PAR PORTE dans le fichier,
       comme dans le brief : deux portes ne comptent pas les memes choses. */
    const stats = statsProjetees(porte, brief, t);
    if (stats.length > 0) declaree.stats = stats;

    /*
     * LES LIENS DE LA PORTE, ecrits juste avant son detail par vue : c'est
     * l'ordre du schema (`appScreenDoorSchema`), et ce sont les CARTES de la
     * fiche qui les citent, jamais l'inverse.
     *
     * TROIS REFUS, SILENCIEUX ET VOULUS : un lien sans id, un lien dont la
     * porte visee n'existe plus (le brief la designe par UID, elle a pu etre
     * retiree), et un lien qui pointe sa propre porte. Le schema les refuserait
     * au montage ; les ecrire ferait rougir l'app entiere pour une case a
     * moitie remplie dans le formulaire.
     */
    const liens = [];
    for (const lien of Array.isArray(porte.liens) ? porte.liens : []) {
      if (lien === null || typeof lien !== 'object') continue;
      const id = String(lien.id || '').trim();
      const par = String(lien.par || '').trim();
      const visee = portes.find(
        (p) => p !== null && (p.uid === lien.porte || String(p.id || '').trim() === lien.porte),
      );
      const idVisee = visee === undefined ? '' : String(visee.id || '').trim();
      if (id === '' || par === '' || idVisee === '' || idVisee === declaree.id) continue;
      liens.push({ id, porte: idVisee, par });
    }
    if (liens.length > 0) declaree.liens = liens;
    return declaree;
  });

  /* AU CLIC SUR UNE FICHE, ecrit juste apres les liens (l'ordre du schema), et
     seulement maintenant : la vue d'arrivee se juge sur les vues de la porte
     LIEE, que la passe precedente vient de fixer. */
  extras.forEach(({ porte, t }, rang) => {
    const clic = clicProjete(porte, declarees[rang], declarees, brief, portes);
    if (clic !== null) declarees[rang].auClic = clic;
    /* « EN TÊTE DE FICHE » ET LES VUES DE FICHE (chantier `fiche-suivi`),
       juste apres le clic : l'ordre du schema. Un natif cite reclame sa colonne
       au catalogue, ou vivent ses valeurs, comme pour un filtre par valeur. */
    const enTetes = enTetesProjetes(porte, brief);
    if (enTetes.length > 0) {
      declarees[rang].etapes = enTetes.map((e) => ({ champ: e.nom, mode: e.mode, ...(e.verrou === undefined ? {} : { verrou: e.verrou }) }));
      for (const e of enTetes) if (e.nom.startsWith(PREFIXE_SOCLE)) colonnesDeFiltre.push(colonneDeCatalogue(e.nom, t));
    }
    const vuesFiche = vuesFicheProjetees(porte, brief);
    if (vuesFiche !== null) declarees[rang].vuesFiche = vuesFiche;
  });

  /* HORS NAVIGATION, juge maintenant que tous les liens sont ecrits. */
  declarees.forEach((declaree, rang) => {
    if (declaree.navigation === false && !navigationTenable(declaree, rang, declarees)) delete declaree.navigation;
  });

  /* MODIFIER DANS LE TABLEAU : on n'ecrit que l'accord explicite, l'absence
     vaut non. Et seulement sur une porte qui MONTE le tableau, la derivation
     etant le dernier mot sur les vues d'une porte : une porte qui perd son
     tableau perd la clef, qui ne dirait plus rien. */
  extras.forEach(({ porte }, rang) => {
    if (porte.modifierDansLeTableau === true && declarees[rang].vues.includes('core.tableau')) {
      declarees[rang].modifierDansLeTableau = true;
    }
  });

  /* LA PILE D'ARRIVEE EST CELLE DE LA PORTE D'ARRIVEE, la seule que le
     fondateur voie en arrivant : `defaultSort` est une option d'ECRAN, passee
     une fois pour toutes ses portes. Les piles des autres portes ne sont pas
     perdues, elles sont INEXPRIMABLES, et leurs clefs entrent quand meme au
     catalogue pour que l'outil tri les propose. */
  const pileDArrivee = declarees[0].__pile;
  for (const declaree of declarees) delete declaree.__pile;

  const vuesParPorte = new Map(declarees.map((d) => [d.id, d.vues]));
  const detail = detailDEcran(portes, brief, vues, ecrits, citees, vuesParPorte, colonnesDeFiltre);

  /* L'ordre des clefs est celui qu'un ecran declare se lit : ce qu'il monte,
     ou il arrive, ce que ses colonnes sont, comment chaque vue les raconte,
     comment on trie, ce qu'on peut faire d'une fiche, puis ses portes. C'est
     l'ordre de `contact`, et `JSON.stringify` respecte celui d'insertion. */
  /*
   * LE DETAIL PROPRE D'UNE PORTE, pose EN DERNIER sur sa declaration : l'ordre
   * des clefs d'une porte est celui du schema, et `vb` y est la derniere.
   */
  for (const declaree of declarees) {
    const propre = detail.vbParPorte.get(declaree.id);
    if (propre !== undefined) declaree.vb = propre;
  }

  /*
   * LES VUES CANVAS (View Canvas, regle G23), ecrites maintenant que le detail
   * est calcule : il ne les connait pas, une vue Canvas n'a pas de binding. Une
   * vue de niveau COLLECTION se monte dans les vues de sa porte et de l'ecran,
   * apres celles du socle ; une vue de FICHE reste hors de `vues` (le schema
   * l'exempte) et porte le jeu qu'elle montre. Leur composant est la surface
   * `canvas['vue:<id>']` du manifeste, que le moteur ecrit (`moteur-app.mjs`).
   */
  const canvasDuBrief = vuesCanvasDuBrief(brief).filter((v) => portes.includes(v.porte));
  const vuesEcran = [...vues];
  for (const v of canvasDuBrief) {
    if (v.niveau !== 'collection') continue;
    const declaree = declarees[portes.indexOf(v.porte)];
    if (declaree !== undefined && !declaree.vues.includes(v.id)) declaree.vues.push(v.id);
    if (!vuesEcran.includes(v.id)) vuesEcran.push(v.id);
  }

  const ecran = { id: 'principal', vues: vuesEcran };
  /* LES VUES DE FICHE SANS PORTE CLASSIQUE (`brief.vuesFicheSansPorte`, gardees
     par la reprise), a la suite, telles quelles : une vue qu'une porte du brief
     porte deja (meme id) n'est pas ecrite deux fois. */
  const sansPorte = (Array.isArray(brief?.vuesFicheSansPorte) ? brief.vuesFicheSansPorte : []).filter(
    (v) => v !== null && typeof v === 'object' && typeof v.id === 'string' && !canvasDuBrief.some((c) => c.id === v.id),
  );
  if (canvasDuBrief.length > 0 || sansPorte.length > 0) {
    ecran.vuesCustom = [
      ...canvasDuBrief.map((v) => ({
        id: v.id,
        label: v.label,
        niveau: v.niveau,
        ...(v.niveau === 'record' && v.jeu !== null ? { jeu: v.jeu } : {}),
      })),
      ...sansPorte.map((v) => structuredClone(v)),
    ];
  }
  ecran.arrivee = arrivee;
  ecran.porteArrivee = declarees[0].id;
  if (detail.colonnes.length > 0) ecran.colonnes = detail.colonnes;
  ecran.vb = detail.vb;
  if (detail.tri.length > 0) {
    ecran.tri = { options: detail.tri };
    /* LA PILE D'ARRIVEE, ecrite ENFIN. Le socle a gagne `defaultSort` au lot
       precedent pour elle, et personne ne l'ecrivait : elle etait « ecrite,
       validee, affichee, et appliquee nulle part ». */
    if (pileDArrivee.length > 0) ecran.tri.defaut = pileDArrivee;
  }
  if (Object.keys(detail.verbes).length > 0) ecran.verbes = detail.verbes;
  ecran.portes = declarees;
  /* LES VUES HTML, deposees par le MCP et reprises telles quelles : le Studio
     ne les ecrit pas, il les rend (ou les retire). Vide : pas de clef. */
  const vuesHtml = (Array.isArray(brief?.vuesHtml) ? brief.vuesHtml : []).filter(
    (v) => v !== null && typeof v === 'object' && !Array.isArray(v),
  );
  if (vuesHtml.length > 0) ecran.vuesHtml = structuredClone(vuesHtml);

  return { version: 1, ecrans: [ecran] };
}

/**
 * POURQUOI CETTE PORTE DU BRIEF NE PEUT PAS QUITTER LA NAVIGATION, lu sur le
 * BRIEF pour le formulaire : `'arrivee'` (la premiere porte declarable),
 * `'sansLien'` (aucune autre porte ne la lie par un lien complet), ou `null`
 * quand elle le peut. Meme jugement que `navigationTenable`, qui tranche sur
 * les liens ECRITS.
 *
 * @param {any} brief @param {any} porte @returns {'arrivee' | 'sansLien' | null}
 */
export function raisonDansNavigation(brief, porte) {
  const portes = portesDeNaissance(brief);
  /* LA MEME PORTE, par son uid quand elle en a un : le prompt lit une copie. */
  const meme = (autre) => autre === porte || (typeof porte?.uid === 'string' && porte.uid !== '' && autre?.uid === porte.uid);
  if (meme(portes[0])) return 'arrivee';
  const id = String(porte?.id || '').trim();
  const lie = portes.some(
    (autre) =>
      !meme(autre) &&
      (Array.isArray(autre.liens) ? autre.liens : []).some(
        (lien) =>
          lien !== null && typeof lien === 'object' &&
          String(lien.id || '').trim() !== '' && String(lien.par || '').trim() !== '' &&
          (lien.porte === porte.uid || (id !== '' && lien.porte === id)),
      ),
  );
  return lie ? null : 'sansLien';
}

/**
 * UNE PORTE PEUT-ELLE QUITTER LA NAVIGATION ? (`portes[].navigation: false`)
 *
 * DEUX REFUS SILENCIEUX, ceux du schema : la porte d'arrivee (la premiere, ici)
 * et une porte qu'aucune autre ne lie par un lien ECRIT, qu'on n'ouvrirait
 * jamais. Le refus du brief reste au brief : il revient de lui-meme quand une
 * porte la lie de nouveau. L'ecrire ferait refuser l'app entiere.
 *
 * @param {any} declaree @param {number} rang @param {any[]} declarees
 * @returns {boolean}
 */
function navigationTenable(declaree, rang, declarees) {
  if (rang === 0) return false;
  return declarees.some(
    (autre) => autre !== declaree && (autre.liens ?? []).some((lien) => lien.porte === declaree.id),
  );
}

/**
 * AU CLIC SUR UNE FICHE, projete (`portes[].auClic` de `taki.screens.json`).
 *
 * LE BRIEF CITE LE LIEN PAR UID (l'id d'un lien se retape), le fichier par son
 * id ; l'id est accepte aussi, comme pour une carte liee. DEUX REFUS SILENCIEUX,
 * comme pour les liens : un lien que la derivation n'ecrit pas (retire, a moitie
 * rempli) emporte le clic, et une vue que la porte liee ne monte pas (ou la
 * fiche) retombe sur son arrivee. Les ecrire ferait refuser l'app entiere pour
 * un choix devenu caduc.
 *
 * Le clic s'etend (chantier `fiche-suivi`) a une VUE DE FICHE de la porte
 * (`{ vueDeFiche }`) et a une AUTRE PORTE (`{ porte, app? }`), meme regle : ce
 * qui ne tient plus tombe, et la fiche s'ouvre.
 *
 * @param {any} porte la porte du brief @param {any} declaree sa declaration
 * @param {any[]} declarees toutes les portes declarees de l'ecran
 * @param {any} brief @param {any[]} portes les portes du brief, au rang de `declarees`
 * @returns {{ lien: string, vue?: string } | { vueDeFiche: string } | { porte: string, app?: string } | null}
 */
function clicProjete(porte, declaree, declarees, brief, portes) {
  const clic = porte?.auClic;
  if (clic === null || typeof clic !== 'object') return null;
  /* UNE VUE DE FICHE (`{ vueDeFiche }`) : seulement une vue de fiche que la
     porte peut montrer, et jamais « Fiche », le defaut, qui retire la clef. */
  if (clic.vueDeFiche !== undefined) {
    const vue = String(clic.vueDeFiche || '').trim();
    const montrees = vuesDeFicheMontrees(porte, brief);
    return vue !== '' && vue !== 'core.fiche' && montrees.includes(vue) ? { vueDeFiche: vue } : null;
  }
  /* UNE AUTRE PORTE (`{ porte, app }`). De cette app : le brief la tient par
     son uid, le fichier par son id, et jamais elle-meme. D'une autre app : son
     id tel quel, que le schema ne peut pas juger (l'app est ailleurs). */
  if (clic.porte !== undefined) {
    const app = String(clic.app || '').trim();
    const cherchee = String(clic.porte || '').trim();
    if (cherchee === '') return null;
    if (app !== '' && app !== String(brief?.id || '').trim()) {
      return /^[a-z][a-z0-9-]*$/.test(cherchee) ? { porte: cherchee, app } : null;
    }
    const rang = portes.findIndex((p) => p !== null && (p.uid === cherchee || String(p.id || '').trim() === cherchee));
    const visee = rang < 0 ? undefined : declarees[rang];
    /* UNE PORTE DU MEME JEU SEULEMENT (D5) : le schema refuserait l'autre, et
       la validation le dit sur la porte (`verrousDePorte`). */
    return visee === undefined || visee === declaree || !memeJeuDePorte(declaree.jeu, visee.jeu) ? null : { porte: visee.id };
  }
  const cherche = String(clic.lien || '').trim();
  if (cherche === '') return null;
  const lien = (Array.isArray(porte.liens) ? porte.liens : []).find(
    (l) => l !== null && typeof l === 'object' && (l.uid === cherche || l.id === cherche),
  );
  const id = lien === undefined ? '' : String(lien.id || '').trim();
  const ecrit = (declaree.liens ?? []).find((l) => l.id === id);
  if (ecrit === undefined) return null;
  const cible = declarees.find((d) => d.id === ecrit.porte);
  const vue = String(clic.vue || '').trim();
  return vue !== '' && vue !== 'core.fiche' && cible !== undefined && cible.vues.includes(vue)
    ? { lien: id, vue }
    : { lien: id };
}

/**
 * LES AUTRES PORTES DU MEME JEU que celle-ci, dans l'ordre du brief : celles ou
 * « Ouvrir dans <porte> » peut ouvrir ses fiches (D5, `memeJeuDePorte` du
 * socle). Dans un brief, un jeu est UN objet (`dsRef`, un couple famille et
 * type par jeu) : le meme jeu, c'est le meme `dsRef`. Une porte sans jeu n'en
 * partage aucun.
 *
 * @param {any} porte @param {any} brief @returns {any[]}
 */
export function portesDuMemeJeu(porte, brief) {
  if (jeuDe(porte, brief) === null) return [];
  return (Array.isArray(brief?.portes) ? brief.portes : []).filter(
    (autre) => autre !== null && typeof autre === 'object' && autre !== porte && autre.uid !== porte.uid && autre.dsRef === porte.dsRef,
  );
}

/**
 * LES VUES DE FICHE QUE CETTE PORTE MONTRE, hors « Fiche », par la regle du
 * socle (`vuesDeFicheDeLaPorte`) : celles que le brief coche et qui existent
 * encore, dans son ordre ; sans case reglee (`vuesFiche: null`), le defaut du
 * socle, les vues HTML du jeu SEULES : une vue Canvas de fiche est eteinte tant
 * qu'on ne la coche pas (decision du fondateur, 27/10).
 *
 * @param {any} porte @param {any} brief @returns {string[]}
 */
export function vuesDeFicheMontrees(porte, brief) {
  const lu = ecranDesVuesDeFiche(porte, brief);
  return lu === null ? [] : vuesDeFicheDeLaPorte(lu.ecran, 'porte');
}

/**
 * COCHER OU DECOCHER UNE VUE DE FICHE au Studio (`SectionVues`) : la valeur de
 * `vuesFiche` qui en resulte. La liste COMPLETE des cases cochees, dans l'ordre
 * de l'offre ; `null` (la clef part) seulement quand elle revient au defaut du
 * socle, les vues HTML du jeu et aucune vue Canvas. « Tout coche » s'ecrit donc
 * en liste : la clef absente ne veut plus dire « toutes » (27/10).
 *
 * @param {any} porte @param {any} brief @param {string} id @param {boolean} on
 * @returns {string[]|null}
 */
export function vuesFicheApresBascule(porte, brief, id, on) {
  const offertes = vuesDeFicheDePorte(porte, brief);
  const montrees = vuesDeFicheMontrees(porte, brief);
  const suite = offertes.map((v) => v.id).filter((v) => (v === id ? on : montrees.includes(v)));
  const parDefaut = vuesDeFicheMontrees({ ...porte, vuesFiche: null }, brief);
  const memes = suite.length === parDefaut.length && suite.every((v) => parDefaut.includes(v));
  return memes ? null : suite;
}

/**
 * LES VUES DE FICHE, projetees (`portes[].vuesFiche`), ou `null` quand le brief
 * ne regle rien (le defaut du socle : les vues HTML du jeu, aucune vue Canvas ;
 * la clef ne s'ecrit pas). Une liste vide dit « Fiche seule » ; une vue cochee
 * qui n'existe plus tombe, en silence, comme un lien a moitie rempli. La liste
 * s'ecrit TELLE QUE le brief la tient : c'est le geste de cocher
 * (`SectionVues`) qui la rend a `null` quand elle revient au defaut, pas la
 * derivation, sinon une liste ecrite a la main ne ferait plus l'aller-retour.
 *
 * @param {any} porte @param {any} brief @returns {string[]|null}
 */
function vuesFicheProjetees(porte, brief) {
  if (!Array.isArray(porte?.vuesFiche)) return null;
  /* PLUS AUCUNE VUE DE FICHE DANS LE JEU (la derniere vue HTML retiree) : il
     n'y a plus rien a restreindre, la clef part. */
  if (vuesDeFicheDePorte(porte, brief).length === 0) return null;
  return vuesDeFicheMontrees(porte, brief);
}

/**
 * « EN TÊTE DE FICHE », projete (`portes[].etapes`) : la LISTE des en-tetes,
 * dans l'ordre, chacun un champ a choix unique du jeu que le brief designe par
 * UID, et son mode. Le brief se lit par la regle du socle (`enTetesDeLaPorte` :
 * l'ancien objet seul vaut une liste d'un, un champ n'entre qu'une fois). Un
 * en-tete dont le champ n'est plus un choix unique du jeu (retire, devenu
 * multiple) ne s'ecrit pas : l'ecrire ferait refuser l'app pour une case
 * devenue caduque, et la validation le dit (`validerPorte`). Vide : rien.
 *
 * @param {any} porte @param {any} brief
 * @returns {{ nom: string, mode: 'flux'|'selection', verrou?: object | object[] }[]}
 */
function enTetesProjetes(porte, brief) {
  const offerts = champsDEtapes(porte, brief);
  return enTetesDuBrief(porte).flatMap((enTete) => {
    const champ = offerts.find((c) => c.uid === enTete.champ);
    if (champ === undefined) return [];
    const verrou = verrouProjete(enTete, porte, brief);
    return [{ nom: champ.nom, mode: enTete.mode, ...(verrou === null ? {} : { verrou }) }];
  });
}

/**
 * LES EN-TETES D'UNE PORTE DU BRIEF, dans l'ordre : la lecture du socle
 * (`enTetesDeLaPorte`), mais les verrous y restent TELS QU'ON LES SAISIT, en
 * liste (`verrous`, meme quand le fichier n'en porte qu'un, sous la clef
 * `verrou` d'un objet seul ou d'une liste). La lecture du socle ecarte un
 * verrou sans condition ou sans raison (il ne tiendrait pas au schema) ; le
 * formulaire, lui, doit le garder le temps qu'on l'ecrive (une raison pas
 * encore tapee ne fait pas disparaitre le reglage), et la validation le dit.
 * La derivation, elle, ne projette que ceux qui tiennent (`verrouProjete`).
 *
 * @param {any} porte
 * @returns {{ champ: string, mode: 'flux'|'selection', verrous?: { si?: any, avant?: string, depuis?: string, raison?: string }[] }[]}
 */
export function enTetesDuBrief(porte) {
  const brut = porte?.etapes;
  const bruts = Array.isArray(brut) ? brut : brut && typeof brut === 'object' ? [brut] : [];
  return enTetesDeLaPorte(porte).map(({ verrous: _lus, ...enTete }) => {
    const saisi = bruts.find((b) => b && b.champ === enTete.champ)?.verrou;
    const liste = (Array.isArray(saisi) ? saisi : saisi === undefined ? [] : [saisi]).filter((v) => v !== null && typeof v === 'object' && !Array.isArray(v));
    return liste.length === 0 ? enTete : { ...enTete, verrous: liste };
  });
}

/**
 * LES VALEURS QU'UN EN-TETE OFFRE a son verrou : les options du champ (celles
 * de son catalogue, natif ou declare), `{ value, label }` dans l'ordre, les
 * archivees ecartees. C'est la liste de « Verrouiller avant » ; `[]` quand
 * le champ n'est plus offert.
 *
 * @param {any} porte @param {any} brief @param {string} uid le champ de l'en-tete (UID du brief)
 * @returns {{ value: string, label: string }[]}
 */
export function valeursDEnTete(porte, brief, uid) {
  const jeu = jeuDe(porte, brief);
  if (jeu === null) return [];
  const traduit = traduireChamps(jeu, brief).find((t) => t.uid === uid);
  const cible = traduit === undefined ? null : (traduit.declare !== null ? traduit.declare : traduit.natif);
  return (Array.isArray(cible?.options) ? cible.options : [])
    .filter((o) => o && typeof o.value === 'string' && o.archived !== true)
    .map((o) => ({ value: o.value, label: String(o.label ?? o.value) }));
}

/**
 * LES VERROUS D'UN EN-TETE, projetes (`portes[].etapes[].verrou`) : chacun
 * s'ecrit tel que le brief le tient (condition et valeur stockee sont deja le
 * vocabulaire de la declaration) quand il tient debout : une condition du
 * socle, une raison, et un `avant` ou un `depuis` (jamais les deux) qui est
 * encore une valeur du champ. Un verrou qui ne tient pas ne s'ecrit pas :
 * l'ecrire ferait refuser l'app pour une case caduque, et la validation le dit
 * (`validerPorte`). Un seul verrou s'ecrit comme un objet (la forme des apps
 * d'avant), plusieurs comme une liste, dans l'ordre. `null` : pas de verrou.
 *
 * @param {{ champ: string, verrous?: { si?: any, avant?: string, depuis?: string, raison?: string }[] }} enTete
 * @param {any} porte @param {any} brief
 * @returns {object | object[] | null}
 */
export function verrouProjete(enTete, porte, brief) {
  const valeurs = valeursDEnTete(porte, brief, enTete.champ);
  const tiennent = (enTete.verrous ?? []).flatMap((verrou) => {
    if (!conditionSchema.safeParse(verrou.si).success || String(verrou.raison ?? '').trim() === '') return [];
    if (verrou.avant !== undefined && verrou.depuis !== undefined) return [];
    for (const seuil of [verrou.avant, verrou.depuis]) if (seuil !== undefined && !valeurs.some((o) => o.value === seuil)) return [];
    return [{
      si: structuredClone(verrou.si),
      ...(verrou.avant === undefined ? {} : { avant: verrou.avant }),
      ...(verrou.depuis === undefined ? {} : { depuis: verrou.depuis }),
      raison: String(verrou.raison).trim(),
    }];
  });
  return tiennent.length === 0 ? null : tiennent.length === 1 ? tiennent[0] : tiennent;
}

/**
 * LES VALEURS PAR DEFAUT D'UNE PORTE A LA CREATION, projetees.
 *
 * CE QUI A REMPLACE LE FORMULAIRE. Le brief disait autrefois QUELS CHAMPS la
 * fenetre de creation demandait (`ajout.champs`), et le scaffold en ecrivait un
 * `Modal`. La CREATION est desormais une FICHE VIDE en zone 5, tenue par le
 * socle : la fiche montre ses cartes, il n'y a plus rien a cocher. Ce qui reste
 * a dire, et que personne d'autre ne sait, c'est ce qu'une fiche neuve porte
 * DEJA : `status: 'disponible'` sur un materiel, par exemple.
 *
 * LA CLEF EST LE NOM DU CHAMP tel que les fichiers le citent : la clef nue d'un
 * champ declare, `socle:<natif>` pour un champ de la famille. C'est la meme
 * traduction que partout ailleurs (`nomDeChampProjete`), donc un champ ECARTE
 * de `taki.fields.json` ne peut pas se retrouver en defaut.
 *
 * LA VALEUR EST TYPEE PAR LA NATURE DU CHAMP : un nombre pour un champ
 * numerique, un booleen pour une case a cocher, une chaine sinon. Le schema du
 * socle n'accepte que ces trois formes, et une chaine « 12 » posee sur un champ
 * nombre se relirait comme du texte.
 *
 * UNE VALEUR VIDE NE S'ECRIT PAS : « pas de defaut » et « defaut vide » sont
 * deux choses differentes, et la seconde ferait naitre une fiche avec une case
 * renseignee-mais-vide.
 *
 * @param {any} porte @param {any} t @returns {{ defauts: Record<string, any> }|null}
 */
function creationProjetee(porte, t) {
  const ajout = porte && porte.ajout ? porte.ajout : null;
  const lignes = ajout && Array.isArray(ajout.defauts) ? ajout.defauts : [];
  const defauts = {};

  for (const ligne of lignes) {
    if (ligne === null || typeof ligne !== 'object') continue;
    const nom = nomDeChampProjete(String(ligne.uid ?? ''), t);
    if (nom === null || Object.prototype.hasOwnProperty.call(defauts, nom)) continue;
    const brut = String(ligne.valeur ?? '').trim();
    if (brut === '') continue;

    const type = nom.startsWith(PREFIXE_SOCLE)
      ? (t.jeu.natifs.get(nom) || {}).type
      : (t.jeu.declares.get(nom) || {}).type;
    /* UN CALCUL OU UN NUMERO N'A PAS DE DEFAUT : sa valeur est posee par le
       socle (le catalogue, `estAutomatique`), et le serveur refuse qu'on
       l'ecrive. Un prix, une note, un pourcentage ou une duree sont des NOMBRES
       sur le disque (`natureDuType`), donc leur defaut aussi. */
    if (estAutomatique(type)) continue;
    if (type === 'number' || (type !== undefined && natureDuType(type) === 'nombre')) {
      const nombre = Number(brut);
      /* UN NOMBRE ILLISIBLE N'EST PAS UN DEFAUT : l'ecrire en chaine ferait
         refuser le fichier par le schema, sur une saisie que personne n'a
         relue. */
      if (!Number.isFinite(nombre)) continue;
      defauts[nom] = nombre;
      continue;
    }
    if (type === 'boolean') {
      defauts[nom] = brut === 'oui' || brut === 'true';
      continue;
    }
    defauts[nom] = brut;
  }

  return Object.keys(defauts).length === 0 ? null : { defauts };
}

/**
 * LE JEU DE CHAQUE PORTE : sa famille du socle, et son type local.
 *
 * POURQUOI CETTE FONCTION EXISTE, ET CE QU'ELLE A COUTE. Le scaffold cablait
 * TOUT l'ecran sur le jeu de la porte d'ARRIVEE : le chargement, l'entree a
 * froid et les trois gestes de fiche. Une app a deux jeux naissait donc en
 * affichant, derriere sa deuxieme porte, les fiches de la premiere. Le moteur
 * le SAVAIT et l'ecrivait dans un `TODO` du code genere, que personne n'ouvre.
 *
 * ELLE NE DIT PLUS CE QU'UN FORMULAIRE DEMANDE : la creation est une fiche vide
 * tenue par le socle, donc le scaffold n'ecrit plus de `Modal` et n'a plus de
 * clefs a recevoir.
 *
 * LA FAMILLE VIENT DE LA DECLARATION PROJETEE (`jeuDePorte`), jamais du brief
 * brut : une famille tapee puis CONFIEE a l'agent sort en trou nomme dans
 * `taki.fields.json`, et la lire autrement ferait ecrire un `sdk.<famille>` pour
 * un jeu que le fichier dit « a decider ».
 *
 * @param {any} brief
 * @returns {{ id: string, famille: string, typeLocal: string }[]}
 */
export function jeuxDesPortes(brief) {
  return portesDeNaissance(brief).map((porte) => {
    const jeu = jeuDePorte(porte, brief);
    const famille = jeu !== null && !estFamilleADecider(jeu.family) ? jeu.family : '';
    const type = jeu !== null && jeu.type !== undefined ? jeu.type : '';
    return {
      id: String(porte.id || '').trim(),
      famille,
      typeLocal: famille === '' ? '' : type,
    };
  });
}

/**
 * LES NOMS QUE LA DECLARATION CITE ET QUE LE CODE DOIT FOURNIR, avec la PROSE
 * qui les explique.
 *
 * C'EST LE MAILLON QUI MANQUAIT, et il ne se devine pas : `actionsDe` REFUSE au
 * montage un effet que l'ecran ne fournit pas, et `filtresDe` refuse un predicat
 * absent du registre `FONCTIONS`. Declarer les quatre clefs sans ecrire ces deux
 * dictionnaires en regard ferait JETER toute app neuve, avec le nom en cause.
 * Vider l'une sans l'autre ne donne rien : c'est un seul geste, en deux fichiers.
 *
 * LA SOURCE DE VERITE EST LE FICHIER PROJETE, jamais le brief : les noms
 * viennent de `ficheEcransDe`, et la prose est RETROUVEE dans le brief par son
 * libelle (le nom se derivant du libelle, la jointure est exacte). Lire les deux
 * cotes separement laisserait le scaffold ecrire un nom que le fichier ne cite
 * pas, ou taire un nom qu'il cite.
 *
 * @param {any} brief @returns {{ effets: any[], predicats: any[] }}
 */
export function nomsDeNaissance(brief) {
  const portesDuBrief = portesDeNaissance(brief);
  const ecran = ficheEcransDe(brief).ecrans[0];

  /** La prose du brief, indexee par libelle : « ce que ca fait », « ce qu'il garde ». */
  const proseAction = new Map();
  const prosePredicat = new Map();
  for (const porte of portesDuBrief) {
    for (const a of Array.isArray(porte.actions) ? porte.actions : []) {
      const label = String((a && a.label) || '').trim();
      if (label !== '' && !proseAction.has(label)) proseAction.set(label, String((a && a.quoi) || '').trim());
    }
    for (const f of filtresDe(porte).autres) {
      const label = String((f && f.label) || '').trim();
      if (label !== '' && !prosePredicat.has(label)) prosePredicat.set(label, String((f && f.garde) || '').trim());
    }
  }

  const effets = [];
  const predicats = [];
  const vusE = new Set();
  const vusP = new Set();
  for (const porte of ecran.portes) {
    for (const action of porte.actions || []) {
      /* UNE ACTION SANS EFFET N'EN CITE AUCUN : l'ajout est tenu par le socle
         (fiche vide en zone 5), donc l'ecran n'a rien a fournir pour lui. Le
         dictionnaire d'effets ne doit pas gagner une entree `undefined`. */
      if (action.fn === undefined || vusE.has(action.fn)) continue;
      vusE.add(action.fn);
      effets.push({
        fn: action.fn,
        label: action.label,
        ajout: action.kind === 'add',
        quoi: proseAction.get(action.label) || '',
      });
    }
    for (const autre of (porte.filtres && porte.filtres.autres) || []) {
      if (!autre.fn || vusP.has(autre.fn)) continue;
      vusP.add(autre.fn);
      predicats.push({ fn: autre.fn, label: autre.label, garde: prosePredicat.get(autre.label) || '' });
    }
  }
  return { effets, predicats };
}

/**
 * LE COMPOSANT ET LE FICHIER DE LA SURFACE D'UNE PORTE CANVAS, sur le modele
 * de l'app de reference : `canvas/MaSemaine.tsx` (Timesheet, porte
 * `ma-semaine`). Un id qui
 * commencerait par un chiffre donnerait un identifiant invalide : il recoit le
 * prefixe `Porte`. Le moteur (`tools/studio-dev/moteur-app.mjs`) ecrit ce
 * fichier ; le Studio le nomme au fondateur.
 *
 * @param {string} porteId @returns {{ id: string, composant: string, fichier: string }}
 */
export function surfaceDePorte(porteId) {
  const brut = String(porteId ?? '')
    .split('-')
    .filter(Boolean)
    .map((m) => m.charAt(0).toUpperCase() + m.slice(1))
    .join('')
    .replace(/[^A-Za-z0-9]/g, '');
  const composant = /^[A-Za-z]/.test(brut) ? brut : `Porte${brut}`;
  return { id: porteId, composant, fichier: `canvas/${composant}.tsx` };
}

/**
 * LE COMPOSANT ET LE FICHIER DE LA SURFACE D'UNE VUE CANVAS (chantier
 * `fiche-suivi`), meme regle que celle d'une porte, sous la clef `vue:<id>` du
 * manifeste (celle que lit `definirAppDeclaree`).
 *
 * @param {string} vueId l'id namespace de la vue (`<app>.<vue>`)
 * @returns {{ id: string, composant: string, fichier: string }}
 */
export function surfaceDeVue(vueId) {
  /* LE NOM APRES LE POINT (`mon-app.plan-de-salle` -> `VuePlanDeSalle`) : le
     prefixe `Vue` distingue la surface d'une vue de celle d'une porte du meme
     nom, dans le meme dossier `canvas/`. */
  const nom = String(vueId ?? '').split('.').slice(1).join('-') || String(vueId ?? '');
  const brut = nom
    .split('-')
    .filter(Boolean)
    .map((m) => m.charAt(0).toUpperCase() + m.slice(1))
    .join('')
    .replace(/[^A-Za-z0-9]/g, '');
  const composant = `Vue${brut}`;
  return { id: `vue:${vueId}`, composant, fichier: `canvas/${composant}.tsx` };
}
